import React, { Component } from 'react'
import '../styles/GlobalStyle.css'
import { db, firebase } from '../firebase/Firebase'
import ColType from '../Types'
import { Modal, Icon, Popover, Select, Tabs, Button, Tooltip } from 'antd'
import StudentResource from '../teacher/pacingguide/StudentResource'
import { injectBaseHref, flattenDoc, getStateStandardCategories, getStateStandardNamesDict, FOLDER_CURRENT } from '../Util'
import DOKStandardBreakdown from './DOKStandardBreakdown'
import DOKTeacherBreakdown from './DOKTeacherBreakdown'
import Empty from '../customcomponents/Empty'
import TeacherStudentReport from '../teacher/reports/TeacherStudentReport'
import PersonAvatar from '../customcomponents/PersonAvatar'
import ReactToPrint from "react-to-print"

const isV3or4 = (v) => v === '3.0' || v === '4.0'
function schoolYearLabel(yearStart) {
    if (!yearStart) return ''
    const y = parseInt(String(yearStart).slice(0, 4), 10)
    if (isNaN(y)) return String(yearStart)
    return `${y}–${y + 1}`
}
function fmt(n) { if (n == null) return '—'; return n.toLocaleString() }
function pct(v) { if (v == null) return '—'; return `${Math.round(v * 100)}%` }
const GAME_LABELS = { castle: 'Castle Climb', space: 'Space Race', basketball: 'Basketball', golf: 'Golf' }
const gameLabel = (k) => GAME_LABELS[k] || k
function fmtDuration(sec) {
    if (sec == null || sec <= 0) return '—'
    const s = Math.round(sec)
    const MIN = 60, HR = 3600, DAY = 86400, WK = 604800, MO = 2592000, YR = 31536000
    if (s >= YR) { const y = Math.floor(s / YR), mo = Math.floor((s % YR) / MO); return mo > 0 ? `${y}y ${mo}mo` : `${y}y` }
    if (s >= MO) { const mo = Math.floor(s / MO), d = Math.floor((s % MO) / DAY); return d > 0 ? `${mo}mo ${d}d` : `${mo}mo` }
    if (s >= WK) { const w = Math.floor(s / WK), d = Math.floor((s % WK) / DAY); return d > 0 ? `${w}w ${d}d` : `${w}w` }
    if (s >= DAY) { const d = Math.floor(s / DAY), h = Math.floor((s % DAY) / HR); return h > 0 ? `${d}d ${h}h` : `${d}d` }
    const h = Math.floor(s / HR), m = Math.floor((s % HR) / MIN)
    if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`
    if (m > 0) { const r = s % MIN; return (m < 10 && r > 0) ? `${m}m ${r}s` : `${m}m` }
    return `${s}s`
}
// Playtime in plain words: "45 min", "3 hr 20 min", "126 hours".
function fmtPlaytime(sec) {
    if (!sec || sec <= 0) return 'None'
    const totalMin = Math.round(sec / 60)
    if (totalMin < 1) return 'Under 1 min'
    if (totalMin < 60) return `${totalMin} min`
    if (sec >= 36000) return `${fmt(Math.round(sec / 3600))} hours`
    const h = Math.floor(totalMin / 60), m = totalMin % 60
    return m ? `${h} hr ${m} min` : `${h} hr`
}
// Expanded-row breakdown for the games views: one line per subject + grade.
function GameBreakdown({ rows }) {
    const subTh = (align) => ({ textAlign: align, padding: align === 'right' ? '6px 0 6px 32px' : '6px 32px 6px 0', fontSize: 14, fontWeight: 700, color: '#64748b', borderBottom: '1px solid #cbd5e1' })
    return <div style={{ marginLeft: 14, paddingLeft: 18, borderLeft: '3px solid #e2e8f0' }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>By subject and grade</div>
        <table style={{ borderCollapse: 'collapse', fontSize: 17 }}>
            <thead><tr>
                <th style={subTh('left')}>Subject</th>
                <th style={subTh('left')}>Grade</th>
                <th style={subTh('right')}>Playtime</th>
                <th style={subTh('right')}>Average score</th>
            </tr></thead>
            <tbody>
                {rows.map((c, i) => <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '8px 32px 8px 0' }}><SubjectName subject={c.category} /></td>
                    <td style={{ padding: '8px 32px 8px 0', color: '#1e293b' }}>{c.standardName || <span style={{ color: '#64748b' }}>—</span>}</td>
                    <td style={{ padding: '8px 0 8px 32px', textAlign: 'right', color: '#1e293b', fontWeight: 600 }}>{fmtPlaytime(c.playtimeSeconds)}</td>
                    <td style={{ padding: '8px 0 8px 32px', textAlign: 'right' }}><AverageScore avg={gameAccuracy(c)} /></td>
                </tr>)}
            </tbody>
        </table>
    </div>
}
// Sums playtime / correct / incorrect / sessions across a list of game stat objects.
function sumGameStats(list) {
    return list.reduce((acc, s) => {
        acc.playtimeSeconds += s.playtimeSeconds || 0
        acc.correct += s.correct || 0
        acc.incorrect += s.incorrect || 0
        acc.sessions += s.sessions || 0
        return acc
    }, { playtimeSeconds: 0, correct: 0, incorrect: 0, sessions: 0 })
}
function gameAccuracy(o) { if (!o) return null; const c = o.correct || 0, i = o.incorrect || 0, t = c + i; return t > 0 ? c / t : null }
// by_teacher entries store only per-category stats; a teacher's totals are the sum.
function sumTeacherTotals(t) {
    const out = { playtimeSeconds: 0, correct: 0, incorrect: 0, sessions: 0 }
    if (t && t.categories) Object.values(t.categories).forEach(c => {
        out.playtimeSeconds += c.playtimeSeconds || 0; out.correct += c.correct || 0
        out.incorrect += c.incorrect || 0; out.sessions += c.sessions || 0
    })
    return out
}
function getAvg(s) { return (s && s.total_graded > 0) ? s.grade_sum / s.total_graded : null }
function iepStudentCount(t) {
    if (!t) return 0
    if (t.students != null) return t.students
    return t.plans || 0
}
function buildWeeklyPoints(bw) { if (!bw) return []; return Object.keys(bw).map(Number).sort((a, b) => a - b).map(w => { const a = getAvg(bw[String(w)]); return a != null ? { x: w, value: a, label: `Week ${w}` } : null }).filter(Boolean) }
function build3WeekPoints(bw) { if (!bw) return []; const p = []; for (let s = 1; s <= 36; s += 3) { const v = []; for (let w = s; w <= s + 2; w++) { const d = bw[String(w)]; if (d) { const a = getAvg(d); if (a != null) v.push(a) } }; if (v.length > 0) p.push({ x: Math.floor((s - 1) / 3) + 1, value: v.reduce((a, b) => a + b, 0) / v.length, label: `W${s}–${s + 2}` }) }; return p }
// A quiz counts as "given" if it has been assigned at least once OR has any graded attempts.
function isQuizGiven(o) {
    if (!o) return false
    return (o.given || 0) > 0 || (o.teacher_given || 0) > 0 || (o.total_graded || 0) > 0
}
// Best-available "assigned" count. Prefer an explicit given count; some
// rollups (e.g. per-teacher) don't track it, so fall back to completed
// attempts rather than showing a misleading 0.
function givenCount(o) {
    if (!o) return 0
    if (o.given != null && o.given > 0) return o.given
    if (o.teacher_given != null && o.teacher_given > 0) return o.teacher_given
    if (o.quizzes_given != null && o.quizzes_given > 0) return o.quizzes_given
    return o.completed || o.total_graded || 0
}
// ---- Weekly aggregation helpers (used by the weekly charts) ----
function mergeWeekBucket(into, from) {
    if (!from) return into
    into.given = (into.given || 0) + (from.given || 0)
    into.teacher_given = (into.teacher_given || 0) + (from.teacher_given || 0)
    into.quizzes_given = (into.quizzes_given || 0) + (from.quizzes_given || 0)
    into.completed = (into.completed || 0) + (from.completed || 0)
    into.total_graded = (into.total_graded || 0) + (from.total_graded || 0)
    into.grade_sum = (into.grade_sum || 0) + (from.grade_sum || 0)
    return into
}
function mergeByWeek(target, byWeek) {
    if (!byWeek) return target
    Object.keys(byWeek).forEach(w => {
        if (!target[w]) target[w] = {}
        mergeWeekBucket(target[w], byWeek[w])
    })
    return target
}
// Best-effort weekly data for a standard-detail object: prefer its own by_week,
// otherwise stitch one together from the per-quiz by_week buckets.
function detailWeekData(det) {
    if (!det) return {}
    if (det.by_week && Object.keys(det.by_week).length) return det.by_week
    const out = {}
    if (det.quizzes) {
        Object.values(det.quizzes).forEach(qd => { if (qd && qd.by_week) mergeByWeek(out, qd.by_week) })
    }
    return out
}
// Weeks 1..36 -> number of quizzes given that week.
function weekGivenSeries(byWeek) {
    const arr = []
    for (let w = 1; w <= 36; w++) {
        const d = byWeek ? byWeek[String(w)] : null
        arr.push({ week: w, value: d ? givenCount(d) : 0 })
    }
    return arr
}
// Weeks 1..36 -> average score that week (null when no graded attempts).
function weekAvgSeries(byWeek) {
    const arr = []
    for (let w = 1; w <= 36; w++) {
        const d = byWeek ? byWeek[String(w)] : null
        const a = d ? getAvg(d) : null
        arr.push({ week: w, value: a })
    }
    return arr
}
// ---- Axis tick helpers for the bar chart ----
function niceStep(raw) {
    if (raw <= 0) return 1
    const pow = Math.pow(10, Math.floor(Math.log10(raw)))
    const n = raw / pow
    let nice
    if (n <= 1) nice = 1
    else if (n <= 2) nice = 2
    else if (n <= 5) nice = 5
    else nice = 10
    return nice * pow
}
function buildIntTicks(maxV, target = 4) {
    const m = Math.max(1, maxV)
    let step = Math.max(1, Math.round(niceStep(m / target)))
    const ticks = []
    for (let v = 0; v <= m + 0.5; v += step) ticks.push(v)
    if (ticks[ticks.length - 1] < m) ticks.push(ticks[ticks.length - 1] + step)
    return ticks
}
const CHART_H = 250
// Ordering helpers: subjects in a fixed order, grades Kindergarten -> High School.
const SUBJECT_ORDER = ['ELA', 'Math', 'Science', 'Social Studies']
const GRADE_ORDER = ['Kindergarten', '1st Grade', '2nd Grade', '3rd Grade', '4th Grade', '5th Grade', '6th Grade', '7th Grade', '8th Grade', '9th Grade', '10th Grade', '11th Grade', '12th Grade', 'HS', 'High School', 'Other']
function subjectIndex(s) { const i = SUBJECT_ORDER.indexOf(s); return i === -1 ? 90 : i }
function gradeIndex(name) { const i = GRADE_ORDER.indexOf(name); return i === -1 ? 900 : i }
// Order schools elementary -> middle -> high (detected in the name), then anything else, alphabetically within each group.
function schoolTypeRank(name) {
    const n = (name || '').toLowerCase()
    if (n.includes('elementary')) return 0
    if (n.includes('middle')) return 1
    if (n.includes('high')) return 2
    return 3
}
// Two-state status badge: yellow "Give Quiz" (not yet given) / blue "Quiz Given" (already given).
function QuizStatusBadge({ given }) {
    if (given) return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 6, backgroundColor: '#eff6ff', color: '#2563eb', fontWeight: 700, fontSize: 16, border: '1px solid #bfdbfe', whiteSpace: 'nowrap' }}><Icon type="check-circle" style={{ fontSize: 14 }} /> Quiz Given</span>
    return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 6, backgroundColor: '#fffbeb', color: '#d97706', fontWeight: 700, fontSize: 16, border: '1px solid #fde68a', whiteSpace: 'nowrap' }}><Icon type="edit" style={{ fontSize: 14 }} /> Give Quiz</span>
}
// Friendly intro card shown once at the top of each view.
function IntroCard({ icon, title, children }) {
    return <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', background: '#fff', border: '1px solid #e8eef5', borderRadius: 16, padding: '18px 22px', marginBottom: 20, boxShadow: 'rgba(100, 100, 111, 0.10) 0px 4px 14px 0px' }}>
        <div style={{ flex: '0 0 auto', width: 50, height: 50, borderRadius: 12, background: '#2d6a9f', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon type={icon} style={{ fontSize: 24 }} />
        </div>
        <div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#1e293b', marginBottom: 4 }}>{title}</div>
            <div style={{ fontSize: 16, color: '#475569', lineHeight: 1.6 }}>{children}</div>
        </div>
    </div>
}
const scoreBand = (avg) => avg >= 0.80
    ? { bg: '#ecfdf5', color: '#059669', border: '#a7f3d0' }
    : avg >= 0.50
        ? { bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe' }
        : { bg: '#fffbeb', color: '#d97706', border: '#fde68a' }
// A single colored score chip — the one signal for "how did they do".
function ScoreChip({ avg }) {
    const b = scoreBand(avg)
    return <span style={{ display: 'inline-flex', alignItems: 'center', padding: '4px 12px', borderRadius: 8, backgroundColor: b.bg, color: b.color, border: `1px solid ${b.border}`, fontWeight: 800, fontSize: 17, whiteSpace: 'nowrap' }}>{pct(avg)}</span>
}
// The one cell a scanning teacher reads. Three states, learned once.
function StatusCell({ det, isFuture }) {
    if (isFuture) return <span style={{ fontSize: 15, color: '#94a3b8', fontWeight: 600 }}>Upcoming</span>
    const given = isQuizGiven(det)
    if (!given) return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 12px', borderRadius: 8, backgroundColor: '#fffbeb', color: '#d97706', fontWeight: 700, fontSize: 16, border: '1px solid #fde68a', whiteSpace: 'nowrap' }}><Icon type="edit" style={{ fontSize: 13 }} /> Give Quiz</span>
    const avg = det && det.total_graded > 0 ? det.grade_sum / det.total_graded : null
    if (avg == null) return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 12px', borderRadius: 8, backgroundColor: '#eff6ff', color: '#2563eb', fontWeight: 700, fontSize: 16, border: '1px solid #bfdbfe', whiteSpace: 'nowrap' }}><Icon type="check-circle" style={{ fontSize: 13 }} /> Quiz Given</span>
    return <ScoreChip avg={avg} />
}
function DLabel({ children }) { return <div style={{ fontSize: 13, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 2 }}>{children}</div> }
function Stat({ label, value }) { return <div><DLabel>{label}</DLabel><div style={{ fontSize: 22, fontWeight: 800, color: '#1e293b' }}>{value}</div></div> }
// ============================================================
// WEEKLY CHARTS (full-width, measured to their container)
// ============================================================
// Measures its own width and hands it to a render-prop child so SVG charts
// can fill the full table width crisply (no viewBox stretching).
class ChartFrame extends Component {
    constructor(props) { super(props); this.state = { width: 0 }; this.ref = React.createRef() }
    componentDidMount() {
        this.measure()
        window.addEventListener('resize', this.measure)
        if (typeof ResizeObserver !== 'undefined' && this.ref.current) {
            this.ro = new ResizeObserver(this.measure)
            this.ro.observe(this.ref.current)
        }
        this.t = setTimeout(this.measure, 60)
    }
    componentWillUnmount() {
        window.removeEventListener('resize', this.measure)
        if (this.ro) this.ro.disconnect()
        if (this.t) clearTimeout(this.t)
    }
    measure = () => {
        const el = this.ref.current
        if (!el) return
        const w = Math.floor(el.getBoundingClientRect().width)
        if (w && w !== this.state.width) this.setState({ width: w })
    }
    render() {
        const { width } = this.state
        return <div ref={this.ref} style={{ width: '100%' }}>
            {width > 0 ? this.props.children(width) : <div style={{ height: CHART_H }} />}
        </div>
    }
}
// Vertical bar chart: weeks 1-36 (x) vs number of quizzes given (y).
class WeeklyGivenBar extends Component {
    state = { hover: null }
    render() {
        const { byWeek, width } = this.props
        const h = CHART_H
        const padL = 46, padR = 18, padT = 18, padB = 40
        const series = weekGivenSeries(byWeek)
        const total = series.reduce((s, d) => s + d.value, 0)
        const maxV = Math.max(1, ...series.map(d => d.value))
        const ticks = buildIntTicks(maxV, 4)
        const axisMax = ticks[ticks.length - 1] || 1
        const innerW = Math.max(10, width - padL - padR)
        const innerH = h - padT - padB
        const n = series.length
        const gap = Math.max(1.5, innerW * 0.004)
        const barW = (innerW - gap * (n - 1)) / n
        const xAt = i => padL + i * (barW + gap)
        const hover = this.state.hover
        return <div style={{ position: 'relative', width: '100%' }}>
            <svg width={width} height={h} style={{ display: 'block' }}>
                {ticks.map((t, i) => {
                    const y = padT + innerH - (t / axisMax) * innerH
                    return <g key={'t' + i}>
                        <line x1={padL} y1={y} x2={width - padR} y2={y} stroke="#eef2f7" strokeWidth={1} />
                        <text x={padL - 8} y={y + 4} textAnchor="end" fontSize={12} fill="#94a3b8" fontWeight={600}>{t}</text>
                    </g>
                })}
                {series.map((d, i) => {
                    const bh = d.value > 0 ? (d.value / axisMax) * innerH : 0
                    const y = padT + innerH - bh
                    const isHover = hover === i
                    return <rect key={'b' + i} x={xAt(i)} y={y} width={Math.max(0.5, barW)} height={bh} rx={2} fill={isHover ? '#1e40af' : '#2d6a9f'} />
                })}
                {series.map((d, i) => <rect key={'hit' + i} x={xAt(i)} y={padT} width={Math.max(0.5, barW)} height={innerH} fill="transparent" onMouseEnter={() => this.setState({ hover: i })} onMouseLeave={() => this.setState({ hover: null })} />)}
                {series.map((d, i) => {
                    const show = d.week === 1 || d.week % 5 === 0 || d.week === 36
                    if (!show) return null
                    return <text key={'xl' + i} x={xAt(i) + barW / 2} y={h - 16} textAnchor="middle" fontSize={12} fill="#94a3b8" fontWeight={600}>{d.week}</text>
                })}
                <text x={padL + innerW / 2} y={h - 2} textAnchor="middle" fontSize={12} fill="#cbd5e1" fontWeight={700}>Week</text>
            </svg>
            {hover != null && <div style={{ position: 'absolute', left: Math.min(Math.max(0, xAt(hover) + barW / 2 - 60), Math.max(0, width - 120)), top: 6, backgroundColor: 'rgba(15,23,42,0.95)', color: '#fff', padding: '6px 10px', borderRadius: 8, fontSize: 14, pointerEvents: 'none', whiteSpace: 'nowrap', lineHeight: 1.5, zIndex: 20 }}>
                <div style={{ fontWeight: 800 }}>Week {series[hover].week}</div>
                <div>{fmt(series[hover].value)} {series[hover].value === 1 ? 'quiz' : 'quizzes'} given</div>
            </div>}
            {total === 0 && <div style={{ position: 'absolute', left: padL, right: padR, top: padT, height: innerH, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 16, fontWeight: 600 }}>No quizzes have been given yet.</div>}
        </div>
    }
}
// Line chart: weeks 1-36 (x) vs average score (y, 0-100%).
class WeeklyAvgLine extends Component {
    state = { hover: null }
    render() {
        const { byWeek, width } = this.props
        const h = CHART_H
        const padL = 46, padR = 18, padT = 18, padB = 40
        const series = weekAvgSeries(byWeek)
        const innerW = Math.max(10, width - padL - padR)
        const innerH = h - padT - padB
        const xAt = week => padL + ((week - 1) / 35) * innerW
        const yAt = v => padT + innerH - v * innerH
        const yticks = [0, 0.25, 0.5, 0.75, 1]
        const linePts = series.filter(d => d.value != null).map(d => ({ ...d, X: xAt(d.week), Y: yAt(d.value) }))
        const pathD = linePts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.X} ${p.Y}`).join(' ')
        const areaD = linePts.length > 0 ? `${pathD} L ${linePts[linePts.length - 1].X} ${padT + innerH} L ${linePts[0].X} ${padT + innerH} Z` : ''
        const hover = this.state.hover
        return <div style={{ position: 'relative', width: '100%' }}>
            <svg width={width} height={h} style={{ display: 'block' }}>
                {yticks.map((t, i) => {
                    const y = yAt(t)
                    return <g key={'t' + i}>
                        <line x1={padL} y1={y} x2={width - padR} y2={y} stroke="#eef2f7" strokeWidth={1} />
                        <text x={padL - 8} y={y + 4} textAnchor="end" fontSize={12} fill="#94a3b8" fontWeight={600}>{Math.round(t * 100)}%</text>
                    </g>
                })}
                {linePts.length > 1 && <path d={areaD} fill="#2d6a9f" opacity={0.08} />}
                {linePts.length > 1 && <path d={pathD} fill="none" stroke="#2d6a9f" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />}
                {linePts.map((p, i) => <circle key={'c' + i} cx={p.X} cy={p.Y} r={hover === i ? 5 : 3} fill="#fff" stroke="#2d6a9f" strokeWidth={2.5} />)}
                {linePts.map((p, i) => <rect key={'hit' + i} x={p.X - Math.max(5, innerW / 72)} y={padT} width={Math.max(10, innerW / 36)} height={innerH} fill="transparent" onMouseEnter={() => this.setState({ hover: i })} onMouseLeave={() => this.setState({ hover: null })} />)}
                {series.map((d, i) => {
                    const show = d.week === 1 || d.week % 5 === 0 || d.week === 36
                    if (!show) return null
                    return <text key={'xl' + i} x={xAt(d.week)} y={h - 16} textAnchor="middle" fontSize={12} fill="#94a3b8" fontWeight={600}>{d.week}</text>
                })}
                <text x={padL + innerW / 2} y={h - 2} textAnchor="middle" fontSize={12} fill="#cbd5e1" fontWeight={700}>Week</text>
            </svg>
            {hover != null && linePts[hover] && <div style={{ position: 'absolute', left: Math.min(Math.max(0, linePts[hover].X - 55), Math.max(0, width - 110)), top: Math.max(0, linePts[hover].Y - 52), backgroundColor: 'rgba(15,23,42,0.95)', color: '#fff', padding: '6px 10px', borderRadius: 8, fontSize: 14, pointerEvents: 'none', whiteSpace: 'nowrap', lineHeight: 1.5, zIndex: 20 }}>
                <div style={{ fontWeight: 800 }}>Week {linePts[hover].week}</div>
                <div>Avg: {pct(linePts[hover].value)}</div>
            </div>}
            {linePts.length === 0 && <div style={{ position: 'absolute', left: padL, right: padR, top: padT, height: innerH, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 16, fontWeight: 600 }}>No graded quizzes yet.</div>}
        </div>
    }
}
// Pill subjects shown above every weekly chart.
const PILL_SUBJECTS = ['All', 'ELA', 'Math', 'Science', 'Social Studies']
// Turn a {subject -> by_week} map into a complete {All, ELA, Math, Science, Social Studies -> by_week}
// map, where "All" is the merge of every subject's weekly data.
function subjectWeekMap(bySubject) {
    const map = {}
    PILL_SUBJECTS.forEach(s => { if (s !== 'All') map[s] = (bySubject && bySubject[s]) ? bySubject[s] : {} })
    if (bySubject) Object.keys(bySubject).forEach(s => { if (!(s in map) && s !== 'All') map[s] = bySubject[s] })
    const all = {}
    Object.keys(map).forEach(s => mergeByWeek(all, map[s]))
    map.All = all
    return map
}
// Tabbed, full-width weekly view: "Quizzes Given" bar chart + "Average Score" line chart,
// with subject pill tabs (All / ELA / Math / Science / Social Studies) at the top right.
class WeekTrends extends Component {
    state = { tab: 'given', subject: 'All' }
    setTab = (tab) => (e) => { if (e) e.stopPropagation(); this.setState({ tab }) }
    setSubject = (subject) => (e) => { if (e) e.stopPropagation(); this.setState({ subject }) }
    render() {
        const { bySubject } = this.props
        const { tab, subject } = this.state
        const map = subjectWeekMap(bySubject)
        const byWeek = map[subject] || {}
        const hasAny = Object.keys(map).some(s => map[s] && Object.keys(map[s]).length > 0)
        const tabBtn = (key, label, icon) => {
            const active = tab === key
            return <button onClick={this.setTab(key)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', fontSize: 16, fontWeight: active ? 800 : 600, border: 'none', borderBottom: active ? '3px solid #2d6a9f' : '3px solid transparent', cursor: 'pointer', backgroundColor: 'transparent', color: active ? '#2d6a9f' : '#64748b' }}>
                <Icon type={icon} style={{ fontSize: 15 }} />{label}
            </button>
        }
        const pill = (s) => {
            const active = subject === s
            return <button key={s} onClick={this.setSubject(s)} style={{ padding: '4px 12px', fontSize: 14, fontWeight: 700, borderRadius: 999, cursor: 'pointer', border: active ? '1px solid #2d6a9f' : '1px solid #e2e8f0', backgroundColor: active ? '#2d6a9f' : '#fff', color: active ? '#fff' : '#64748b', whiteSpace: 'nowrap' }}>{s}</button>
        }
        if (!hasAny) return <div style={{ padding: '20px 0', color: '#94a3b8', fontSize: 16 }}>Weekly data isn't available here yet.</div>
        return <div style={{ width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, borderBottom: '1px solid #e2e8f0', marginBottom: 12, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex' }}>
                    {tabBtn('given', 'Quizzes Given', 'bar-chart')}
                    {tabBtn('avg', 'Average Score', 'line-chart')}
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', paddingBottom: 8 }}>
                    {PILL_SUBJECTS.map(pill)}
                </div>
            </div>
            <div style={{ fontSize: 14, color: '#64748b', marginBottom: 8, lineHeight: 1.5 }}>
                {tab === 'given'
                    ? `How many ${subject === 'All' ? '' : subject + ' '}quizzes were given each week of the school year (weeks 1–36).`
                    : `The average score on ${subject === 'All' ? '' : subject + ' '}quizzes completed each week of the school year (weeks 1–36).`}
            </div>
            {tab === 'given'
                ? <ChartFrame key={'g-' + subject}>{w => <WeeklyGivenBar byWeek={byWeek} width={w} />}</ChartFrame>
                : <ChartFrame key={'a-' + subject}>{w => <WeeklyAvgLine byWeek={byWeek} width={w} />}</ChartFrame>}
        </div>
    }
}

class StudentQuizHistory extends Component {
    state = { subject: 'All' }
    setSubject = (subject) => (e) => { if (e) e.stopPropagation(); this.setState({ subject }) }
    render() {
        const byWeekBySubject = this.props.byWeek || {}
        const subjects = Object.keys(byWeekBySubject)
            .filter(s => Object.keys(byWeekBySubject[s] || {}).length > 0)
            .sort((a, b) => (subjectIndex(a) - subjectIndex(b)) || a.localeCompare(b))
        if (!subjects.length) return null
        const subject = subjects.includes(this.state.subject) ? this.state.subject : 'All'

        // Convert to the { week: { grade_sum, total_graded } } shape WeeklyAvgLine
        // reads; "All" merges every subject.
        const byWeek = {}
        const addSubject = (s) => {
            Object.entries(byWeekBySubject[s] || {}).forEach(([wk, v]) => {
                if (!byWeek[wk]) byWeek[wk] = { grade_sum: 0, total_graded: 0 }
                byWeek[wk].total_graded += v[0] || 0
                byWeek[wk].grade_sum += v[1] || 0
            })
        }
        if (subject === 'All') subjects.forEach(addSubject)
        else addSubject(subject)

        const pill = (s) => {
            const active = subject === s
            return <button key={s} onClick={this.setSubject(s)} style={{ padding: '4px 12px', fontSize: 14, fontWeight: 700, borderRadius: 999, cursor: 'pointer', border: active ? '1px solid #2d6a9f' : '1px solid #e2e8f0', backgroundColor: active ? '#2d6a9f' : '#fff', color: active ? '#fff' : '#64748b', whiteSpace: 'nowrap' }}>{s}</button>
        }

        return <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', borderBottom: '1px solid #e2e8f0', paddingBottom: 8, marginBottom: 8 }}>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#334155' }}>Quiz scores this school year</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {['All', ...subjects].map(pill)}
                </div>
            </div>
            <div style={{ fontSize: 14, color: '#64748b', marginBottom: 8 }}>
                This student's average score each week on {subject === 'All' ? '' : subject + ' '}quizzes (weeks 1–36).
            </div>
            <ChartFrame key={'student-line-' + subject}>{w => <WeeklyAvgLine byWeek={byWeek} width={w} />}</ChartFrame>
        </div>
    }
}
// The expansion: stats + (optionally) the full-width weekly charts and the full-results button.
// The expansion: stats + (optionally) the weekly charts and the full-results link.
function RowDetail({ det, bySubject, onResults, hideCharts }) {
    const wrap = { marginLeft: 14, paddingLeft: 18, borderLeft: '3px solid #e2e8f0' }
    if (!isQuizGiven(det)) {
        return <div style={{ ...wrap, padding: '6px 0 6px 18px', color: '#334155', fontSize: 16 }}>This quiz hasn't been given yet. Results will show here once students complete it.</div>
    }
    const avg = det && det.total_graded > 0 ? det.grade_sum / det.total_graded : null
    const gv = givenCount(det)
    const comp = det ? (det.completed || det.total_graded || 0) : 0
    return <div style={wrap}>
        <div style={{ display: 'flex', gap: 40, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: hideCharts ? 0 : 18 }}>
            <FlatStat label="Average score">{avg == null ? <span style={{ fontSize: 17, fontWeight: 400, color: '#475569' }}>No scores yet</span> : <AverageScore avg={avg} big />}</FlatStat>
            <FlatStat label="Quizzes given" value={fmt(gv)} />
            <FlatStat label="Completed by students" value={fmt(comp)} />
        </div>
        {!hideCharts ? <WeekTrends bySubject={bySubject} /> : null}
        {onResults ? <div style={{ marginTop: 14 }}><ResultsButton onClick={onResults} /></div> : null}
    </div>
}

function Explainer({ title, children }) {
    return <div style={{ display: 'flex', gap: 10, padding: '12px 14px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, marginBottom: 20, alignItems: 'flex-start' }}>
        <Icon type="info-circle" style={{ color: '#3b82f6', fontSize: 18, marginTop: 2, flexShrink: 0 }} />
        <div style={{ fontSize: 16, color: '#1e293b', lineHeight: 1.55 }}>{title && <div style={{ fontWeight: 800, color: '#0f172a', marginBottom: 2 }}>{title}</div>}{children}</div>
    </div>
}
function Note({ children }) { return <div style={{ fontSize: 16, color: '#334155', lineHeight: 1.5, marginBottom: 22 }}>{children}</div> }
class EmadBar extends Component {
    state = { hover: false }
    render() {
        const { stat, w = 110, h = 10 } = this.props; const { hover } = this.state
        if (!stat) return <span style={{ color: '#d1d5db', fontSize: 15 }}>—</span>
        const e = stat.exceed || 0, m = stat.meet || 0, a = stat.approach || 0, d = stat.does_not_meet || 0, t = e + m + a + d
        if (t === 0) return <span style={{ color: '#d1d5db', fontSize: 15 }}>—</span>
        const ew = (e / t) * w, mw = (m / t) * w, aw = (a / t) * w
        const ep = Math.round((e / t) * 100), mp = Math.round((m / t) * 100), ap = Math.round((a / t) * 100), dp = Math.round((d / t) * 100)
        return <div style={{ position: 'relative', display: 'inline-block' }} onMouseEnter={() => this.setState({ hover: true })} onMouseLeave={() => this.setState({ hover: false })}>
            <svg width={w} height={h} style={{ display: 'block', borderRadius: 4, overflow: 'hidden', cursor: 'default' }}><rect x={0} y={0} width={ew} height={h} fill="#059669" /><rect x={ew} y={0} width={mw} height={h} fill="#2563eb" /><rect x={ew + mw} y={0} width={aw} height={h} fill="#d97706" /><rect x={ew + mw + aw} y={0} width={w - ew - mw - aw} height={h} fill="#dc2626" /></svg>
            {hover && <div style={{ position: 'absolute', bottom: h + 6, left: '50%', transform: 'translateX(-50%)', backgroundColor: 'rgba(15,23,42,0.95)', color: '#fff', padding: '8px 12px', borderRadius: 8, fontSize: 15, whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 20, lineHeight: 1.6 }}>
                <div style={{ fontWeight: 800, marginBottom: 4 }}>How students performed</div>
                <div><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, backgroundColor: '#059669', marginRight: 6 }} />Exceeds: {ep}% ({e})</div>
                <div><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, backgroundColor: '#2563eb', marginRight: 6 }} />Meets: {mp}% ({m})</div>
                <div><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, backgroundColor: '#d97706', marginRight: 6 }} />Approaches: {ap}% ({a})</div>
                <div><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, backgroundColor: '#dc2626', marginRight: 6 }} />Does Not Meet: {dp}% ({d})</div>
            </div>}
        </div>
    }
}
function ResultsButton({ onClick }) {
    return <button type="button" onClick={e => { e.stopPropagation(); onClick() }}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 16, fontWeight: 700, border: 'none', background: 'none', cursor: 'pointer', color: '#1d4ed8' }}>
        <Icon type="bar-chart" /> See question-by-question results
    </button>
}
function hasResults(det) { return det && det.total_graded > 0 }
function getQuestionColor(cp) { if (cp < 30) return { bg: '#fef2f2', color: '#dc2626', bar: '#dc2626' }; if (cp < 60) return { bg: '#fffbeb', color: '#d97706', bar: '#d97706' }; return { bg: '#ecfdf5', color: '#059669', bar: '#059669' } }
function QuestionBars({ questions, label }) { if (!questions || Object.keys(questions).length === 0) return null; return <div style={{ marginBottom: 16 }}>{label && <div style={{ fontSize: 18, fontWeight: 800, color: '#334155', marginBottom: 4 }}>{label}</div>}<Note>Each bar is the percentage of students who answered that question correctly. Green means most students got it right; orange and red flag the questions students struggled with most.</Note>{Object.keys(questions).sort((a, b) => Number(a) - Number(b)).map(qk => { const qd = questions[qk]; const cp = qd.total > 0 ? (qd.correct / qd.total) * 100 : 0; const col = getQuestionColor(cp); return <div key={qk} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}><div style={{ fontSize: 17, fontWeight: 700, color: '#64748b', width: 100 }}>Question {Number(qk) + 1}</div><div style={{ flex: 1, height: 24, backgroundColor: '#f1f5f9', borderRadius: 6, overflow: 'hidden' }}><div style={{ height: '100%', width: `${cp}%`, backgroundColor: col.bar, borderRadius: 6 }} /></div><div style={{ fontSize: 17, fontWeight: 800, color: col.color, width: 55, textAlign: 'right' }}>{cp.toFixed(1)}%</div><div style={{ fontSize: 15, color: '#94a3b8', width: 85, textAlign: 'right' }}>{qd.correct}/{qd.total}</div></div> })}</div> }
function StatsHeader({ stat, label }) { const avg = stat.total_graded > 0 ? stat.grade_sum / stat.total_graded : null; return <div style={{ display: 'flex', gap: 20, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>{label && <div style={{ fontSize: 21, fontWeight: 900, color: '#0f172a' }}>{label}</div>}<div><span style={{ fontSize: 16, color: '#94a3b8', fontWeight: 700 }}>Avg: </span><span style={{ fontSize: 24, fontWeight: 900, color: '#1e293b' }}>{pct(avg)}</span></div><div><span style={{ fontSize: 16, color: '#94a3b8', fontWeight: 700 }}>Quizzes Given: </span><span style={{ fontSize: 20, fontWeight: 700 }}>{fmt(givenCount(stat))}</span></div><div><span style={{ fontSize: 16, color: '#94a3b8', fontWeight: 700 }}>Completed: </span><span style={{ fontSize: 20, fontWeight: 700 }}>{fmt(stat.completed || stat.total_graded)}</span></div><QuizStatusBadge given={isQuizGiven(stat)} /><EmadBar stat={{ exceed: stat.exceed || 0, meet: stat.meet || 0, approach: stat.approach || 0, does_not_meet: stat.does_not_meet || 0 }} w={140} h={12} /></div> }
// ============================================================
// DEMO DATA GENERATION
// ============================================================
const DEMO_AVATAR_COLORS = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2', '#eb2f96', '#fa8c16']
function seededRandom(seed) { let s = Math.abs(seed) || 1; return function () { s = (s * 1103515245 + 12345) & 0x7fffffff; return s / 0x7fffffff } }
function hashString(str) { let hash = 5381; for (let i = 0; i < str.length; i++) { hash = ((hash << 5) + hash) + str.charCodeAt(i); hash = hash & hash }; return Math.abs(hash) }
function buildDemoAssessments(quizStat, qdoc, numQuestions, teachersWithStudents) {
    const targetAvg = quizStat && quizStat.total_graded > 0
        ? (quizStat.grade_sum / quizStat.total_graded)
        : 0.65
    const seed = hashString(
        JSON.stringify({
            gs: quizStat.grade_sum,
            tg: quizStat.total_graded,
            g: quizStat.given,
            q: quizStat.questions || {}
        }) + (qdoc ? qdoc.id || '' : '')
    )
    const rng = seededRandom(seed)
    const actualNumQuestions = (qdoc && qdoc.numQuestions)
        ? qdoc.numQuestions
        : (quizStat && quizStat.questions
            ? Object.keys(quizStat.questions).length
            : numQuestions)
    const version = (qdoc && qdoc.version) ? qdoc.version : '3.0'
    const teachers = []
    const teachersDict = {}
    const teacherAssessments = []
    const individualTeacherRetakeDict = {}
    const teacherAssessmentCountDict = {}
    const studentsDict = {}
    const students = []
    const flatStudents = []
    teachersWithStudents.forEach(entry => {
        const teacher = entry.teacher
        const teacherStudents = entry.students || []
        teachers.push(teacher)
        teachersDict[teacher.id] = teacher
        teacherStudents.forEach(student => {
            if (!studentsDict[student.id]) {
                studentsDict[student.id] = student
                students.push(student)
            }
            flatStudents.push({ teacher, student })
        })
    })
    if (flatStudents.length === 0) {
        return {
            teacherAssessments: [],
            teacherAssessmentDict: {},
            teacherAssessmentCountDict: {},
            teacherAssessmentRetakes: {},
            teachers,
            teachersDict,
            individualTeacherRetakeDict: {},
            students,
            studentsDict,
        }
    }
    const clamp = (v, lo = 0, hi = 1) => Math.max(lo, Math.min(hi, v))
    let questionRates = []
    for (let qi = 0; qi < actualNumQuestions; qi++) {
        const qData = quizStat && quizStat.questions ? quizStat.questions[String(qi)] : null
        const rate = qData && qData.total > 0
            ? (qData.correct / qData.total)
            : null
        questionRates.push(rate)
    }
    const hasRealQuestionRates = questionRates.some(v => v != null)
    if (!hasRealQuestionRates) {
        questionRates = []
        for (let qi = 0; qi < actualNumQuestions; qi++) {
            const wobble = (rng() - 0.5) * 0.22
            questionRates.push(clamp(targetAvg + wobble, 0.10, 0.95))
        }
    } else {
        questionRates = questionRates.map(v => v == null ? targetAvg : v)
    }
    const avgQuestionRate = questionRates.reduce((a, b) => a + b, 0) / Math.max(1, questionRates.length)
    const delta = targetAvg - avgQuestionRate
    questionRates = questionRates.map(v => clamp(v + delta, 0.05, 0.98))
    const correctedAvg = questionRates.reduce((a, b) => a + b, 0) / Math.max(1, questionRates.length)
    const correction = targetAvg - correctedAvg
    questionRates = questionRates.map(v => clamp(v + correction, 0.05, 0.98))
    const teacherTargets = {}
    const teacherEntries = teachersWithStudents.slice()
    teacherEntries.forEach((entry, idx) => {
        const wobble = (rng() - 0.5) * 0.16
        teacherTargets[entry.teacher.id] = clamp(targetAvg + wobble, 0.05, 0.98)
    })
    let weightedSum = 0
    let weightedCount = 0
    teacherEntries.forEach(entry => {
        const count = (entry.students || []).length
        weightedSum += teacherTargets[entry.teacher.id] * count
        weightedCount += count
    })
    const currentTeacherWeightedAvg = weightedCount > 0 ? (weightedSum / weightedCount) : targetAvg
    const teacherDelta = targetAvg - currentTeacherWeightedAvg
    teacherEntries.forEach(entry => {
        teacherTargets[entry.teacher.id] = clamp(
            teacherTargets[entry.teacher.id] + teacherDelta,
            0.05,
            0.98
        )
    })
    const teacherStudentGradeTargets = {}
    teacherEntries.forEach(entry => {
        const teacher = entry.teacher
        const teacherStudents = (entry.students || []).slice()
        const tAvg = teacherTargets[teacher.id]
        const studentTargets = {}
        teacherStudents.forEach(student => {
            const wobble = (rng() - 0.5) * 0.24
            studentTargets[student.id] = clamp(tAvg + wobble, 0.0, 1.0)
        })
        const vals = Object.values(studentTargets)
        const curAvg = vals.length > 0
            ? vals.reduce((a, b) => a + b, 0) / vals.length
            : tAvg
        const d = tAvg - curAvg
        Object.keys(studentTargets).forEach(studentId => {
            studentTargets[studentId] = clamp(studentTargets[studentId] + d, 0.0, 1.0)
        })
        teacherStudentGradeTargets[teacher.id] = studentTargets
    })
    const studentChosenAnswersByTeacher = {}
    const realizedQuestionTotals = {}
    const realizedQuestionCorrect = {}
    for (let qi = 0; qi < actualNumQuestions; qi++) {
        realizedQuestionTotals[String(qi)] = 0
        realizedQuestionCorrect[String(qi)] = 0
    }
    teacherEntries.forEach(entry => {
        const teacher = entry.teacher
        const teacherStudents = entry.students || []
        const answersForTeacher = {}
        teacherStudents.forEach(student => {
            const sTarget = teacherStudentGradeTargets[teacher.id][student.id]
            const chosenAnswersBaseQuiz = {}
            for (let qi = 0; qi < actualNumQuestions; qi++) {
                const qRate = questionRates[qi]
                let pCorrect = (sTarget * 0.65) + (qRate * 0.35)
                pCorrect += (rng() - 0.5) * 0.08
                pCorrect = clamp(pCorrect, 0.02, 0.98)
                const isCorrect = rng() < pCorrect
                chosenAnswersBaseQuiz[String(qi)] = isCorrect
                realizedQuestionTotals[String(qi)] += 1
                if (isCorrect) realizedQuestionCorrect[String(qi)] += 1
            }
            answersForTeacher[student.id] = chosenAnswersBaseQuiz
        })
        studentChosenAnswersByTeacher[teacher.id] = answersForTeacher
    })
    teacherEntries.forEach((entry, ti) => {
        const teacher = entry.teacher
        const teacherStudents = entry.students || []
        const studentGrades = {}
        const studentSurveys = {}
        teacherStudents.forEach(student => {
            const chosenAnswersBaseQuiz = studentChosenAnswersByTeacher[teacher.id][student.id]
            let correctCount = 0
            for (let qi = 0; qi < actualNumQuestions; qi++) {
                if (chosenAnswersBaseQuiz[String(qi)]) correctCount++
            }
            const grade = actualNumQuestions > 0 ? (correctCount / actualNumQuestions) : 0
            studentGrades[student.id] = {
                graded: true,
                finalGrade: {
                    grade,
                    correct: correctCount,
                    total: actualNumQuestions
                },
                chosenAnswersBaseQuiz
            }
            const svRoll = rng()
            studentSurveys[student.id] = {
                completed: true,
                qualitative: svRoll < 0.55 ? 'good' : svRoll < 0.80 ? 'somewhatGood' : 'notGood'
            }
        })
        const quizData = {
            numQuestions: actualNumQuestions,
            version,
            docs: (qdoc && qdoc.docs) ? qdoc.docs : null,
            standardCategory: (qdoc && qdoc.standardCategory) || '',
            standardName: (qdoc && qdoc.standardName) || '',
            questionStandards: (qdoc && qdoc.questionStandards) || null,
            uuid: (qdoc && qdoc.id) || 'demo-quiz',
            id: (qdoc && qdoc.id) || 'demo-quiz',
            assessmentCategory: 'quiz',
        }
        const assessment = {
            id: 'demo-assessment-' + ti,
            teacherId: teacher.id,
            teacher,
            quiz: quizData,
            studentGrades,
            studentSurveys,
            status: 'current',
            timeStamp: { seconds: Date.now() / 1000 }
        }
        let totalAssessments = 0
        let totalCorrect = 0
        let totalTotal = 0
        let totalGrade = 0
        Object.values(studentGrades).forEach(sg => {
            if (sg.graded) {
                totalAssessments++
                totalCorrect += sg.finalGrade.correct
                totalTotal += sg.finalGrade.total
                totalGrade += sg.finalGrade.grade
            }
        })
        assessment.totalStats = {
            totalAssessments,
            correct: totalCorrect,
            total: totalTotal,
            totalGrade,
            avg: totalAssessments > 0 ? totalGrade / totalAssessments : 0
        }
        assessment.title = teacher.firstName + ' ' + teacher.lastName
        assessment.value = (assessment.totalStats.avg * 100).toFixed(2)
        teacherAssessments.push(assessment)
        individualTeacherRetakeDict[teacher.id] = [assessment]
        teacherAssessmentCountDict[teacher.id] = 1
    })
    return {
        teacherAssessments,
        teacherAssessmentDict: Object.fromEntries(teacherAssessments.map(a => [a.id, a])),
        teacherAssessmentCountDict,
        teacherAssessmentRetakes: { 0: teacherAssessments },
        teachers,
        teachersDict,
        individualTeacherRetakeDict,
        students,
        studentsDict,
    }
}
const SELECT_STYLE = { padding: '7px 28px 7px 10px', fontSize: 18, borderRadius: 8, border: '1px solid #e2e8f0', fontWeight: 600, color: '#1e293b', appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2364748b' d='M2 4l4 4 4-4'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center', backgroundSize: '12px', cursor: 'pointer' }
const IEP_PLAN_TYPES = ['IEP', 'Tier 2', 'Tier 3']
const STUDENT_FILTERS = [
    { key: 'all', label: 'All Students' },
    { key: 'plans', label: 'Students with Plans' },
    { key: 'IEP', label: 'IEP' },
    { key: 'Tier 2', label: 'Tier 2' },
    { key: 'Tier 3', label: 'Tier 3' },
]
const STUDENT_FILTER_LABEL = (k) => (STUDENT_FILTERS.find(f => f.key === k) || {}).label || k
const PLAN_BADGE_COLORS = {
    'IEP': { bg: '#f5f3ff', color: '#7c3aed', border: '#ddd6fe' },
    'Tier 2': { bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe' },
    'Tier 3': { bg: '#fff7ed', color: '#ea580c', border: '#fed7aa' },
}
function PlanBadges({ plans, small }) {
    if (!plans || plans.length === 0) return <span style={{ color: '#cbd5e1', fontWeight: 600 }}>—</span>
    return <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
        {plans.map(p => {
            const c = PLAN_BADGE_COLORS[p] || PLAN_BADGE_COLORS['Tier 2']
            return <span key={p} style={{ padding: small ? '0 6px' : '2px 10px', borderRadius: 999, background: c.bg, color: c.color, border: `1px solid ${c.border}`, fontWeight: 700, fontSize: small ? 11 : 14, whiteSpace: 'nowrap' }}>{p}</span>
        })}
    </span>
}
function studentName(s) {
    const last = (s && s.lastName) || '', first = (s && s.firstName) || ''
    if (last && first) return `${last}, ${first}`
    return last || first || 'Unknown student'
}
function studentGradeLabel(g) {
    if (g == null || g === '') return '—'
    const v = String(g)
    return /^k/i.test(v) ? 'K' : v
}
function studentGradeRank(g) {
    const v = String(g == null ? '' : g)
    if (/^k/i.test(v)) return 0
    const n = parseInt(v, 10)
    return isNaN(n) ? 99 : n
}
// ============================================================
// STUDENTS VIEW / SIDE NAV HELPERS (flat, educational, low color)
// ============================================================
// Performance levels — same names and color family as EmadBar.
// Keep the cutoffs in sync with the ones used to build exceed/meet/approach/does_not_meet.
const LEVELS = [
    { min: 0.9, word: 'Exceeds', color: '#047857', range: '90% or higher' },
    { min: 0.7, word: 'Meets', color: '#1d4ed8', range: '70% to 89%' },
    { min: 0.5, word: 'Approaches', color: '#b45309', range: '50% to 69%' },
    { min: -Infinity, word: 'Does Not Meet', color: '#b91c1c', range: 'Below 50%' },
]
function levelFor(avg) { return LEVELS.find(l => avg >= l.min) }
// ============================================================
// FLAT / EDUCATIONAL VIEW HELPERS
// ============================================================
const SUBJECT_LOOK = {
    'ELA': { color: '#7c3aed', icon: 'read' },
    'Math': { color: '#2563eb', icon: 'calculator' },
    'Science': { color: '#059669', icon: 'experiment' },
    'Social Studies': { color: '#ea580c', icon: 'global' },
}
function subjectLook(s) { return SUBJECT_LOOK[s] || { color: '#475569', icon: 'book' } }
function SubjectIcon({ subject, size = 30 }) {
    const look = subjectLook(subject)
    return <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: size, height: size, borderRadius: '50%', backgroundColor: look.color, color: '#fff', flex: `0 0 ${size}px` }}>
        <Icon type={look.icon} style={{ fontSize: Math.round(size / 2) }} />
    </span>
}
function SubjectName({ subject, size = 17 }) {
    return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, fontWeight: 700, color: '#1e293b', fontSize: size }}>
        <SubjectIcon subject={subject} />{subject}
    </span>
}
// Average score, colored by range (same ranges as ScoreChip elsewhere): 80%+ green, 50–79% blue, below 50% orange.
function averageColor(avg) {
    if (avg >= 0.8) return '#047857'
    if (avg >= 0.5) return '#1d4ed8'
    return '#b45309'
}
function AverageScore({ avg, big }) {
    if (avg == null) return <span style={{ color: '#475569', fontSize: big ? 17 : undefined, fontWeight: 400 }}>No quizzes yet</span>
    return <span style={{ fontWeight: 800, color: averageColor(avg), fontSize: big ? 26 : 17 }}>{pct(avg)}</span>
}
// Small gray key explaining the average colors.
function AverageColorKey() {
    const item = (color, text) => <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: color }} />{text}
    </span>
    return <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 14, color: '#64748b' }}>
        {item('#047857', '80% or higher')}
        {item('#1d4ed8', '50% to 79%')}
        {item('#b45309', 'Below 50%')}
    </div>
}
// Soft tint per grade level, so a grade always has the same color.
const GRADE_AVATAR = [
    { bg: '#fef3c7', fg: '#92400e' }, // K
    { bg: '#dbeafe', fg: '#1e40af' }, // 1
    { bg: '#d1fae5', fg: '#065f46' }, // 2
    { bg: '#ede9fe', fg: '#5b21b6' }, // 3
    { bg: '#ffedd5', fg: '#9a3412' }, // 4
    { bg: '#cffafe', fg: '#155e75' }, // 5
    { bg: '#fce7f3', fg: '#9d174d' }, // 6
    { bg: '#e0e7ff', fg: '#3730a3' }, // 7
    { bg: '#ecfccb', fg: '#3f6212' }, // 8
    { bg: '#fee2e2', fg: '#991b1b' }, // 9
    { bg: '#e0f2fe', fg: '#075985' }, // 10
    { bg: '#fae8ff', fg: '#86198f' }, // 11
    { bg: '#ccfbf1', fg: '#115e59' }, // 12
]
function StudentInitials({ s }) {
    const initials = (((s.firstName || '')[0] || '') + ((s.lastName || '')[0] || '')).toUpperCase() || '?'
    const rank = studentGradeRank(s.grade)
    const c = rank === 99 ? { bg: '#e2e8f0', fg: '#334155' } : GRADE_AVATAR[rank % GRADE_AVATAR.length]
    return <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 38, height: 38, borderRadius: '50%', backgroundColor: c.bg, color: c.fg, fontWeight: 800, fontSize: 15, flex: '0 0 38px' }}>{initials}</span>
}
function gradeWords(g) {
    if (g == null || g === '') return '—'
    const v = String(g)
    if (/^k/i.test(v)) return 'Kindergarten'
    const n = parseInt(v, 10)
    return isNaN(n) ? v : `Grade ${n}`
}
// Plain text link; the click bubbles up to the row's onClick.
function DetailsLink({ open, showLabel = 'View progress', hideLabel = 'Hide progress' }) {
    return <button type="button" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 0', fontSize: 16, fontWeight: 700, border: 'none', background: 'none', cursor: 'pointer', color: '#1d4ed8', whiteSpace: 'nowrap' }}>
        {open ? hideLabel : showLabel}<Icon type={open ? 'up' : 'down'} style={{ fontSize: 12 }} />
    </button>
}
// Plan badges in one neutral style.
function FlatPlans({ plans }) {
    if (!plans || !plans.length) return <span style={{ color: '#64748b' }}>None</span>
    return <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
        {plans.map(p => <span key={p} style={{ padding: '2px 10px', borderRadius: 6, backgroundColor: '#f1f5f9', border: '1px solid #cbd5e1', color: '#1e293b', fontWeight: 700, fontSize: 14, whiteSpace: 'nowrap' }}>{p}</span>)}
    </span>
}

// ---- Shared building blocks for the flat views ----
const FLAT_SELECT_STYLE = { ...SELECT_STYLE, fontSize: 17, borderColor: '#cbd5e1', width: '100%', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
function FieldLabel({ htmlFor, children }) {
    return <label htmlFor={htmlFor} style={{ display: 'block', fontSize: 14, fontWeight: 700, color: '#64748b', marginBottom: 6 }}>{children}</label>
}
function FlatSelectField({ id, label, value, onChange, title, children }) {
    return <div style={{ minWidth: 0 }}>
        <FieldLabel htmlFor={id}>{label}</FieldLabel>
        <select id={id} title={title} value={value} onChange={e => onChange(e.target.value)} style={FLAT_SELECT_STYLE}>{children}</select>
    </div>
}
function FilterGrid({ children, max }) {
    return <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 16, marginBottom: 24, maxWidth: max }}>{children}</div>
}
// "Oak Elementary" + gray context line.
function ViewHeading({ title, sub }) {
    if (!title && !sub) return null
    return <div style={{ marginBottom: 16 }}>
        {title ? <div style={{ fontSize: 26, fontWeight: 800, color: '#0f172a', lineHeight: 1.25 }}>{title}</div> : null}
        {sub ? <div style={{ fontSize: 16, color: '#475569', marginTop: 2 }}>{sub}</div> : null}
    </div>
}
function StatRow({ children }) {
    return <div style={{ display: 'flex', gap: 48, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 28, paddingBottom: 24, borderBottom: '1px solid #e2e8f0' }}>{children}</div>
}
function FlatStat({ label, value, children }) {
    return <div>
        <div style={{ lineHeight: 1.2, fontSize: 26, fontWeight: 800, color: '#0f172a' }}>{children || value}</div>
        <div style={{ fontSize: 15, color: '#475569' }}>{label}</div>
    </div>
}
function SectionTitle({ children, note, right }) {
    return <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
        <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a' }}>{children}</div>
            {note ? <div style={{ fontSize: 15, color: '#475569', marginTop: 2 }}>{note}</div> : null}
        </div>
        {right || null}
    </div>
}
function flatTh(align = 'left', width) {
    return { textAlign: align, padding: '10px 12px', fontSize: 14, fontWeight: 700, color: '#64748b', borderBottom: '1px solid #cbd5e1', width }
}
function LoadingLine({ text = 'Loading…' }) {
    return <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '60px 0', justifyContent: 'center', fontSize: 18, color: '#334155' }}><Icon type="loading" style={{ fontSize: 24, color: '#2d6a9f' }} />{text}</div>
}
function EmptyLine({ children }) {
    return <div style={{ padding: '40px 0', textAlign: 'center', fontSize: 17, color: '#334155', lineHeight: 1.6 }}>{children}</div>
}
// Average for a quiz-stats object, or why there isn't one.
function QuizScore({ det }) {
    if (!isQuizGiven(det)) return <span style={{ color: '#64748b' }}>Not given yet</span>
    const avg = det && det.total_graded > 0 ? det.grade_sum / det.total_graded : null
    if (avg == null) return <span style={{ color: '#475569' }}>No scores yet</span>
    return <AverageScore avg={avg} />
}
// Quiz + practice numbers for one student, optionally limited to one subject.
function studentStats(s, subject) {
    const q = (s && s.quizzes) || {}, p = (s && s.practice) || {}
    if (!subject || subject === 'All') {
        return { taken: q.taken || 0, avg: q.taken > 0 ? q.grade_sum / q.taken : null, practice: p.count || 0 }
    }
    const qs = (q.by_subject && q.by_subject[subject]) || {}
    return {
        taken: qs.taken || 0,
        avg: qs.taken > 0 ? qs.grade_sum / qs.taken : null,
        practice: (p.by_subject && p.by_subject[subject]) || 0,
    }
}
function filterStudentRows(rows, filter, search) {
    const term = (search || '').trim().toLowerCase()
    return (rows || []).filter(s => {
        const plans = s.plans || []
        if (filter === 'plans' && plans.length === 0) return false
        if (filter && filter !== 'all' && filter !== 'plans' && !plans.includes(filter)) return false
        if (term && !(`${s.firstName || ''} ${s.lastName || ''}`.toLowerCase().includes(term))) return false
        return true
    })
}
function sortStudentRows(rows, sort, subject) {
    const byName = (a, b) => (a.lastName || '').localeCompare(b.lastName || '') || (a.firstName || '').localeCompare(b.firstName || '')
    const out = [...rows]
    if (sort === 'lowest') {
        // students with no quizzes go last
        out.sort((a, b) => {
            const A = studentStats(a, subject).avg, B = studentStats(b, subject).avg
            if (A == null && B == null) return byName(a, b)
            if (A == null) return 1
            if (B == null) return -1
            return (A - B) || byName(a, b)
        })
    } else if (sort === 'quizzes') {
        out.sort((a, b) => (studentStats(b, subject).taken - studentStats(a, subject).taken) || byName(a, b))
    } else if (sort === 'grade') {
        out.sort((a, b) => (studentGradeRank(a.grade) - studentGradeRank(b.grade)) || byName(a, b))
    } else {
        out.sort(byName)
    }
    return out
}
function fmtDate(sec) { if (!sec) return '—'; return new Date(sec * 1000).toLocaleDateString() }
const REPORT_PAGE_STYLE = `
@page { size: landscape; margin: 10mm; }
@media print {
  html, body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; background: #fff; }
  .report-page { break-before: page; page-break-before: always; }
  .report-avoid-break, tr { break-inside: avoid; page-break-inside: avoid; }
  thead { display: table-header-group; }
}
`

// Compact score chip for the printed reports (same ranges and text colors as the dashboard).
function MiniScore({ avg }) {
    if (avg == null) return <span style={{ color: '#64748b' }}>—</span>
    const b = scoreBand(avg)
    return <span style={{ display: 'inline-block', padding: '1px 8px', borderRadius: 6, backgroundColor: b.bg, color: averageColor(avg), border: `1px solid ${b.border}`, fontWeight: 800, fontSize: 13, whiteSpace: 'nowrap' }}>{pct(avg)}</span>
}
// Neutral plan badges for print.
function PrintPlans({ plans }) {
    if (!plans || !plans.length) return <span style={{ color: '#64748b' }}>—</span>
    return <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
        {plans.map(p => <span key={p} style={{ padding: '0 6px', borderRadius: 4, backgroundColor: '#f1f5f9', border: '1px solid #cbd5e1', color: '#1e293b', fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap' }}>{p}</span>)}
    </span>
}
// "How to read" key for the printed reports.
function PrintColorKey({ iep }) {
    const sw = (avg, text) => <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginRight: 16 }}>
        <span style={{ width: 12, height: 12, borderRadius: 3, backgroundColor: scoreBand(avg).bg, border: `2px solid ${averageColor(avg)}` }} />{text}
    </span>
    return <div style={{ fontSize: 13, color: '#334155', lineHeight: 1.7 }}>
        <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: 2 }}>How to read this report</div>
        <div>Average scores are shown as colored percentages: {sw(0.9, '80% or higher')}{sw(0.6, '50% to 79%')}{sw(0.3, 'Below 50%')}</div>
        <div>A dash (—) means no activity yet.</div>
        {iep ? <div>For IEP &amp; MTSS, "on track" is the share of plans on track to meet their goal. Plans with no measurements yet count as on track.</div> : null}
    </div>
}
// Standard-code prefixes per subject, used only to label synthetic demo game standards.
const DEMO_GAME_STD_PREFIXES = {
    'ELA': ['RL', 'RI', 'W', 'SL', 'L'],
    'Math': ['OA', 'NBT', 'NF', 'MD', 'G'],
    'Science': ['PS', 'LS', 'ESS', 'ETS'],
    'Social Studies': ['CIV', 'ECO', 'GEO', 'HIS'],
}
const GAME_DEMO_SUBJECTS = ['ELA', 'Math', 'Science', 'Social Studies']
function demoGradeCodePart(gradeName) {
    const g = String(gradeName || '')
    if (/kinder/i.test(g)) return 'K'
    const m = g.match(/(\d+)/)
    return m ? m[1] : 'G'
}
// Random-ish split of an integer total into n non-negative parts that sum EXACTLY to total.
function splitInt(total, n, rng) {
    if (n <= 0) return []
    if (n === 1) return [total]
    const weights = []; let wsum = 0
    for (let i = 0; i < n; i++) { const w = 0.5 + rng(); weights.push(w); wsum += w }
    const parts = []; let acc = 0
    for (let i = 0; i < n - 1; i++) { const v = Math.round(total * weights[i] / wsum); parts.push(v); acc += v }
    parts.push(Math.max(0, total - acc))
    return parts
}
// ============================================================
// PLAIN-LANGUAGE TOOLTIPS
// ============================================================
const TIPS = {
    gradeLevel: 'The grade and subject this row covers.',
    averageGrade: 'The average grade students earned on the quiz. Green means most students did well, blue is in the middle, and orange means many students struggled. "Give Quiz" means it has not been given yet.',
    viewStandards: 'Click anywhere on this row to see each individual standard in this grade level.',
    school: 'The school this row covers.',
}
function givenTipForSubject(subject) {
    const phrase = (!subject || subject === 'All') ? 'quizzes for all subject areas' : subject + ' quizzes'
    return `How many ${phrase} teachers have given. A dash (—) means none have been given yet.`
}
function givenTipForTeacher(subject) {
    const phrase = (!subject || subject === 'All') ? 'quizzes across all subject areas' : subject + ' quizzes'
    return `How many ${phrase} this teacher has given. A dash (—) means they have not given any yet.`
}
const GIVEN_TIP_STANDARD = 'How many quizzes teachers have given for this standard. A dash (—) means none have been given yet.'
function HeadTip({ label, tip, align = 'right', width, style, icon }) {
    const base = { textAlign: align, padding: '10px 12px', fontSize: 14, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.8, borderBottom: '2px solid #e2e8f0', width }
    return <th style={{ ...base, ...(style || {}) }}>
        <Tooltip title={tip}>
            <span style={{ cursor: 'help', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 4, justifyContent: align === 'right' ? 'flex-end' : 'flex-start' }}>
                {icon ? <Icon type={icon} style={{ fontSize: 14, color: '#94a3b8' }} /> : null}{label}<Icon type="question-circle" style={{ fontSize: 13, color: '#cbd5e1' }} />
            </span>
        </Tooltip>
    </th>
}
function ValTip({ tip, children, block }) {
    return <Tooltip title={tip}><span style={{ cursor: 'help', display: block ? 'inline-block' : undefined }}>{children}</span></Tooltip>
}
function ShowDetails({ open }) {
    return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 15, fontWeight: 700, color: '#2563eb', whiteSpace: 'nowrap' }}>
        {open ? 'Hide details' : 'Show details'}<Icon type={open ? 'up' : 'down'} style={{ fontSize: 12 }} />
    </span>
}
function IEPExpandToggle({ open }) {
    return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 15, fontWeight: 700, color: '#2563eb', whiteSpace: 'nowrap' }}>
        {open ? 'Hide Plans' : 'View Plans'}<Icon type={open ? 'up' : 'down'} style={{ fontSize: 12 }} />
    </span>
}
function iepTsSeconds(ts) {
    if (!ts) return 0
    if (typeof ts.seconds === 'number') return ts.seconds
    if (ts instanceof Date) return ts.getTime() / 1000
    if (typeof ts.toDate === 'function') return ts.toDate().getTime() / 1000
    return 0
}
// ============================================================
// MAIN COMPONENT
// ============================================================
class QuizStats extends Component {
    state = {
        init: false, quizStats: [], districtState: '', schoolsLoaded: false,
        view: 'students', selectedSchoolId: null, showSciSS: false,
        selectedSubject: 'All',
        // standards view selectors + loaded data
        standardsCategory: null, standardsName: null,
        standardDetailData: null, standardDetailLoading: false,
        standardsList: null, standardsLoading: false,
        sortCol: 'label', sortDir: 'asc', sparkMode: 'weekly',
        modalVisible: false, modalStandard: null, modalStandardDetail: null,
        modalQuizDocs: {}, modalQuizLoading: false, modalTab: 'overall',
        teachers: [], teachersDict: {},
        students: [], studentsDict: {},
        quizModalStatsTab: 'teachers',
        expandedRows: {},
        // pacing guide view
        pacingCategory: null, pacingName: null,
        pgLoading: true, pgGuide: null, pgErr: null, pgYear: null,
        // pacing guide per-standard stats (from the per-school pacingGuideStats doc)
        pgStats: null, pgStatsLoading: true, pgStatsSchoolId: null,
        // IEP / MTSS view
        iepType: 'IEP', iepDistrict: null, iepSchools: [], iepLoaded: false,
        selectedYear: null, availableYears: [], yearsLoaded: false,
        gameBaseDocs: [], gameStandardsDocs: [],
        gamesLoaded: false, gamesLoading: false,
        gamesSubject: null,
        // IEP-by-teacher expansion
        iepExpand: {}, iepTeacherDocs: null, iepTeacherDocsSchoolId: null,
        // printable report
        reportModal: false, reportScope: 'district', reportPrinting: false, reportPreparing: false,
        reportSections: { quizzes: true, practice: true, games: true, iep: true },
        // Students view
        studentDocsBySchool: {}, studentFilter: 'all', studentSearch: '',
        studentSubject: 'All', studentSort: 'name', studentLimit: 100, studentGrade: 'all',
        // students-with-plans quiz + practice stats (IEP & MTSS by School)
        iepQuizSchools: [], iepQuizDistrict: null, iepQuizLoaded: false,
        // printable student report
        studentReportModal: false, studentReportScope: 'district', studentReportFilter: 'plans',
        studentReportHistory: true, studentReportPreparing: false, studentReportPrinting: false,
        studentReportPages: null,
        studentPicking: false,
        // Quiz Detailed Results view
        qrStandardKey: null, qrPending: false,
    }
    componentDidMount() {
        document.title = 'Usage & Results - Dot It';
        window.scrollTo(0, 0);
        this.getData();
    }
    componentDidUpdate() {
        const s = this.state
        if (s.view === 'quiz_results' && s.qrPending && !s.standardDetailLoading && !s.standardsLoading) {
            this.setState({ qrPending: false }, () => {
                const rows = this.quizResultsRows()
                const first = rows.find(r => r.hasData) || rows[0]
                if (first) this.selectQuizResultsStandard(first.standard)
            })
            return
        }
        const { view, yearsLoaded, selectedSchoolId, studentDocsBySchool, studentPicking } = this.state
        if (view !== 'students' || !yearsLoaded || studentPicking) return
        // First visit (or after a year change): find a school that has students.
        if (this.studentAutoPick !== false) { this.pickStudentSchool(); return }
        // Otherwise just make sure the selected school's students are loaded.
        if (selectedSchoolId && studentDocsBySchool[selectedSchoolId] === undefined) {
            this.loadStudentDocs(selectedSchoolId)
        }
    }
    componentWillReceiveProps() { this.getData() }
    isDemo() { const did = this.getDistrictId(); return did && did.startsWith('demo_') }
    toggleRow = (key) => {
        this.setState(prev => {
            const e = { ...prev.expandedRows }
            if (e[key]) delete e[key]; else e[key] = true
            return { expandedRows: e }
        })
    }
    // First school in dropdown order; the one default used everywhere.
    defaultSchoolId() { const s = this.allSchoolsSorted(); return s.length ? s[0].id : null }
    isKnownSchool(sid) { return !!sid && this.allSchools().some(s => s.id === sid) }
    // Single entry point for a user picking a school in ANY view. The choice then
    // sticks across views and year changes, and stops the Students auto-pick.
    selectSchool = (sid, extra = {}, cb) => {
        if (!sid) return
        this.userPickedSchool = true
        this.studentAutoPick = false
        this.setState({ selectedSchoolId: sid, expandedRows: {}, iepExpand: {}, studentLimit: 100, ...extra }, cb)
    }
    getData() {
        if (this.props.adminBaseProps && !(this.props.adminBaseProps.district && this.props.adminBaseProps.district.id && this.props.adminBaseProps.district.state)) return
        if (this.props.instructionalFacilitatorBaseProps && !(this.props.instructionalFacilitatorBaseProps.district && this.props.instructionalFacilitatorBaseProps.district.id && this.props.instructionalFacilitatorBaseProps.district.state && this.props.instructionalFacilitatorBaseProps.schools && this.props.instructionalFacilitatorBaseProps.schools.length > 0)) return
        if (this.state.init) return
        this.setState({ init: true, selectedSchoolId: this.state.selectedSchoolId || this.defaultSchoolId() })
        this.discoverYears()
    }

    // Appends the selected-year filter to any quizStats/pacingGuideStats query.
    // When no year is known yet (legacy docs with no yearStart) the query is
    // returned unchanged, so behaviour matches the pre-year-split dashboard.
    withYear = (q) => (this.state.selectedYear ? q.where('yearStart', '==', this.state.selectedYear) : q)

    // [June 1 .. next June 1) in epoch seconds for the selected school year.
    yearWindowSeconds() {
        const ys = this.state.selectedYear
        if (!ys) return null
        const y = parseInt(String(ys).slice(0, 4), 10)
        if (isNaN(y)) return null
        return { start: Date.UTC(y, 5, 1) / 1000, end: Date.UTC(y + 1, 5, 1) / 1000 }
    }

    discoverYears = async () => {
        const districtId = this.getDistrictId()
        if (!districtId) return
        const isIF = !!this.props.instructionalFacilitatorBaseProps
        const base = () => db.collection(ColType.quizStats).where('districtId', '==', districtId)
        let queries
        if (isIF) {
            const sids = this.props.instructionalFacilitatorBaseProps.schools.map(s => s.id)
            queries = []
            this.chunkArray(sids, 30).forEach(chunk => {
                queries.push(base().where('type', '==', 'school').where('schoolId', 'in', chunk))
                queries.push(base().where('type', '==', 'iep-school').where('schoolId', 'in', chunk))
            })
        } else {
            queries = [
                base().where('type', '==', 'district'),
                base().where('type', '==', 'iep-district'),
            ]
        }
        const results = await Promise.all(queries.map(q => q.get().catch(e => { console.log('year discovery error', e); return null })))
        const years = new Set()
        results.forEach(qs => { if (qs) qs.forEach(d => { const ys = d.data().yearStart; if (ys) years.add(ys) }) })
        const list = Array.from(years).sort().reverse()
        const selected = list.length ? list[0] : null
        this.setState({ availableYears: list, selectedYear: selected, yearsLoaded: true }, () => this.loadYearData())
    }

    // Loads school/district/teacher quiz docs + IEP docs for the selected year.
    loadYearData = () => {
        const districtId = this.getDistrictId()
        const ds = this.getDistrictState()
        this.loadIEPStats()
        const parse = d => ({
            ...d,
            quizzes: d.quizzes ? JSON.parse(d.quizzes) : null,
            resources: d.resources ? JSON.parse(d.resources) : null
        })
        const isIF = !!this.props.instructionalFacilitatorBaseProps
        const sids = isIF ? this.props.instructionalFacilitatorBaseProps.schools.map(s => s.id) : null
        const preferred = { sid: this.state.selectedSchoolId, cat: this.state.pacingCategory, name: this.state.pacingName }
        let schoolQuery = isIF
            ? db.collection(ColType.quizStats).where('districtId', '==', districtId).where('type', '==', 'school').where('schoolId', 'in', sids)
            : db.collection(ColType.quizStats).where('districtId', '==', districtId).where('type', '==', 'school')
        schoolQuery = this.withYear(schoolQuery)
        schoolQuery.get().then(qs => {
            const schoolDocs = []
            qs.forEach(d => schoolDocs.push(parse(d.data())))
            this.setState({ quizStats: schoolDocs, districtState: ds, schoolsLoaded: true })
            this.initDefaults(schoolDocs, ds, preferred)
            if (isIF) {
                const districtQuery = this.withYear(db.collection(ColType.quizStats).where('districtId', '==', districtId).where('type', '==', 'district').where('schoolId', 'in', sids))
                const teacherQuery = this.withYear(db.collection(ColType.quizStats).where('districtId', '==', districtId).where('type', '==', 'teacher').where('schoolId', 'in', sids))
                Promise.all([districtQuery.get(), teacherQuery.get()]).then(([dqs, tqs]) => {
                    const restDocs = []
                    dqs.forEach(d => restDocs.push(parse(d.data())))
                    tqs.forEach(d => restDocs.push(parse(d.data())))
                    this.setState(prev => ({ quizStats: [...prev.quizStats, ...restDocs] }))
                })
            } else {
                const restQuery = this.withYear(db.collection(ColType.quizStats).where('districtId', '==', districtId).where('type', 'in', ['district', 'teacher']))
                restQuery.get().then(qs2 => {
                    const restDocs = []
                    qs2.forEach(d => restDocs.push(parse(d.data())))
                    this.setState(prev => ({ quizStats: [...prev.quizStats, ...restDocs] }))
                })
            }
        })
    }

    // Switch the whole dashboard to a different school year.
    onYearChange = (year) => {
        if (!year || year === this.state.selectedYear) return
        this.studentDocPromises = {}
        this.studentAutoPick = !this.userPickedSchool
        this.setState({
            selectedYear: year,
            quizStats: [], schoolsLoaded: false,
            iepSchools: [], iepDistrict: null, iepLoaded: false,
            pgStats: null, pgStatsLoading: true, pgStatsSchoolId: null,
            pgGuide: null, pgLoading: true, pgErr: null,
            standardDetailData: null, standardDetailLoading: false,
            standardsList: null, standardsLoading: false,
            expandedRows: {}, modalVisible: false,
            gameBaseDocs: [], gameStandardsDocs: [], gamesLoaded: false, gamesSubject: null,
            iepExpand: {}, iepTeacherDocs: null, iepTeacherDocsSchoolId: null,
            studentDocsBySchool: {}, studentLimit: 100,
            iepQuizSchools: [], iepQuizDistrict: null, iepQuizLoaded: false,
            qrStandardKey: null, qrPending: false, modalStandardDetail: null, modalTab: 'overall',
        }, () => {
            this.loadYearData()
            if (String(this.state.view).startsWith('games_')) this.loadGameStats()
            if (this.state.view === 'students') this.loadStudentDocs(this.state.selectedSchoolId)
        })
    }
    loadIEPStats() {
        const districtId = this.getDistrictId()
        if (!districtId) return
        const isIF = !!this.props.instructionalFacilitatorBaseProps
        const sids = isIF ? this.props.instructionalFacilitatorBaseProps.schools.map(s => s.id) : null
        const parse = d => ({ ...d, iep: d.data ? JSON.parse(d.data) : null })
        let schoolQ = db.collection(ColType.quizStats).where('districtId', '==', districtId).where('type', '==', 'iep-school')
        if (isIF) schoolQ = schoolQ.where('schoolId', 'in', sids)
        schoolQ = this.withYear(schoolQ)
        schoolQ.get().then(qs => {
            // One doc per school: if the year filter wasn't applied, keep the newest yearStart.
            const bySchool = {}
            qs.forEach(d => {
                const r = parse(d.data())
                const cur = bySchool[r.schoolId]
                if (!cur || String(r.yearStart || '') > String(cur.yearStart || '')) bySchool[r.schoolId] = r
            })
            const rows = Object.values(bySchool)
            this.setState(prev => ({
                iepSchools: rows,
                iepLoaded: true,
                selectedSchoolId: prev.selectedSchoolId || this.defaultSchoolId(),
            }))
        }).catch(e => { console.log('iep stats load error', e); this.setState({ iepLoaded: true }) })
        if (!isIF) {
            this.withYear(db.collection(ColType.quizStats).where('districtId', '==', districtId).where('type', '==', 'iep-district')).get().then(qs => {
                let dd = null; qs.forEach(d => { if (!dd) dd = parse(d.data()) }); this.setState({ iepDistrict: dd })
            }).catch(() => { })
        }

        // ---- Quiz + practice stats for students with plans ----
        let quizQ = db.collection(ColType.quizStats).where('districtId', '==', districtId).where('type', '==', 'iep-quiz-school')
        if (isIF) quizQ = quizQ.where('schoolId', 'in', sids)
        this.withYear(quizQ).get().then(qs => {
            const rows = []
            qs.forEach(d => {
                const x = d.data()
                rows.push({ schoolId: x.schoolId, groups: x.data ? JSON.parse(x.data) : {} })
            })
            this.setState({ iepQuizSchools: rows, iepQuizLoaded: true })
        }).catch(e => { console.log('iep quiz stats load error', e); this.setState({ iepQuizLoaded: true }) })
        if (!isIF) {
            this.withYear(db.collection(ColType.quizStats).where('districtId', '==', districtId).where('type', '==', 'iep-quiz-district')).get().then(qs => {
                let groups = null
                qs.forEach(d => { if (!groups) { const x = d.data(); groups = x.data ? JSON.parse(x.data) : null } })
                this.setState({ iepQuizDistrict: groups })
            }).catch(() => { })
        }
    }
    loadStudentDocs = (sid) => {
        if (!sid) return Promise.resolve([])
        const cached = this.state.studentDocsBySchool[sid]
        if (cached) return Promise.resolve(cached)
        if (!this.studentDocPromises) this.studentDocPromises = {}
        const year = this.state.selectedYear
        const key = (year || '') + '|' + sid
        if (this.studentDocPromises[key]) return this.studentDocPromises[key]
        const districtId = this.getDistrictId()
        const p = this.withYear(db.collection(ColType.quizStats)
            .where('districtId', '==', districtId)
            .where('type', '==', 'student-school')
            .where('schoolId', '==', sid))
            .get()
            .then(qs => {
                const chunks = []
                qs.forEach(d => {
                    const x = d.data()
                    chunks.push({ i: x.chunkIndex || 0, rows: x.data ? JSON.parse(x.data) : [] })
                })
                chunks.sort((a, b) => a.i - b.i)
                const rows = [].concat(...chunks.map(c => c.rows))
                // ignore a response that arrives after the year was switched
                if (this.state.selectedYear === year) {
                    this.setState(prev => ({ studentDocsBySchool: { ...prev.studentDocsBySchool, [sid]: rows } }))
                }
                return rows
            })
            .catch(e => {
                console.log('student stats load error', e)
                if (this.state.selectedYear === year) {
                    this.setState(prev => ({ studentDocsBySchool: { ...prev.studentDocsBySchool, [sid]: [] } }))
                }
                return []
            })
            .finally(() => { delete this.studentDocPromises[key] })
        this.studentDocPromises[key] = p
        return p
    }
    // Picks the first school (current one first, then dropdown order) that has
    // any student rows. Runs once on first visit and again after a year change.
    pickStudentSchool = async () => {
        if (this.studentPickRunning) return
        this.studentPickRunning = true
        this.setState({ studentPicking: true })
        const year = this.state.selectedYear
        const current = this.state.selectedSchoolId
        const ordered = this.allSchoolsSorted().map(s => s.id)
        const candidates = current ? [current, ...ordered.filter(id => id !== current)] : ordered
        let found = null
        for (const sid of candidates) {
            const rows = await this.loadStudentDocs(sid)
            // stop if the year changed or the user picked a school themselves
            if (this.state.selectedYear !== year || this.studentAutoPick === false) break
            if (rows && rows.length) { found = sid; break }
        }
        const stillAuto = this.studentAutoPick !== false && this.state.selectedYear === year
        this.studentAutoPick = false
        this.studentPickRunning = false
        this.setState(prev => ({
            studentPicking: false,
            ...(stillAuto && found && found !== prev.selectedSchoolId
                ? { selectedSchoolId: found, expandedRows: {}, studentLimit: 100 }
                : {}),
        }))
    }
    onStudentsSchoolChange = (sid) => {
        this.selectSchool(sid, { studentGrade: 'all' }, () => this.loadStudentDocs(sid))
    }
    openStudentsForSchool = (sid, filter) => {
        this.selectSchool(sid, { view: 'students', studentFilter: filter || 'all', studentSearch: '' }, () => {
            window.scrollTo({ top: 0 })
            this.loadStudentDocs(sid)
        })
    }
    // Runs before the student report prints: loads the chosen schools' student docs.
    prepareStudentReport = async () => {
        await new Promise(resolve => this.setState({ studentReportPreparing: true }, resolve))
        try {
            const scope = this.state.studentReportScope || 'district'
            const schools = scope === 'district' ? this.allSchoolsSorted() : this.allSchools().filter(s => s.id === scope)
            const lists = await Promise.all(schools.map(s => this.loadStudentDocs(s.id)))
            const pages = schools.map((s, i) => ({
                sid: s.id,
                name: this.getSchoolName(s.id),
                rows: sortStudentRows(filterStudentRows(lists[i] || [], this.state.studentReportFilter, ''), 'name', 'All'),
            })).filter(p => p.rows.length > 0)
            await new Promise(resolve => this.setState({ studentReportPages: pages, studentReportPrinting: true }, resolve))
        } catch (e) {
            console.log('student report prepare error', e)
            this.setState({ studentReportPreparing: false, studentReportPrinting: false })
            throw e
        }
    }
    // Game stats use bare year ('2026'), derived the same way loadPacingGuide does.
    gameYear() {
        const sy = this.state.selectedYear
        if (sy) return String(sy).slice(0, 4)
        const y = this.state.standardYears
        return y ? y.currentYear : null
    }
    getSchoolName(sid) {
        const s = this.allSchoolsDict()[sid]
        if (s) return s.schoolName || s.name || sid
        const sd = this.schoolDocs().find(d => d.schoolId === sid)
        return (sd && sd.quizzes && sd.quizzes.name) || sid
    }
    gameSubjects() {
        const ds = this.getDistrictState()
        return getStateStandardCategories(ds)   // ['ELA','Math','Science','Social Studies', ...]
    }
    gameGradesForSubject(subject) {
        const ds = this.getDistrictState()
        const dict = getStateStandardNamesDict(ds)
        return (dict && dict[subject]) || []
    }
    // Teachers for a demo school: reuse the quiz teachers_summary names when present,
    // otherwise fabricate a small deterministic roster.
    demoGameTeachersForSchool(sid) {
        const sd = this.schoolDocs().find(d => d.schoolId === sid)
        if (sd && sd.quizzes && sd.quizzes.teachers_summary && Object.keys(sd.quizzes.teachers_summary).length) {
            return Object.entries(sd.quizzes.teachers_summary).map(([tid, t]) => ({
                id: tid,
                firstName: t.firstName || 'Teacher',
                lastName: t.lastName || String(tid).substring(0, 6),
                gradeLevel: t.gradeLevel || ''
            }))
        }
        const rng = seededRandom(hashString('demo-game-teachers-' + sid))
        const firsts = ['Avery', 'Jordan', 'Taylor', 'Morgan', 'Riley', 'Casey', 'Jamie', 'Quinn', 'Reese', 'Sydney']
        const lasts = ['Nguyen', 'Patel', 'Garcia', 'Johnson', 'Williams', 'Brown', 'Davis', 'Martinez', 'Lee', 'Clark']
        const n = 4 + Math.floor(rng() * 4)
        const out = []
        for (let i = 0; i < n; i++) out.push({
            id: 'demo-game-teacher-' + sid + '-' + i,
            firstName: firsts[Math.floor(rng() * firsts.length)],
            lastName: lasts[Math.floor(rng() * lasts.length)],
            gradeLevel: ''
        })
        return out
    }

    // Front-end-only synthetic game stats for demo districts. Builds the same
    // gameBaseDocs / gameStandardsDocs shapes loadGameStats would fetch, so every
    // games view renders unchanged. Deterministic per (school, year).
    buildDemoGameData = () => {
        const districtId = this.getDistrictId()
        const gy = this.gameYear() || (this.state.selectedYear ? String(this.state.selectedYear).slice(0, 4) : '2026')
        const subjects = GAME_DEMO_SUBJECTS
        const schools = this.sortedGameSchools()
        const base = [], std = []

        schools.forEach(school => {
            const sid = school.id
            const rng = seededRandom(hashString('demo-game-' + sid + '-' + gy))
            const teachers = this.demoGameTeachersForSchool(sid)
            const byTeacher = {}
            const agg = {}   // 'subject||grade' -> totals, used to derive coherent standards docs

            teachers.forEach(teacher => {
                const shuffled = subjects.slice().sort(() => rng() - 0.5)
                const teacherSubjects = shuffled.slice(0, 1 + (rng() < 0.5 ? 1 : 0))   // 1-2 subjects each
                const categories = {}
                teacherSubjects.forEach(subject => {
                    let grades = this.gameGradesForSubject(subject)
                    if (!grades || !grades.length) grades = GRADE_ORDER.slice(0, 6)
                    const grade = (teacher.gradeLevel && grades.includes(teacher.gradeLevel))
                        ? teacher.gradeLevel : grades[Math.floor(rng() * grades.length)]
                    const sessions = 15 + Math.floor(rng() * 100)
                    const playtimeSeconds = sessions * (180 + Math.floor(rng() * 300))
                    const questions = sessions * (5 + Math.floor(rng() * 10))
                    const correct = Math.round(questions * (0.5 + rng() * 0.4))
                    const incorrect = Math.max(0, questions - correct)
                    categories[subject] = { standardName: grade, playtimeSeconds, correct, incorrect, sessions }
                    const k = subject + '||' + grade
                    if (!agg[k]) agg[k] = { subject, grade, playtimeSeconds: 0, correct: 0, incorrect: 0, sessions: 0 }
                    agg[k].playtimeSeconds += playtimeSeconds; agg[k].correct += correct
                    agg[k].incorrect += incorrect; agg[k].sessions += sessions
                })
                byTeacher[teacher.id] = { firstName: teacher.firstName, lastName: teacher.lastName, categories }
            })

            base.push({ schoolId: sid, districtId, year: gy, type: 'base', by_teacher: byTeacher })

            // Split each (subject, grade) total across a few standards so the
            // Games-by-Standard totals reconcile with the school/teacher rollups.
            const bySubject = {}
            Object.values(agg).forEach(a => {
                if (!bySubject[a.subject]) bySubject[a.subject] = {}
                const prefixes = DEMO_GAME_STD_PREFIXES[a.subject] || ['STD']
                const gp = demoGradeCodePart(a.grade)
                const n = 3 + Math.floor(rng() * 4)
                const pt = splitInt(a.playtimeSeconds, n, rng), co = splitInt(a.correct, n, rng)
                const ic = splitInt(a.incorrect, n, rng), se = splitInt(a.sessions, n, rng)
                for (let i = 0; i < n; i++) {
                    const code = gp + '.' + prefixes[i % prefixes.length] + '.' + (Math.floor(i / prefixes.length) + 1)
                    bySubject[a.subject][code] = { standardName: a.grade, playtimeSeconds: pt[i], correct: co[i], incorrect: ic[i], sessions: se[i] }
                }
            })
            Object.keys(bySubject).forEach(subject => {
                std.push({ schoolId: sid, districtId, year: gy, type: 'standards', standardCategory: subject, standards: bySubject[subject] })
            })
        })

        // The games views read the shared selectedSchoolId, so nothing school-related is set here.
        this.setState({ gameBaseDocs: base, gameStandardsDocs: std, gamesLoaded: true, gamesLoading: false }, () => {
            if (!this.state.gamesSubject || !GAME_DEMO_SUBJECTS.includes(this.state.gamesSubject)) {
                this.setState({ gamesSubject: GAME_DEMO_SUBJECTS[0] })
            }
        })
    }
    loadGameStats = async () => {
        const did = this.getDistrictId()
        if (!did) return
        if (this.isDemo()) { this.buildDemoGameData(); return }
        let gy = this.gameYear()
        if (!gy) {
            try {
                const doc = await db.collection(ColType.standardYears).doc('all').get()
                if (doc.exists) { const sy = flattenDoc(doc); this.setState({ standardYears: sy }); gy = sy.currentYear }
            } catch (e) { /* non-fatal */ }
        }
        if (!gy) { this.setState({ gamesLoaded: true }); return }
        this.setState({ gamesLoading: true })
        const isIF = !!this.props.instructionalFacilitatorBaseProps
        const sids = isIF ? this.props.instructionalFacilitatorBaseProps.schools.map(s => s.id) : null
        let baseQ = db.collection(ColType.gameStats).where('districtId', '==', did).where('year', '==', gy).where('type', '==', 'base')
        let stdQ = db.collection(ColType.gameStats).where('districtId', '==', did).where('year', '==', gy).where('type', '==', 'standards')
        if (isIF) { baseQ = baseQ.where('schoolId', 'in', sids); stdQ = stdQ.where('schoolId', 'in', sids) }
        try {
            const [bqs, sqs] = await Promise.all([baseQ.get(), stdQ.get()])
            const base = [], std = []
            bqs.forEach(d => base.push(d.data()))
            sqs.forEach(d => std.push(d.data()))
            // The games views read the shared selectedSchoolId, so nothing school-related is set here.
            this.setState({ gameBaseDocs: base, gameStandardsDocs: std, gamesLoaded: true, gamesLoading: false }, () => {
                const subs = this.gameSubjects()
                if (!this.state.gamesSubject || !subs.includes(this.state.gamesSubject)) this.setState({ gamesSubject: subs[0] || null })
            })
        } catch (e) {
            console.log('game stats load error', e)
            this.setState({ gamesLoaded: true, gamesLoading: false })
        }
    }
    onGamesSchoolChange = (sid) => this.selectSchool(sid)
    iepRate(t) { return t && t.total > 0 ? t.on_track / t.total : null }
    getDistrict() {
        if (this.props.adminBaseProps) return this.props.adminBaseProps.district
        if (this.props.instructionalFacilitatorBaseProps) return this.props.instructionalFacilitatorBaseProps.district
        return null
    }
    // Grade then last-name ordering, mirroring the MTSS/IEP page.
    compareIEPStudents = (a, b) => {
        const ga = a.grade === 'K' ? -1 : parseInt(a.grade, 10)
        const gb = b.grade === 'K' ? -1 : parseInt(b.grade, 10)
        const na = isNaN(ga) ? 999 : ga
        const nb = isNaN(gb) ? 999 : gb
        if (na !== nb) return na - nb
        return ((a.lastName || '') + '').localeCompare((b.lastName || '') + '')
    }
    setStateIepExpand = (teacherId, data) => {
        this.setState(prev => ({ iepExpand: { ...prev.iepExpand, [teacherId]: data } }))
    }
    onIEPSchoolChange = (sid) => this.selectSchool(sid)
    // Full teacher docs for the current school (loaded once, cached), so
    // TeacherStudentReport gets the same teacher shape the MTSS page passes it.
    ensureIEPTeacherDocs = async () => {
        const schoolId = this.state.selectedSchoolId
        const districtId = this.getDistrictId()
        if (!schoolId || !districtId) return {}
        if (this.state.iepTeacherDocs && this.state.iepTeacherDocsSchoolId === schoolId) return this.state.iepTeacherDocs
        const dict = {}
        const add = qs => qs.forEach(doc => { const t = flattenDoc(doc); if (!dict[t.id]) dict[t.id] = t })
        try {
            const qs1 = await db.collection(ColType.teacher).where('districtId', '==', districtId).where('schoolIds', 'array-contains', schoolId).get()
            add(qs1)
        } catch (e) { console.log('iep teacher docs array-contains error', e) }
        if (Object.keys(dict).length === 0) {
            try {
                const qs2 = await db.collection(ColType.teacher).where('districtId', '==', districtId).where('schoolId', '==', schoolId).get()
                add(qs2)
            } catch (e) { console.log('iep teacher docs schoolId error', e) }
        }
        this.setState({ iepTeacherDocs: dict, iepTeacherDocsSchoolId: schoolId })
        return dict
    }
    toggleIEPTeacherRow = (teacherId) => {
        const key = `iepteacher:${teacherId}`
        const willOpen = !this.state.expandedRows[key]
        this.toggleRow(key)
        if (willOpen && !this.state.iepExpand[teacherId]) this.loadIEPTeacherStudents(teacherId)
    }
    onIEPTeacherStudentChange = (teacherId, studentId) => {
        this.setState(prev => {
            const cur = prev.iepExpand[teacherId] || {}
            return { iepExpand: { ...prev.iepExpand, [teacherId]: { ...cur, selectedStudentId: studentId } } }
        })
    }
    // Build the dropdown from the teacher's plans (same rules as generate_iep_stats):
    // written by this teacher, current folder, not archived, created in the selected year.
    // IFs are limited by security rules to their own schools, so their query carries a
    // schoolId filter; if that is still rejected, fall back to the teacher's roster.
    loadIEPTeacherStudents = async (teacherId) => {
        const districtId = this.getDistrictId()
        const schoolId = this.state.selectedSchoolId
        if (!districtId || !teacherId || !schoolId) return
        this.setStateIepExpand(teacherId, { loading: true })
        const isIF = !!this.props.instructionalFacilitatorBaseProps
        const win = this.yearWindowSeconds()
        const inYear = iep => {
            if (!win) return true
            const s = iepTsSeconds(iep.timeStamp)
            return s >= win.start && s < win.end
        }
        const keep = iep => iep && iep.studentId && !iep.isArchived && iep.teacherId === teacherId && inYear(iep)
        try {
            const teacherDocs = await this.ensureIEPTeacherDocs()
            const teacherDoc = (teacherDocs && teacherDocs[teacherId]) || { id: teacherId }

            const goalsByStudent = {}
            const addGoal = iep => {
                if (!keep(iep)) return
                if (!goalsByStudent[iep.studentId]) goalsByStudent[iep.studentId] = { IEPGoals: [], IEPDict: {} }
                if (goalsByStudent[iep.studentId].IEPDict[iep.id]) return
                goalsByStudent[iep.studentId].IEPGoals.push(iep)
                goalsByStudent[iep.studentId].IEPDict[iep.id] = iep
            }
            let studentDocsById = {}

            // 1) Plan-based: the teacher's own plans (includes students no longer on the roster).
            let planQueryOk = false
            try {
                let q = db.collection(ColType.iep)
                    .where('districtId', '==', districtId)
                    .where('teacherId', '==', teacherId)
                    .where('folder', '==', FOLDER_CURRENT)
                if (isIF) q = q.where('schoolId', '==', schoolId)
                const qs = await q.get()
                qs.forEach(doc => addGoal(flattenDoc(doc)))
                planQueryOk = true
            } catch (e) {
                console.log('iep plan-based query rejected, falling back to roster', e)
            }

            // 2) Fallback: current roster at this school -> each student's plans (the query IFs are allowed to run).
            if (!planQueryOk) {
                const sqs = await db.collection(ColType.student)
                    .where('teacherIds', 'array-contains', teacherId)
                    .where('schoolId', '==', schoolId)
                    .where('districtId', '==', districtId)
                    .where('folder', '==', FOLDER_CURRENT)
                    .get()
                const roster = []
                sqs.forEach(d => { const s = flattenDoc(d); roster.push(s); studentDocsById[s.id] = s })
                await Promise.all(roster.map(async s => {
                    try {
                        const gs = await db.collection(ColType.iep)
                            .where('districtId', '==', districtId)
                            .where('studentId', '==', s.id)
                            .where('folder', '==', FOLDER_CURRENT)
                            .get()
                        gs.forEach(doc => addGoal(flattenDoc(doc)))
                    } catch (e) { /* skip this student */ }
                }))
            }

            // Student docs: fetch by id for any we don't already have (students may have left the roster).
            const ids = Object.keys(goalsByStudent)
            const missing = ids.filter(id => !studentDocsById[id])
            const snaps = await Promise.all(missing.map(id => db.collection(ColType.student).doc(id).get().catch(() => null)))
            snaps.forEach((snap, i) => {
                studentDocsById[missing[i]] = (snap && snap.exists)
                    ? flattenDoc(snap)
                    : { id: missing[i], firstName: 'Unknown', lastName: 'Student' }
            })

            const students = ids.map(id => studentDocsById[id]).sort(this.compareIEPStudents)
            const studentDict = {}
            students.forEach(s => { studentDict[s.id] = s })
            this.setStateIepExpand(teacherId, {
                loading: false, teacher: teacherDoc,
                students, studentDict, goalsByStudent,
                selectedStudentId: students.length ? students[0].id : null,
            })
        } catch (e) {
            console.log('iep teacher expansion load error', e)
            this.setStateIepExpand(teacherId, { loading: false, students: [], studentDict: {}, goalsByStudent: {}, selectedStudentId: null })
        }
    }
    renderIEPTeacherExpansion(row) {
        const teacherId = row.key
        const data = this.state.iepExpand[teacherId]
        const wrap = {}
        if (!data || data.loading) {
            return <div style={{ ...wrap, display: 'flex', alignItems: 'center', gap: 10, padding: '12px 0 12px 18px', color: '#334155', fontSize: 17 }}>
                <Icon type="loading" style={{ fontSize: 20, color: '#2d6a9f' }} />Loading students…
            </div>
        }
        if (!data.students || data.students.length === 0) {
            return <div style={{ ...wrap, padding: '12px 0 12px 18px', color: '#334155', fontSize: 17 }}>This teacher has no students with IEP or MTSS plans this school year.</div>
        }
        const selectedStudentId = data.selectedStudentId
        const selectedStudent = selectedStudentId ? data.studentDict[selectedStudentId] : null
        const goals = (selectedStudentId && data.goalsByStudent[selectedStudentId]) || { IEPGoals: [], IEPDict: {} }
        const teacherForProps = {
            ...(data.teacher || {}),
            id: teacherId,
            firstName: (data.teacher && data.teacher.firstName) || row.firstName || '',
            lastName: (data.teacher && data.teacher.lastName) || row.lastName || '',
            schoolId: (data.teacher && data.teacher.schoolId) || this.state.selectedSchoolId,
            districtId: (data.teacher && data.teacher.districtId) || this.getDistrictId(),
        }
        const reportProps = {
            schoolId: this.state.selectedSchoolId,
            teacherId: teacherId,
            teacher: teacherForProps,
            studentId: selectedStudentId,
            student: selectedStudent,
            students: data.students,
            studentDict: data.studentDict,
            IEPGoals: goals.IEPGoals,
            IEPDict: goals.IEPDict,
            district: this.getDistrict(),
        }
        return <div style={wrap}>
            <div style={{ marginBottom: 16, maxWidth: 380 }}>
                <FieldLabel>Choose a student ({fmt(data.students.length)})</FieldLabel>
                <Select showSearch value={selectedStudentId || undefined} placeholder="Choose a student" style={{ width: '100%' }} size="large" optionFilterProp="children"
                    onChange={sid => this.onIEPTeacherStudentChange(teacherId, sid)}
                    filterOption={(input, option) => {
                        if (!input) return true
                        const person = option.props.children.props.person
                        const fullName = ((person.firstName || '') + ' ' + (person.lastName || '')).toLowerCase()
                        return fullName.includes(input.toLowerCase())
                    }}>
                    {data.students.map(s => <Select.Option value={s.id} key={s.id}>
                        <PersonAvatar person={s} containerClassName="font-16" avatarClassName="font-16 font-bold mr-2" personClassName="font-black font-16" />
                    </Select.Option>)}
                </Select>
            </div>
            {selectedStudent
                ? <TeacherStudentReport key={selectedStudentId} teacherBaseProps={reportProps} showStudentNav={false} showFooter={false} noMarginTop={true} isAdmin={true} />
                : <div style={{ padding: '16px 0', color: '#334155', fontSize: 17 }}>Choose a student to see their IEP and MTSS goals.</div>}
        </div>
    }

    chunkArray = (array, size) => { const c = []; for (let i = 0; i < array.length; i += size) c.push(array.slice(i, i + size)); return c }
    getTeacherAssessmentStats = (ta) => { let stats = { totalAssessments: 0, correct: 0, total: 0, totalGrade: 0, avg: 0 }; Object.keys(ta.studentGrades).forEach(sid => { const sg = ta.studentGrades[sid]; if (sg.graded) { stats.totalAssessments++; stats.correct += sg.finalGrade.correct; stats.total += sg.finalGrade.total; stats.totalGrade += sg.finalGrade.grade } }); if (stats.totalAssessments > 0) stats.avg = stats.totalGrade / stats.totalAssessments; return stats }
    handleTeacherAssessmentQuerySnapshot = (taList) => {
        let teacherAssessments = [], teacherAssessmentDict = {}, teacherAssessmentCountDict = {}, teacherAssessmentRetakes = {}, teachers = [], teachersDict = {}, individualTeacherRetakeDict = {}
        taList.forEach(ta => {
            if (ta.quiz && isV3or4(ta.quiz.version)) { ta.quiz.docs = injectBaseHref(ta.quiz.docs) }
            ta.totalStats = this.getTeacherAssessmentStats(ta)
            if (ta.totalStats.totalAssessments > 0) {
                ta.title = ta.teacher.firstName + ' ' + ta.teacher.lastName; ta.value = (ta.totalStats.avg * 100).toFixed(2)
                if (!teacherAssessmentCountDict[ta.teacher.id]) teacherAssessmentCountDict[ta.teacher.id] = 0
                const count = teacherAssessmentCountDict[ta.teacher.id]
                if (!teacherAssessmentRetakes[count]) teacherAssessmentRetakes[count] = []
                if (!individualTeacherRetakeDict[ta.teacher.id]) individualTeacherRetakeDict[ta.teacher.id] = []
                teacherAssessmentRetakes[count].push(ta); individualTeacherRetakeDict[ta.teacher.id].push(ta); teacherAssessmentCountDict[ta.teacher.id]++
            }
            teacherAssessments.push(ta); teacherAssessmentDict[ta.id] = ta
            if (!teachersDict[ta.teacher.id]) { teachersDict[ta.teacher.id] = ta.teacher; teachers.push(ta.teacher) }
        })
        teachers.sort((a, b) => (a.lastName || '').localeCompare(b.lastName || ''))
        teacherAssessments.sort((a, b) => (a.teacherId || '').localeCompare(b.teacherId || ''))
        this.setState({ teacherAssessments, teacherAssessmentDict, teacherAssessmentCountDict, teacherAssessmentRetakes, teachers, teachersDict, individualTeacherRetakeDict, loadingTeachers: false })
    }
    getOverallModalQuestions = (detail) => {
        if (!detail) return null
        const quizzes = detail.quizzes || {}
        const quizKeys = Object.keys(quizzes)
        const agg = {}
        let foundAny = false
        quizKeys.forEach(qk => {
            const qd = quizzes[qk]
            const questions = qd && qd.questions
            if (!questions) return
            foundAny = true
            Object.keys(questions).forEach(qi => {
                const q = questions[qi]
                if (!agg[qi]) agg[qi] = { total: 0, correct: 0 }
                agg[qi].total += q.total || 0
                agg[qi].correct += q.correct || 0
            })
        })
        if (foundAny) return agg
        return detail.questions || null
    }
    getActiveSchoolId() { return this.state.selectedSchoolId || null }
    getSelectedQuizId() {
        const { modalVisible, modalTab, modalStandardDetail, standardDetailData, view } = this.state
        const inDetail = modalVisible || view === 'quiz_results'
        if (inDetail && modalStandardDetail && modalTab && modalTab !== 'overall') {
            const qd = modalStandardDetail.quizzes && modalStandardDetail.quizzes[modalTab]
            return qd && qd.quizId ? qd.quizId : null
        }
        if (view === 'quiz_results') return null
        if (view === 'standards' && standardDetailData && standardDetailData.quizzes) {
            const qk = Object.keys(standardDetailData.quizzes).map(Number).filter(n => !isNaN(n)).sort((a, b) => a - b)
            if (qk.length > 0) { const fq = standardDetailData.quizzes[String(qk[0])]; return fq && fq.quizId ? fq.quizId : null }
        }
        return null
    }
    // Fetch a demo teacher's students. Instructional facilitators are limited by
    // Firestore rules to their own schools, so their student reads MUST carry a
    // schoolId filter — without it the query is rejected and no demo data can be
    // built. Demo teachers (from teachers_summary) have no schoolId of their own,
    // so we scope by the teacher's school(s) when present, else the active school.
    fetchDemoStudentsForTeacher = (teacher) => {
        const districtId = this.getDistrictId()
        let schoolIds = []
        if (teacher && teacher.schoolIds && teacher.schoolIds.length > 0) schoolIds = teacher.schoolIds.slice()
        else if (teacher && teacher.schoolId) schoolIds = [teacher.schoolId]
        if (schoolIds.length === 0) {
            const active = this.getActiveSchoolId()
            if (active) schoolIds = [active]
        }
        // Per-school query: try the richer (folder + ordered) form, fall back to the
        // plain schoolId-scoped form, which is known to be allowed for IFs.
        const queryForSchool = (schoolId) =>
            db.collection(ColType.student)
                .where('teacherIds', 'array-contains', teacher.id)
                .where('schoolId', '==', schoolId)
                .where('districtId', '==', districtId)
                .where('folder', '==', 'current')
                .orderBy('grade', 'desc')
                .get()
                .then(qs => { const docs = []; qs.forEach(doc => docs.push(flattenDoc(doc))); return docs })
                .catch(() =>
                    db.collection(ColType.student)
                        .where('teacherIds', 'array-contains', teacher.id)
                        .where('schoolId', '==', schoolId)
                        .where('districtId', '==', districtId)
                        .get()
                        .then(qs => { const docs = []; qs.forEach(doc => docs.push(flattenDoc(doc))); return docs })
                        .catch(e2 => { console.log('Could not fetch students for teacher', teacher.id, e2); return [] })
                )
        // No school info at all (admin-only edge case) — district-scoped fallback.
        if (schoolIds.length === 0) {
            return db.collection(ColType.student)
                .where('teacherIds', 'array-contains', teacher.id)
                .where('districtId', '==', districtId)
                .limit(30)
                .get()
                .then(qs => { const docs = []; qs.forEach(doc => docs.push(flattenDoc(doc))); return { teacher, students: docs } })
                .catch(e2 => { console.log('Could not fetch students for teacher', teacher.id, e2); return { teacher, students: [] } })
        }
        return Promise.all(schoolIds.map(queryForSchool)).then(lists => {
            const seen = {}, students = []
            lists.forEach(list => list.forEach(s => { if (!seen[s.id]) { seen[s.id] = true; students.push(s) } }))
            return { teacher, students }
        })
    }
    getDemoSchoolTeachers() {
        const sid = this.getActiveSchoolId();
        const sd = this.schoolDocs().find(d => d.schoolId === sid);
        if (!sd || !sd.quizzes || !sd.quizzes.teachers_summary) return null;
        return Object.entries(sd.quizzes.teachers_summary).map(([tid, t]) =>
        ({
            id: tid, firstName: t.firstName || 'Teacher', lastName: t.lastName || tid.substring(0, 8),
            avatarColor: DEMO_AVATAR_COLORS[Math.abs(hashString(tid)) % DEMO_AVATAR_COLORS.length]
        }))
    }
    loadAllDemoQuizData = async () => {
        const { modalStandardDetail, modalQuizDocs } = this.state
        if (!modalStandardDetail) return
        const quizzes = modalStandardDetail.quizzes || {}
        const quizIndices = Object.keys(quizzes).map(Number).filter(n => !isNaN(n)).sort((a, b) => a - b)
        const schoolTeachers = this.getDemoSchoolTeachers()
        if (!schoolTeachers || schoolTeachers.length === 0) return
        const studentPromises = schoolTeachers.map(teacher => this.fetchDemoStudentsForTeacher(teacher))
        const results = await Promise.all(studentPromises)
        const teachersWithStudents = results.filter(r => r.students.length > 0)
        if (teachersWithStudents.length === 0) return
        const allDemoQuizData = {}
        const overallQuestionStats = {}
        for (const qi of quizIndices) {
            const qd = quizzes[String(qi)]
            if (!qd || !qd.total_graded) continue
            const qid = qd.quizId
            const qdoc = qid ? modalQuizDocs[qid] : null
            const numQuestions = (qdoc && qdoc.numQuestions)
                ? qdoc.numQuestions
                : (qd.questions && Object.keys(qd.questions).length > 0
                    ? Object.keys(qd.questions).length : 3)
            const maxTeachers = Math.max(0, qd.given || 0)
            const limitedTeachers = teachersWithStudents
                .slice()
                .sort((a, b) => {
                    const ah = hashString(`${qid}-${a.teacher.id}`)
                    const bh = hashString(`${qid}-${b.teacher.id}`)
                    return ah - bh
                })
                .slice(0, maxTeachers)
            if (limitedTeachers.length === 0) continue
            const demo = buildDemoAssessments(qd, qdoc, numQuestions, limitedTeachers)
            const questionStats = {}
            demo.teacherAssessments.forEach(ta => {
                Object.values(ta.studentGrades).forEach(sg => {
                    if (!sg.graded || !sg.chosenAnswersBaseQuiz) return
                    Object.entries(sg.chosenAnswersBaseQuiz).forEach(([qKey, isCorrect]) => {
                        if (!questionStats[qKey]) questionStats[qKey] = { total: 0, correct: 0 }
                        questionStats[qKey].total += 1
                        if (isCorrect) questionStats[qKey].correct += 1
                        if (!overallQuestionStats[qKey]) overallQuestionStats[qKey] = { total: 0, correct: 0 }
                        overallQuestionStats[qKey].total += 1
                        if (isCorrect) overallQuestionStats[qKey].correct += 1
                    })
                })
            })
            allDemoQuizData[String(qi)] = { questionStats, demo }
        }
        this.setState({ allDemoQuizData, demoOverallQuestionStats: overallQuestionStats })
    }
    loadDemoTeacherData = async () => {
        const { modalTab, allDemoQuizData } = this.state
        if (!modalTab || modalTab === 'overall') { this.setState({ loadingTeachers: false }); return }
        if (allDemoQuizData && allDemoQuizData[modalTab]) {
            const cached = allDemoQuizData[modalTab]
            this.setState({
                teacherAssessments: cached.demo.teacherAssessments,
                teacherAssessmentDict: cached.demo.teacherAssessmentDict,
                teacherAssessmentCountDict: cached.demo.teacherAssessmentCountDict,
                teacherAssessmentRetakes: cached.demo.teacherAssessmentRetakes,
                teachers: cached.demo.teachers,
                teachersDict: cached.demo.teachersDict,
                individualTeacherRetakeDict: cached.demo.individualTeacherRetakeDict,
                students: cached.demo.students,
                studentsDict: cached.demo.studentsDict,
                demoQuestionStats: cached.questionStats,
                loadingTeachers: false, loadingStudents: false,
            })
            return
        }
        const { modalStandardDetail, modalQuizDocs } = this.state
        if (!modalStandardDetail || modalTab === 'overall') { this.setState({ loadingTeachers: false }); return }
        const qd = modalStandardDetail.quizzes && modalStandardDetail.quizzes[modalTab]
        if (!qd || !qd.total_graded) { this.setState({ teacherAssessments: [], teachers: [], loadingTeachers: false }); return }
        const qid = qd.quizId
        const qdoc = qid ? modalQuizDocs[qid] : null
        const numQuestions = (qdoc && qdoc.numQuestions) ? qdoc.numQuestions : (qd.questions ? Object.keys(qd.questions).length : 3)
        const schoolTeachers = this.getDemoSchoolTeachers()
        const maxTeachers = Math.max(0, qd.given || 0)
        const limitedTeachers = schoolTeachers
            ? schoolTeachers.slice().sort((a, b) => {
                const ah = hashString(`${qid}-${a.id}`)
                const bh = hashString(`${qid}-${b.id}`)
                return ah - bh
            }).slice(0, maxTeachers)
            : []
        if (!limitedTeachers || limitedTeachers.length === 0) { this.setState({ teacherAssessments: [], teachers: [], loadingTeachers: false }); return }
        try {
            const studentPromises = limitedTeachers.map(teacher => this.fetchDemoStudentsForTeacher(teacher))
            const results = await Promise.all(studentPromises)
            const teachersWithStudents = results.filter(r => r.students.length > 0)
            if (teachersWithStudents.length === 0) { this.setState({ teacherAssessments: [], teachers: [], loadingTeachers: false }); return }
            const demo = buildDemoAssessments(qd, qdoc, numQuestions, teachersWithStudents)
            const demoQuestionStats = {}
            demo.teacherAssessments.forEach(ta => {
                Object.values(ta.studentGrades).forEach(sg => {
                    if (!sg.graded || !sg.chosenAnswersBaseQuiz) return
                    Object.entries(sg.chosenAnswersBaseQuiz).forEach(([qKey, isCorrect]) => {
                        if (!demoQuestionStats[qKey]) demoQuestionStats[qKey] = { total: 0, correct: 0 }
                        demoQuestionStats[qKey].total += 1
                        if (isCorrect) demoQuestionStats[qKey].correct += 1
                    })
                })
            })
            this.setState({
                teacherAssessments: demo.teacherAssessments,
                teacherAssessmentDict: demo.teacherAssessmentDict,
                teacherAssessmentCountDict: demo.teacherAssessmentCountDict,
                teacherAssessmentRetakes: demo.teacherAssessmentRetakes,
                teachers: demo.teachers,
                teachersDict: demo.teachersDict,
                individualTeacherRetakeDict: demo.individualTeacherRetakeDict,
                students: demo.students,
                studentsDict: demo.studentsDict,
                demoQuestionStats: demoQuestionStats,
                loadingTeachers: false, loadingStudents: false,
            })
        } catch (e) {
            console.log('Error loading demo teacher data', e)
            this.setState({ teacherAssessments: [], teachers: [], loadingTeachers: false })
        }
    }
    loadTeacherData = async () => {
        if (this.isDemo()) { this.loadDemoTeacherData(); return }
        try {
            let districtId = this.getDistrictId(), schoolId = this.getActiveSchoolId(), quizId = this.getSelectedQuizId()
            if (!(districtId && schoolId && quizId)) { this.setState({ teacherAssessments: [], teachers: [], loadingTeachers: false }); return }
            let teachers = [], teachersDict = {}
            const addTeachers = qs => qs.forEach(doc => { const t = flattenDoc(doc); if (!teachersDict[t.id]) { teachers.push(t); teachersDict[t.id] = t } })
            try {
                const qsByArr = await db.collection(ColType.teacher).where('districtId', '==', districtId).where('schoolIds', 'array-contains', schoolId).get()
                addTeachers(qsByArr)
            } catch (e) { console.log('teacher schoolIds query error (falling back to schoolId ==)', e) }
            if (teachers.length === 0) {
                try {
                    const qsById = await db.collection(ColType.teacher).where('districtId', '==', districtId).where('schoolId', '==', schoolId).get()
                    addTeachers(qsById)
                } catch (e) { console.log('teacher schoolId query error', e) }
            }
            if (teachers.length === 0) { this.setState({ teacherAssessments: [], teachers: [], teachersDict, loadingTeachers: false }); return }
            let teacherAssessments = [], promises = []
            teachers.forEach(teacher => { promises.push(db.collection(ColType.cleverAssessments).where('districtId', '==', districtId).where('schoolId', '==', schoolId).where('teacherId', '==', teacher.id).where('quiz.uuid', '==', quizId).where('status', '==', 'current').get().then(qs => { qs.forEach(doc => teacherAssessments.push(flattenDoc(doc))); teacherAssessments.sort((a, b) => { if (!a || !b || !a.timeStamp || !b.timeStamp) return 0; return a.timeStamp.seconds - b.timeStamp.seconds }) }).catch(e => console.log('error', e))) })
            Promise.all(promises).then(() => {
                if (this.getSelectedQuizId() !== quizId) return
                const win = this.yearWindowSeconds()
                const filtered = win ? teacherAssessments.filter(ta => ta && ta.timeStamp && typeof ta.timeStamp.seconds === 'number' && ta.timeStamp.seconds >= win.start && ta.timeStamp.seconds < win.end) : teacherAssessments
                this.handleTeacherAssessmentQuerySnapshot(filtered)
            }).catch(e => console.log('error', e))
        } catch (e) { console.log('error', e); this.setState({ loadingTeachers: false }) }
    }
    viewTeacherAssessment = (teacherId) => {
        if (this.isDemo()) { this.setState({ activeTabKey: 'student-tab', viewingTeacherId: teacherId, loadingStudents: false }); return }
        this.setState({ activeTabKey: 'student-tab', viewingTeacherId: teacherId, loadingStudents: true }, () => {
            const districtId = this.getDistrictId()
            const teacher = (this.state.teachersDict && this.state.teachersDict[teacherId]) || {}
            // Prefer schoolIds when it's a non-empty array, else the single schoolId,
            // else fall back to the school the modal is scoped to.
            let schoolIds = []
            if (teacher.schoolIds && teacher.schoolIds.length > 0) schoolIds = teacher.schoolIds.slice()
            else if (teacher.schoolId) schoolIds = [teacher.schoolId]
            const active = this.getActiveSchoolId()
            if (schoolIds.length === 0 && active) schoolIds = [active]
            let students = this.state.students ? this.state.students.slice() : []
            let studentsDict = { ...this.state.studentsDict }
            const promises = []
            schoolIds.forEach(schoolId => {
                promises.push(db.collection(ColType.student)
                    .where('teacherIds', 'array-contains', teacherId)
                    .where('schoolId', '==', schoolId)
                    .where('districtId', '==', districtId)
                    .get()
                    .then(qs => { qs.forEach(doc => { const s = flattenDoc(doc); if (!studentsDict[s.id]) { students.push(s); studentsDict[s.id] = s } }) })
                    .catch(e => console.log('student query error', e)))
            })
            Promise.all(promises).then(() => this.setState({ students, studentsDict, loadingStudents: false })).catch(() => this.setState({ loadingStudents: false }))
        })
    }
    initDefaults(quizStats, districtState, preferred) {
        const sds = quizStats.filter(d => d.type === 'school')
        // Keep whatever school is already selected (from any view). Only fall back
        // to a default when nothing valid is selected yet.
        const current = this.state.selectedSchoolId
        const sid = this.isKnownSchool(current) ? current
            : (preferred && this.isKnownSchool(preferred.sid)) ? preferred.sid
                : (this.defaultSchoolId() || (sds[0] ? sds[0].schoolId : null))
        // The selected school may have no quiz doc (e.g. IEP or student data only);
        // in that case subject/grade stay null and the quiz views show their empty state.
        const chosen = sds.find(d => d.schoolId === sid) || null
        let cat = null, name = null
        if (chosen && chosen.quizzes && chosen.quizzes.by_subject) {
            const cats = Object.keys(chosen.quizzes.by_subject).sort()
            if (preferred && preferred.cat && cats.includes(preferred.cat)) cat = preferred.cat
            else cat = cats.includes('ELA') ? 'ELA' : (cats[0] || null)
            if (cat && chosen.quizzes.by_subject[cat] && chosen.quizzes.by_subject[cat].by_standard_name) {
                const names = Object.keys(chosen.quizzes.by_subject[cat].by_standard_name).sort()
                if (preferred && preferred.name && names.includes(preferred.name)) name = preferred.name
                else name = names[0] || null
            }
        }
        const willLoadPacing = !!(sid && cat && name && this.state.view === 'pacing')
        this.setState({
            quizStats, districtState,
            selectedSchoolId: sid,
            standardsCategory: cat, standardsName: name,
            pacingCategory: cat, pacingName: name,
            pgLoading: willLoadPacing, pgStatsLoading: willLoadPacing,
        }, () => {
            if (this.state.view === 'pacing') { this.loadPacingStats(sid); if (cat && name) this.loadPacingGuide(cat, name, sid) }
            else if (this.state.view === 'standards') { if (cat && name) this.loadStandardDetail(cat, name, sid) }
            else if (this.state.view === 'quiz_results') { if (cat && name) this.loadQuizResultsFor(cat, name, sid) }
        })
    }
    // Every school in the district (from baseProps), independent of whether stats exist.
    allSchools() {
        if (this.props.adminBaseProps && this.props.adminBaseProps.schools) return this.props.adminBaseProps.schools
        if (this.props.instructionalFacilitatorBaseProps && this.props.instructionalFacilitatorBaseProps.schools) return this.props.instructionalFacilitatorBaseProps.schools
        return []
    }
    allSchoolsDict() {
        const d = {}
        this.allSchools().forEach(s => { d[s.id] = s })
        return d
    }
    // Every school in the district (elementary -> middle -> high, then alphabetical),
    // regardless of whether stats exist — used to fill the school dropdowns.
    allSchoolsSorted() {
        return [...this.allSchools()].sort((a, b) => {
            const na = this.getSchoolName(a.id), nb = this.getSchoolName(b.id)
            const ra = schoolTypeRank(na), rb = schoolTypeRank(nb)
            return ra !== rb ? ra - rb : na.localeCompare(nb)
        })
    }
    districtDoc() { return this.state.quizStats.find(d => d.type === 'district') || null }
    schoolDocs() {
        return this.state.quizStats.filter(d => d.type === 'school').sort((a, b) => {
            const na = (a.quizzes && a.quizzes.name) || a.schoolId || '', nb = (b.quizzes && b.quizzes.name) || b.schoolId || ''
            const ra = schoolTypeRank(na), rb = schoolTypeRank(nb)
            return ra !== rb ? ra - rb : na.localeCompare(nb)
        })
    }
    subjects() { const dd = this.districtDoc(); if (!dd || !dd.quizzes || !dd.quizzes.by_subject) return []; return Object.keys(dd.quizzes.by_subject).sort() }
    getDistrictId() { if (this.props.adminBaseProps) return this.props.adminBaseProps.district.id; if (this.props.instructionalFacilitatorBaseProps && this.props.instructionalFacilitatorBaseProps.district) return this.props.instructionalFacilitatorBaseProps.district.id; return null }
    getDistrictState() { if (this.props.adminBaseProps) return this.props.adminBaseProps.district.state || ''; if (this.props.instructionalFacilitatorBaseProps && this.props.instructionalFacilitatorBaseProps.district) return this.props.instructionalFacilitatorBaseProps.district.state || ''; return '' }
    getStandardNames(sid, cat) { const sd = this.schoolDocs().find(d => d.schoolId === sid); if (!sd || !sd.quizzes || !sd.quizzes.by_subject || !cat) return []; const subj = sd.quizzes.by_subject[cat]; if (!subj || !subj.by_standard_name) return []; return Object.keys(subj.by_standard_name).sort() }
    getCategories(sid) { const sd = this.schoolDocs().find(d => d.schoolId === sid); if (!sd || !sd.quizzes || !sd.quizzes.by_subject) return []; return Object.keys(sd.quizzes.by_subject).sort() }
    // Per-school "quizzes given" + average grade, totalled overall and by subject.
    schoolSubjectSummary(sd) {
        const out = {}
        let allGiven = 0, allGradeSum = 0, allTotalGraded = 0
        if (sd.quizzes && sd.quizzes.by_subject) {
            Object.entries(sd.quizzes.by_subject).forEach(([subj, sD]) => {
                let given = 0, gradeSum = 0, totalGraded = 0
                if (sD.by_standard_name) {
                    Object.values(sD.by_standard_name).forEach(d => {
                        const t = d.totals
                        if (!t) return
                        given += givenCount(t)
                        gradeSum += t.grade_sum || 0
                        totalGraded += t.total_graded || 0
                    })
                }
                out[subj] = { given, avg: totalGraded > 0 ? gradeSum / totalGraded : null, gradeSum, totalGraded }
                allGiven += given; allGradeSum += gradeSum; allTotalGraded += totalGraded
            })
        }
        out.All = { given: allGiven, avg: allTotalGraded > 0 ? allGradeSum / allTotalGraded : null, gradeSum: allGradeSum, totalGraded: allTotalGraded }
        return out
    }
    // Per-school resources-completed totals (overall + by subject + by grade within subject),
    // read straight from the school doc's parsed `resources` blob.
    schoolResourceSummary(sd) {
        const r = sd && sd.resources
        const bySubject = {}
        let total = 0
        if (r && r.by_subject) {
            Object.entries(r.by_subject).forEach(([subj, sD]) => {
                const count = (sD.totals && sD.totals.count) || 0
                const grades = {}
                if (sD.by_standard_name) {
                    Object.entries(sD.by_standard_name).forEach(([gName, gD]) => {
                        grades[gName] = (gD.totals && gD.totals.count) || 0
                    })
                }
                bySubject[subj] = { count, grades }
                total += count
            })
        }
        if (total === 0 && r && r.totals && r.totals.count) total = r.totals.count
        return { total, bySubject }
    }
    // Aggregated by_week for a whole school (prefers the highest available rollup
    // level to avoid double-counting), used by the schools-view weekly charts.
    schoolWeekData(sd) {
        if (!sd || !sd.quizzes) return {}
        if (sd.quizzes.by_week && Object.keys(sd.quizzes.by_week).length) return sd.quizzes.by_week
        const out = {}
        if (sd.quizzes.by_subject) {
            Object.values(sd.quizzes.by_subject).forEach(sD => {
                if (!sD) return
                if (sD.by_week && Object.keys(sD.by_week).length) { mergeByWeek(out, sD.by_week); return }
                if (sD.by_standard_name) Object.values(sD.by_standard_name).forEach(d => { if (d && d.by_week) mergeByWeek(out, d.by_week) })
            })
        }
        return out
    }
    // by_week per subject for a whole school -> { ELA: {...}, Math: {...}, ... } (feeds the chart pills).
    schoolWeekBySubject(sd) {
        const out = {}
        if (sd && sd.quizzes && sd.quizzes.by_subject) {
            Object.entries(sd.quizzes.by_subject).forEach(([cat, sD]) => {
                if (!sD) return
                const o = {}
                if (sD.by_week && Object.keys(sD.by_week).length) mergeByWeek(o, sD.by_week)
                else if (sD.by_standard_name) Object.values(sD.by_standard_name).forEach(d => { if (d && d.by_week) mergeByWeek(o, d.by_week) })
                if (Object.keys(o).length) out[cat] = o
            })
        }
        return out
    }
    // by_week per subject for a single grade level (across all subjects that grade appears in).
    gradeWeekBySubject(sid, gradeName) {
        const sd = this.schoolDocs().find(d => d.schoolId === sid)
        const out = {}
        if (sd && sd.quizzes && sd.quizzes.by_subject && gradeName) {
            Object.entries(sd.quizzes.by_subject).forEach(([cat, sD]) => {
                const g = sD && sD.by_standard_name && sD.by_standard_name[gradeName]
                if (g && g.by_week && Object.keys(g.by_week).length) { out[cat] = {}; mergeByWeek(out[cat], g.by_week) }
            })
        }
        return out
    }
    // by_week per subject for a single teacher at a school.
    teacherWeekBySubject(sid, teacherId) {
        const out = {}
        const tDocs = this.state.quizStats.filter(d => d.type === 'teacher' && d.schoolId === sid)
        tDocs.forEach(td => {
            const tD = td.quizzes && td.quizzes[teacherId]
            if (!tD || !tD.by_subject) return
            Object.entries(tD.by_subject).forEach(([cat, sD]) => {
                if (sD && sD.by_week && Object.keys(sD.by_week).length) { if (!out[cat]) out[cat] = {}; mergeByWeek(out[cat], sD.by_week) }
            })
        })
        if (Object.keys(out).length === 0) {
            // Fall back to the per-teacher overall weekly rollup (no subject split available).
            const sd = this.schoolDocs().find(d => d.schoolId === sid)
            const ts = sd && sd.quizzes && sd.quizzes.teachers_summary && sd.quizzes.teachers_summary[teacherId]
            if (ts && ts.by_week && Object.keys(ts.by_week).length) out.ELA = { ...ts.by_week }
        }
        return out
    }
    // ---- Pacing guide: per (school, subject, grade, week) quiz-given count + teacher breakdown ----
    // FALLBACK path (grade-level rollup). Used only when the per-school pacingGuideStats doc
    // doesn't have a matching per-standard entry yet (e.g. stats not regenerated). Quizzes given
    // that week for the grade come from the school rollup; the teacher list comes from the
    // per-teacher docs for that school (scoped to the grade level when it's available).
    pacingWeekInfo(sid, cat, name, weekNumber) {
        const wk = String(weekNumber)
        const sd = this.schoolDocs().find(d => d.schoolId === sid)
        let given = 0, avg = null
        const gradeNode = sd && sd.quizzes && sd.quizzes.by_subject && sd.quizzes.by_subject[cat] && sd.quizzes.by_subject[cat].by_standard_name && sd.quizzes.by_subject[cat].by_standard_name[name]
        if (gradeNode && gradeNode.by_week && gradeNode.by_week[wk]) {
            given = givenCount(gradeNode.by_week[wk])
            avg = getAvg(gradeNode.by_week[wk])
        }
        const matched = [], anySubject = []
        const tDocs = this.state.quizStats.filter(d => d.type === 'teacher' && d.schoolId === sid)
        tDocs.forEach(td => {
            const ch = td.quizzes
            if (!ch) return
            Object.entries(ch).forEach(([tid, tD]) => {
                const sD = tD.by_subject && tD.by_subject[cat]
                if (!sD || !sD.by_week || !sD.by_week[wk]) return
                const g = givenCount(sD.by_week[wk])
                if (g <= 0) return
                const row = { name: ((tD.firstName || '') + ' ' + (tD.lastName || '')).trim() || tid, given: g, avg: getAvg(sD.by_week[wk]), grade: tD.gradeLevel || '' }
                anySubject.push(row)
                if (tD.gradeLevel && tD.gradeLevel === name) matched.push(row)
            })
        })
        let teachers = matched.length ? matched : anySubject
        teachers.sort((a, b) => (b.given - a.given) || a.name.localeCompare(b.name))
        if (given <= 0 && teachers.length) given = teachers.reduce((s, t) => s + t.given, 0)
        return { given, avg, teachers }
    }
    // ---- Pacing guide: per-STANDARD given / avg / teachers, straight from the per-school
    // pacingGuideStats doc. guides[cat][name][week] is an array of
    // { standard, action, given, avg, teachers: [{ name, given }] }. ----
    pacingStandardInfo(cat, name, weekNumber, standardKey) {
        const guides = this.state.pgStats
        if (!guides || !cat || !name || !standardKey) return null
        const byName = guides[cat] && guides[cat][name]
        if (!byName) return null
        const weekArr = byName[String(weekNumber)]
        if (!Array.isArray(weekArr)) return null
        const entry = weekArr.find(e => e && e.standard === standardKey)
        if (!entry) return null
        const teachers = Array.isArray(entry.teachers) ? entry.teachers.slice() : []
        teachers.sort((a, b) => ((b.given || 0) - (a.given || 0)) || (a.name || '').localeCompare(b.name || ''))
        return { given: entry.given || 0, avg: (entry.avg == null ? null : entry.avg), teachers }
    }
    // Per-standard stats when available, otherwise the grade-level weekly fallback.
    pacingItemInfo(item, weekNumber) {
        const { pacingCategory: cat, pacingName: name, selectedSchoolId: sid } = this.state
        const perStandard = this.pacingStandardInfo(cat, name, weekNumber, item.standard)
        return { info: perStandard || this.pacingWeekInfo(sid, cat, name, weekNumber), usingPerStandard: !!perStandard }
    }
    sortedGameSchools() {
        return [...this.allSchools()].sort((a, b) => {
            const na = this.getSchoolName(a.id), nb = this.getSchoolName(b.id)
            const ra = schoolTypeRank(na), rb = schoolTypeRank(nb)
            return ra !== rb ? ra - rb : na.localeCompare(nb)
        })
    }
    // Load the per-school pacingGuideStats doc (covers every subject/grade for the school).
    // Only needs to be (re)fetched when the school changes.
    loadPacingStats = async (sid) => {
        const districtId = this.getDistrictId()
        if (!districtId || !sid) { this.setState({ pgStats: null, pgStatsSchoolId: null, pgStatsLoading: false }); return }
        this.setState({ pgStatsLoading: true, pgStats: null, pgStatsSchoolId: sid })
        try {
            const col = (ColType && ColType.pacingGuideStats) || 'pacingGuideStats'
            const qs = await this.withYear(db.collection(col)
                .where('districtId', '==', districtId)
                .where('schoolId', '==', sid))
                .get()
            let guides = null
            qs.forEach(doc => {
                if (guides) return
                const d = flattenDoc(doc)
                if (d && d.type === 'school' && d.guides) guides = d.guides
            })
            this.setState({ pgStats: guides, pgStatsSchoolId: sid, pgStatsLoading: false })
        } catch (e) {
            console.log('pacing stats load error', e)
            this.setState({ pgStats: null, pgStatsLoading: false })
        }
    }
    loadPacingGuide = async (cat, name, sid) => {
        this.setState({ pgLoading: true, pgGuide: null, pgErr: null })
        const districtId = this.getDistrictId()
        if (!districtId || !cat || !name) { this.setState({ pgLoading: false }); return }
        let standardYears = this.state.standardYears
        if (!standardYears) {
            try {
                const ydoc = await db.collection(ColType.standardYears).doc('all').get()
                if (ydoc.exists) { standardYears = flattenDoc(ydoc); this.setState({ standardYears, activeYear: standardYears.currentYear }) }
            } catch (e) { /* non-fatal */ }
        }
        // Curriculum pacing guide is keyed by calendar year (e.g. '2026') — the
        // first 4 chars of the selected yearStart. Falls back to currentYear.
        const selYear = this.state.selectedYear ? String(this.state.selectedYear).slice(0, 4) : null
        const activeYear = selYear || (standardYears ? standardYears.currentYear : null)
        try {
            let q = db.collection(ColType.pacingGuides)
                .where('standardName', '==', name)
                .where('standardCategory', '==', cat)
                .where('districtId', '==', districtId)
            if (activeYear != null) q = q.where('year', '==', activeYear)
            const qs = await q.get()
            let guide = null
            qs.forEach(doc => { if (!guide) guide = flattenDoc(doc) })
            this.setState({ pgGuide: guide, pgYear: activeYear, pgLoading: false })
        } catch (e) {
            console.log('pacing guide load error', e)
            this.setState({ pgErr: 'Could not load the pacing guide for this selection.', pgLoading: false })
        }
    }
    onPacingSchoolChange = sid => {
        const cats = this.getCategories(sid)
        const cat = cats.includes(this.state.pacingCategory) ? this.state.pacingCategory : (cats.includes('ELA') ? 'ELA' : (cats[0] || null))
        const names = cat ? this.getStandardNames(sid, cat) : []
        const name = names.includes(this.state.pacingName) ? this.state.pacingName : (names[0] || null)
        this.selectSchool(sid, { pacingCategory: cat, pacingName: name }, () => {
            this.loadPacingStats(sid)
            if (cat && name) this.loadPacingGuide(cat, name, sid)
            else this.setState({ pgLoading: false })
        })
    }
    onPacingCategoryChange = cat => {
        const names = this.getStandardNames(this.state.selectedSchoolId, cat)
        const name = names[0] || null
        this.setState({ pacingCategory: cat, pacingName: name }, () => { if (cat && name) this.loadPacingGuide(cat, name, this.state.selectedSchoolId) })
    }
    onPacingNameChange = name => {
        this.setState({ pacingName: name }, () => { if (this.state.pacingCategory && name) this.loadPacingGuide(this.state.pacingCategory, name, this.state.selectedSchoolId) })
    }
    resolvePacingSelection(sid) {
        const cats = this.getCategories(sid)
        const cat = cats.includes(this.state.pacingCategory) ? this.state.pacingCategory : (cats.includes('ELA') ? 'ELA' : (cats[0] || null))
        const names = cat ? this.getStandardNames(sid, cat) : []
        const name = names.includes(this.state.pacingName) ? this.state.pacingName : (names[0] || null)
        return { cat, name }
    }
    // ---- Standards view data loading (subject + grade level selectors) ----
    resolveStandardsSelection(sid) {
        const cats = this.getCategories(sid)
        const cat = cats.includes(this.state.standardsCategory) ? this.state.standardsCategory : (cats.includes('ELA') ? 'ELA' : (cats[0] || null))
        const names = cat ? this.getStandardNames(sid, cat) : []
        const name = names.includes(this.state.standardsName) ? this.state.standardsName : (names[0] || null)
        return { cat, name }
    }
    loadStandardDetail = async (cat, name, schoolId) => {
        this.setState({ standardDetailData: null, standardDetailLoading: true, standardsList: null, standardsLoading: true, sortCol: 'label', sortDir: 'asc' })
        const did = this.getDistrictId()
        let q = db.collection(ColType.quizStats).where('type', '==', 'standard-detail').where('standardCategory', '==', cat)
        if (did) q = q.where('districtId', '==', did)
        if (schoolId) q = q.where('schoolId', '==', schoolId)
        else q = q.where('schoolId', '==', '')
        q = this.withYear(q)
        q.get().then(qs => {
            let data = null
            qs.forEach(doc => { const d = doc.data(); if (d.data) { const p = JSON.parse(d.data); if (p[name]) data = p[name] } })
            this.setState({ standardDetailData: data, standardDetailLoading: false })
        }).catch(() => this.setState({ standardDetailLoading: false }))
        const state = this.getDistrictState()
        if (!state) { this.setState({ standardsLoading: false }); return }
        let standardYears = this.state.standardYears
        if (!standardYears) {
            let doc = await db.collection(ColType.standardYears).doc('all').get()
            if (!doc.exists) { this.setState({ standardsLoading: false }); return }
            standardYears = flattenDoc(doc)
            this.setState({ standardYears, activeYear: standardYears.currentYear })
        }
        const key = state + cat
        if (!standardYears.hasOwnProperty(key)) {
            console.error('standardYears does not have key', key)
            this.setState({ standardsLoading: false })
            return
        }
        const standardYear = standardYears[key]
        db.collection(ColType.standards)
            .where('standardCategory', '==', cat)
            .where('standardName', '==', name)
            .where('state', '==', state)
            .where('year', '==', standardYear)
            .orderBy('sortOrder')
            .get()
            .then(qs => { let stds = []; qs.forEach(doc => stds.push({ id: doc.id, ...doc.data() })); stds.sort((a, b) => (a.standard || '').localeCompare(b.standard || '')); this.setState({ standardsList: stds, standardsLoading: false }) })
            .catch(() => this.setState({ standardsLoading: false }))
    }
    goToStandards = (cat, name, schoolId) => {
        const sid = schoolId || this.state.selectedSchoolId
        this.setState({ view: 'standards', selectedSchoolId: sid, standardsCategory: cat, standardsName: name, expandedRows: {} }, () => { window.scrollTo({ top: 0 }); if (cat && name) this.loadStandardDetail(cat, name, sid) })
    }
    onStandardsSchoolChange = sid => {
        const cats = this.getCategories(sid)
        const cat = cats.includes(this.state.standardsCategory) ? this.state.standardsCategory : (cats[0] || null)
        const names = cat ? this.getStandardNames(sid, cat) : []
        const name = names.includes(this.state.standardsName) ? this.state.standardsName : (names[0] || null)
        this.selectSchool(sid, { standardsCategory: cat, standardsName: name }, () => { if (cat && name) this.reloadStandardsData(cat, name, sid) })
    }
    onStandardsCategoryChange = cat => {
        const names = this.getStandardNames(this.state.selectedSchoolId, cat)
        const name = names[0] || null
        this.setState({ standardsCategory: cat, standardsName: name, expandedRows: {} }, () => { if (cat && name) this.reloadStandardsData(cat, name, this.state.selectedSchoolId) })
    }
    onStandardsNameChange = name => {
        this.setState({ standardsName: name, expandedRows: {} }, () => { if (this.state.standardsCategory && name) this.reloadStandardsData(this.state.standardsCategory, name, this.state.selectedSchoolId) })
    }
    goView = v => {
        this.setState({ view: v, sortCol: 'label', sortDir: 'asc', expandedRows: {}, iepExpand: {}, studentLimit: 100, ...(v === 'pacing' ? { pgLoading: true, pgStatsLoading: true } : {}) }, () => {
            window.scrollTo({ top: 0 })
            if (v === 'iep_school' || v === 'iep_teacher') {
                if (!this.state.selectedSchoolId) this.setState({ selectedSchoolId: this.defaultSchoolId() })
            }
            if (v === 'standards') {
                const sid = this.state.selectedSchoolId
                const { cat, name } = this.resolveStandardsSelection(sid)
                this.setState({ standardsCategory: cat, standardsName: name }, () => { if (cat && name) this.loadStandardDetail(cat, name, sid) })
            }
            if (v === 'pacing') {
                const sid = this.state.selectedSchoolId
                const { cat, name } = this.resolvePacingSelection(sid)
                this.setState({ pacingCategory: cat, pacingName: name }, () => {
                    this.loadPacingStats(sid)
                    if (cat && name) this.loadPacingGuide(cat, name, sid)
                    else this.setState({ pgLoading: false })   // nothing to load → stop spinning
                })
            }
            if (v === 'games_school' || v === 'games_teacher' || v === 'games_standard') this.loadGameStats()
            if (v === 'students') {
                const schools = this.allSchoolsSorted()
                const sid = this.state.selectedSchoolId || (schools.length ? schools[0].id : null)
                this.setState({ selectedSchoolId: sid }, () => this.loadStudentDocs(sid))
            }
            if (v === 'quiz_results') {
                const sid = this.state.selectedSchoolId
                const { cat, name } = this.resolveStandardsSelection(sid)
                this.setState({ standardsCategory: cat, standardsName: name }, () => { if (cat && name) this.loadQuizResultsFor(cat, name, sid) })
            }
        })
    }
    onSort = col => { const { sortCol, sortDir } = this.state; if (sortCol === col) this.setState({ sortDir: sortDir === 'asc' ? 'desc' : 'asc' }); else this.setState({ sortCol: col, sortDir: ['label'].includes(col) ? 'asc' : 'desc' }) }
    sorted(rows) { const { sortCol, sortDir } = this.state; if (!sortCol) return [...rows].sort((a, b) => (a.label || '').localeCompare(b.label || '')); return [...rows].sort((a, b) => { let va = a[sortCol], vb = b[sortCol]; if (va == null) va = sortDir === 'asc' ? Infinity : -Infinity; if (vb == null) vb = sortDir === 'asc' ? Infinity : -Infinity; if (typeof va === 'string') return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va); return sortDir === 'asc' ? va - vb : vb - va }) }
    openResultsModal = (sk, det, cat, name) => {
        if (!det) return
        let normalizedDetail = det
        if (this.isDemo() && det.quizzes) {
            normalizedDetail = { ...det, quizzes: { ...det.quizzes } }
            Object.keys(normalizedDetail.quizzes).forEach(qk => {
                const qd = normalizedDetail.quizzes[qk]
                if (!qd.questions || Object.keys(qd.questions).length === 0) {
                    normalizedDetail.quizzes[qk] = { ...qd, questions: det.questions || null }
                }
            })
        }
        this.setState({ modalVisible: true, modalStandard: sk, modalStandardDetail: normalizedDetail, modalQuizLoading: true, modalQuizDocs: {}, modalTab: 'overall' })
        const ids = [...new Set(Object.values(normalizedDetail.quizzes || {}).map(q => q.quizId).filter(Boolean))]
        const done = l => this.setState({ modalQuizDocs: l, modalQuizLoading: false }, () => { if (this.isDemo()) this.loadAllDemoQuizData() })
        if (!ids.length) { done({}); return }
        const loaded = {}
        let rem = ids.length
        ids.forEach(qid => {
            db.collection(ColType.quizzes).doc(qid).get().then(doc => {
                if (doc.exists) {
                    const q = { id: doc.id, ...doc.data() }
                    if (isV3or4(q.version) && q.docs) q.docs = injectBaseHref(q.docs)
                    loaded[qid] = q
                }
                rem--; if (rem <= 0) done(loaded)
            }).catch(() => { rem--; if (rem <= 0) done(loaded) })
        })
    }
    closeResultsModal = () => {
        this.setState({ modalVisible: false })
        setTimeout(() => {
            if (this.state.view === 'quiz_results') return
            this.setState({ modalStandard: null, modalStandardDetail: null, modalQuizDocs: {}, modalQuizLoading: false, modalTab: 'overall' })
        }, 350)
    }
    // ---- Quiz Detailed Results view ----
    // Standards for the current school/subject/grade, same order as the standards table (given first).
    quizResultsRows() {
        const stds = this.state.standardsList || [], data = this.state.standardDetailData || {}
        const rows = stds.map(std => {
            const key = std.standard || std.id, det = data[key]
            return {
                standard: key,
                description: std.title || std.description || std.standardDescription || '',
                detail: det, given: isQuizGiven(det), hasData: hasResults(det),
            }
        })
        rows.sort((a, b) => (a.given === b.given ? a.standard.localeCompare(b.standard) : (a.given ? -1 : 1)))
        return rows
    }
    // Next/previous standard that has results, starting from the selected one.
    adjacentWithData(rows, dir) {
        const i = rows.findIndex(r => r.standard === this.state.qrStandardKey)
        const start = i === -1 ? (dir > 0 ? 0 : -1) : i + dir
        for (let j = start; j >= 0 && j < rows.length; j += dir) if (rows[j].hasData) return rows[j]
        return null
    }
    // Load standards + standard-detail docs, then componentDidUpdate picks the first standard.
    loadQuizResultsFor = (cat, name, sid) => {
        this.qrToken = (this.qrToken || 0) + 1
        this.setState({ qrPending: true, qrStandardKey: null, standardDetailLoading: true, standardsLoading: true },
            () => this.loadStandardDetail(cat, name, sid))
    }
    // Same demo normalization openResultsModal does.
    normalizeStandardDetail(det) {
        if (!this.isDemo() || !det || !det.quizzes) return det
        const out = { ...det, quizzes: { ...det.quizzes } }
        Object.keys(out.quizzes).forEach(qk => {
            const qd = out.quizzes[qk]
            if (!qd.questions || Object.keys(qd.questions).length === 0) out.quizzes[qk] = { ...qd, questions: det.questions || null }
        })
        return out
    }
    fetchQuizDocs(detail) {
        const ids = [...new Set(Object.values((detail && detail.quizzes) || {}).map(q => q && q.quizId).filter(Boolean))]
        return Promise.all(ids.map(qid => db.collection(ColType.quizzes).doc(qid).get().then(doc => {
            if (!doc.exists) return null
            const q = { id: doc.id, ...doc.data() }
            if (isV3or4(q.version) && q.docs) q.docs = injectBaseHref(q.docs)
            return q
        }).catch(() => null))).then(list => {
            const out = {}
            list.forEach(q => { if (q) out[q.id] = q })
            return out
        })
    }
    // Select a standard: reset the per-quiz state, open the first quiz with data, load its breakdown.
    selectQuizResultsStandard = (key) => {
        const row = this.quizResultsRows().find(r => r.standard === key)
        const det = row ? row.detail : null
        const token = this.qrToken = (this.qrToken || 0) + 1
        const reset = {
            qrStandardKey: key, modalStandard: key, modalQuizDocs: {},
            quizModalStatsTab: 'teachers', viewingTeacherId: null,
            teacherAssessments: [], teacherAssessmentDict: {}, teacherAssessmentCountDict: {}, teacherAssessmentRetakes: {},
            teachers: [], teachersDict: {}, individualTeacherRetakeDict: {}, students: [], studentsDict: {},
            allDemoQuizData: null, demoOverallQuestionStats: null, demoQuestionStats: null,
        }
        if (!det) {
            this.setState({ ...reset, modalStandardDetail: null, modalTab: null, modalQuizLoading: false, loadingTeachers: false })
            return
        }
        const detail = this.normalizeStandardDetail(det)
        const quizzes = detail.quizzes || {}
        const idx = Object.keys(quizzes).map(Number).filter(n => !isNaN(n)).sort((a, b) => a - b)
        const firstWithData = idx.find(i => hasResults(quizzes[String(i)]))
        const tab = firstWithData != null ? String(firstWithData) : (idx.length ? String(idx[0]) : null)
        this.setState({ ...reset, modalStandardDetail: detail, modalTab: tab, modalQuizLoading: true, loadingTeachers: !!tab }, async () => {
            const docs = await this.fetchQuizDocs(detail)
            if (token !== this.qrToken) return
            this.setState({ modalQuizDocs: docs, modalQuizLoading: false }, async () => {
                if (!tab) return
                if (this.isDemo()) { await this.loadAllDemoQuizData(); if (token !== this.qrToken) return }
                this.loadTeacherData()
            })
        })
    }
    onQuizResultsTab = (key) => {
        if (key === this.state.modalTab) return
        this.setState({ modalTab: key, loadingTeachers: true, quizModalStatsTab: 'teachers', viewingTeacherId: null, teacherAssessments: [] },
            () => this.loadTeacherData())
    }
    // Filter changes reload whichever standards-based view is open.
    reloadStandardsData = (cat, name, sid) => {
        if (this.state.view === 'quiz_results') this.loadQuizResultsFor(cat, name, sid)
        else this.loadStandardDetail(cat, name, sid)
    }
    buildPoints(bw) { return this.state.sparkMode === 'weekly' ? buildWeeklyPoints(bw) : build3WeekPoints(bw) }
    buildRow(label, totals, byWeek, extra) { const a = getAvg(totals), pts = this.buildPoints(byWeek); return { label, avg: a, completed: totals.total_graded || 0, given: givenCount(totals), totals, sparkPoints: pts, byWeek, ...extra } }
    standardRows(sid) { const { selectedSubject } = this.state; const sd = this.schoolDocs().find(d => d.schoolId === sid); if (!sd || !sd.quizzes || !sd.quizzes.by_subject) return []; if (selectedSubject === 'All') { const r = []; Object.entries(sd.quizzes.by_subject).forEach(([s, sD]) => { if (sD.by_standard_name) Object.entries(sD.by_standard_name).forEach(([n, d]) => { r.push(this.buildRow(`${s} — ${n}`, d.totals, d.by_week, { standardName: n, standardCategory: s })) }) }); return r }; const s = sd.quizzes.by_subject[selectedSubject]; if (!s || !s.by_standard_name) return []; return Object.entries(s.by_standard_name).map(([n, d]) => this.buildRow(n, d.totals, d.by_week, { standardName: n, standardCategory: selectedSubject })) }
    teacherRows(sid) { const { selectedSubject } = this.state; const sd = this.schoolDocs().find(d => d.schoolId === sid); if (!sd || !sd.quizzes || !sd.quizzes.teachers_summary) return []; const build = (tid, ts) => { if (!ts.totals || ts.totals.total_graded === 0) return null; return this.buildRow(`${ts.lastName}, ${ts.firstName}`, ts.totals, ts.by_week, { id: tid, gradeLevel: ts.gradeLevel || '' }) }; if (selectedSubject === 'All') return Object.entries(sd.quizzes.teachers_summary).map(([tid, ts]) => build(tid, ts)).filter(Boolean); const tDocs = this.state.quizStats.filter(d => d.type === 'teacher' && d.schoolId === sid); const r = []; tDocs.forEach(td => { const ch = td.quizzes; if (!ch) return; Object.entries(ch).forEach(([tid, tD]) => { if (!tD.by_subject || !tD.by_subject[selectedSubject]) return; const sD = tD.by_subject[selectedSubject]; if (!sD.totals || sD.totals.total_graded === 0) return; r.push(this.buildRow(`${tD.lastName}, ${tD.firstName}`, sD.totals, sD.by_week, { id: tid, gradeLevel: tD.gradeLevel || '' })) }) }); if (r.length > 0) return r; return Object.entries(sd.quizzes.teachers_summary).map(([tid, ts]) => build(tid, ts)).filter(Boolean) }

    // ---- Printable report ----
    prepareReport = async () => {
        await new Promise(resolve => this.setState({ reportPreparing: true }, resolve))
        try {
            if (this.state.reportSections.games && !this.state.gamesLoaded) await this.loadGameStats()
            await new Promise(resolve => this.setState({ reportPrinting: true }, resolve))
        } catch (e) {
            console.log('report prepare error', e)
            this.setState({ reportPreparing: false, reportPrinting: false })
            throw e
        }
    }
    reportDistrictName() {
        const d = this.getDistrict()
        return (d && (d.districtName || d.name)) || 'District'
    }
    reportSchoolData(sid) {
        const sec = this.state.reportSections
        const sd = this.schoolDocs().find(d => d.schoolId === sid) || null
        const iep = this.state.iepSchools.find(d => d.schoolId === sid) || null
        const game = this.state.gameBaseDocs.find(d => d.schoolId === sid) || null
        const quizSum = sd ? this.schoolSubjectSummary(sd) : null
        const practice = sd ? this.schoolResourceSummary(sd) : { total: 0, bySubject: {} }
        const gameTot = game ? Object.values(game.by_teacher || {}).reduce((a, t) => {
            const s = sumTeacherTotals(t)
            a.playtimeSeconds += s.playtimeSeconds; a.correct += s.correct; a.incorrect += s.incorrect; a.sessions += s.sessions
            return a
        }, { playtimeSeconds: 0, correct: 0, incorrect: 0, sessions: 0 }) : null
        const iepTotals = (iep && iep.iep && iep.iep.totals) || {}
        const hasQuiz = !!(quizSum && quizSum.All.given > 0)
        const hasPractice = practice.total > 0
        const hasGames = !!(gameTot && (gameTot.sessions > 0 || gameTot.playtimeSeconds > 0))
        const hasIep = IEP_PLAN_TYPES.some(pt => iepStudentCount(iepTotals[pt]) > 0)
        return {
            sid, name: this.getSchoolName(sid), sd, iep, game, quizSum, practice, gameTot, iepTotals,
            hasQuiz, hasPractice, hasGames, hasIep,
            hasAny: (sec.quizzes && hasQuiz) || (sec.practice && hasPractice) || (sec.games && hasGames) || (sec.iep && hasIep),
        }
    }
    reportTable(columns, rows, emptyText) {
        const th = align => ({ textAlign: align || 'left', padding: '7px 8px', fontSize: 13, fontWeight: 700, color: '#475569', borderBottom: '2px solid #94a3b8' })
        const td = (align, i) => ({ textAlign: align || 'left', padding: '7px 8px', fontSize: 14, color: '#1e293b', borderBottom: '1px solid #e2e8f0', verticalAlign: 'middle', fontWeight: i === 0 ? 600 : 400 })
        return <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 18 }}>
            <thead><tr>{columns.map((c, i) => <th key={i} style={th(c.align)}>{c.label}</th>)}</tr></thead>
            <tbody>
                {rows.length === 0
                    ? <tr><td colSpan={columns.length} style={{ ...td(), color: '#64748b' }}>{emptyText || 'No activity yet.'}</td></tr>
                    : rows.map((r, ri) => <tr key={ri}>{r.map((cell, ci) => <td key={ci} style={td(columns[ci].align, ci)}>{cell}</td>)}</tr>)}
            </tbody>
        </table>
    }
    reportHeading(text, sub) {
        return <div className="report-avoid-break" style={{ margin: '22px 0 10px' }}>
            <div style={{ fontSize: 19, fontWeight: 800, color: '#0f172a', borderBottom: '2px solid #2d6a9f', paddingBottom: 4 }}>{text}</div>
            {sub ? <div style={{ fontSize: 13, color: '#475569', marginTop: 4 }}>{sub}</div> : null}
        </div>
    }
    reportSubHeading(text) {
        return <div style={{ fontSize: 15, fontWeight: 800, color: '#334155', margin: '14px 0 6px' }}>{text}</div>
    }
    // Overview only: students with plans + on-track chip in one cell (explained on the cover).
    reportIepCell(t) {
        const n = iepStudentCount(t)
        if (!n) return <span style={{ color: '#64748b' }}>—</span>
        return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>{fmt(n)}<MiniScore avg={this.iepRate(t)} /></span>
    }
    reportQuizCell(d) {
        if (!d || (d.given === 0 && d.avg == null)) return <span style={{ color: '#64748b' }}>—</span>
        return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>{fmt(d.given)} given<MiniScore avg={d.avg} /></span>
    }
    renderReportCover(pages, scopeLabel) {
        const sec = this.state.reportSections
        const quizzesGiven = pages.reduce((s, p) => s + (p.quizSum ? p.quizSum.All.given : 0), 0)
        const practice = pages.reduce((s, p) => s + p.practice.total, 0)
        const playtime = pages.reduce((s, p) => s + (p.gameTot ? p.gameTot.playtimeSeconds : 0), 0)
        const kpi = (label, value) => <div>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>{value}</div>
            <div style={{ fontSize: 14, color: '#475569' }}>{label}</div>
        </div>
        return <div>
            <div style={{ borderBottom: '3px solid #2d6a9f', paddingBottom: 14, marginBottom: 20 }}>
                <img src={`${window.location.origin}/dotit-logo-color-new.png`} alt="logo" height="50" style={{ display: 'block', marginBottom: 22 }} />
                <div style={{ fontSize: 30, fontWeight: 800, color: '#0f172a' }}>{this.reportDistrictName()}</div>
                <div style={{ fontSize: 18, color: '#334155' }}>Data report · {schoolYearLabel(this.state.selectedYear)} school year</div>
                <div style={{ fontSize: 14, color: '#475569', marginTop: 4 }}>{scopeLabel} · Printed {new Date().toLocaleDateString()}</div>
            </div>
            <div className="report-avoid-break" style={{ display: 'flex', gap: 48, flexWrap: 'wrap', paddingBottom: 18, marginBottom: 18, borderBottom: '1px solid #e2e8f0' }}>
                {kpi('Schools', fmt(pages.length))}
                {sec.quizzes ? kpi('Quizzes given', fmt(quizzesGiven)) : null}
                {sec.practice ? kpi('Practice done', fmt(practice)) : null}
                {sec.games ? kpi('Game playtime', fmtPlaytime(playtime)) : null}
            </div>
            <PrintColorKey iep={sec.iep} />
        </div>
    }
    renderReportOverview(pages) {
        const sec = this.state.reportSections
        const SUBJ = ['ELA', 'Math', 'Science', 'Social Studies']
        return <div>
            {this.reportHeading('School overview', 'Every school side by side for the selected school year.')}
            {sec.quizzes ? <div className="report-avoid-break">
                {this.reportSubHeading('Quizzes: quizzes given and average score')}
                {this.reportTable(
                    [{ label: 'School' }, ...SUBJ.map(s => ({ label: s, align: 'right' })), { label: 'All subjects', align: 'right' }],
                    pages.map(p => [p.name, ...SUBJ.map(s => this.reportQuizCell(p.quizSum && p.quizSum[s])), this.reportQuizCell(p.quizSum && p.quizSum.All)])
                )}
            </div> : null}
            {sec.practice ? <div className="report-avoid-break">
                {this.reportSubHeading('Student practice: practice done')}
                {this.reportTable(
                    [{ label: 'School' }, ...SUBJ.map(s => ({ label: s, align: 'right' })), { label: 'Total', align: 'right' }],
                    pages.map(p => [p.name, ...SUBJ.map(s => { const c = p.practice.bySubject[s] ? p.practice.bySubject[s].count : 0; return c ? fmt(c) : '—' }), p.practice.total ? fmt(p.practice.total) : '—'])
                )}
            </div> : null}
            {sec.games ? <div className="report-avoid-break">
                {this.reportSubHeading('Games: playtime and average score')}
                {this.reportTable(
                    [{ label: 'School' }, { label: 'Playtime', align: 'right' }, { label: 'Sessions', align: 'right' }, { label: 'Average score', align: 'right' }],
                    pages.map(p => [p.name, p.gameTot && p.gameTot.playtimeSeconds ? fmtPlaytime(p.gameTot.playtimeSeconds) : '—', p.gameTot && p.gameTot.sessions ? fmt(p.gameTot.sessions) : '—', <MiniScore avg={gameAccuracy(p.gameTot)} />])
                )}
            </div> : null}
            {sec.iep ? <div className="report-avoid-break">
                {this.reportSubHeading('IEP & MTSS: students with plans and share on track')}
                {this.reportTable(
                    [{ label: 'School' }, ...IEP_PLAN_TYPES.map(pt => ({ label: pt, align: 'right' }))],
                    pages.map(p => [p.name, ...IEP_PLAN_TYPES.map(pt => this.reportIepCell(p.iepTotals[pt]))])
                )}
            </div> : null}
        </div>
    }
    renderReportSchool(p, newPage = true) {
        const sec = this.state.reportSections
        const SUBJ = ['ELA', 'Math', 'Science', 'Social Studies']
        const q = p.sd && p.sd.quizzes
        const wk = p.sd ? this.schoolWeekData(p.sd) : {}
        const hasWeeks = Object.keys(wk).length > 0
        const bySubj = (a, b) => subjectIndex(a) - subjectIndex(b) || a.localeCompare(b)
        const dash = <span style={{ color: '#64748b' }}>—</span>

        // Quizzes: one row per subject, then all subjects
        const quizSubjectRows = SUBJ.filter(s => p.quizSum && p.quizSum[s] && (p.quizSum[s].given > 0 || p.quizSum[s].avg != null))
            .map(s => [s, fmt(p.quizSum[s].given), <MiniScore avg={p.quizSum[s].avg} />])
        if (p.quizSum) quizSubjectRows.push([<b>All subjects</b>, <b>{fmt(p.quizSum.All.given)}</b>, <MiniScore avg={p.quizSum.All.avg} />])

        // Quizzes: teachers
        const teacherRows = q && q.teachers_summary
            ? Object.entries(q.teachers_summary)
                .filter(([, t]) => t.totals && isQuizGiven(t.totals))
                .sort(([, a], [, b]) => (gradeIndex(a.gradeLevel || 'Other') - gradeIndex(b.gradeLevel || 'Other')) || (a.lastName || '').localeCompare(b.lastName || ''))
                .map(([, t]) => [`${t.lastName || ''}, ${t.firstName || ''}`, t.gradeLevel || '—', fmt(givenCount(t.totals)), <MiniScore avg={getAvg(t.totals)} />])
            : []
        // Quizzes: grade levels
        const gradeRows = []
        if (q && q.by_subject) {
            Object.keys(q.by_subject).sort(bySubj).forEach(subj => {
                const byName = q.by_subject[subj].by_standard_name || {}
                Object.keys(byName).sort((a, b) => gradeIndex(a) - gradeIndex(b) || a.localeCompare(b)).forEach(g => {
                    const t = byName[g].totals
                    if (!t || !isQuizGiven(t)) return
                    gradeRows.push([g, subj, fmt(givenCount(t)), <MiniScore avg={getAvg(t)} />])
                })
            })
        }
        // Practice
        const practiceRows = []
        Object.keys(p.practice.bySubject).sort(bySubj).forEach(subj => {
            const row = p.practice.bySubject[subj]
            if (!row.count) return
            const grades = Object.keys(row.grades).filter(g => row.grades[g] > 0).sort((a, b) => gradeIndex(a) - gradeIndex(b))
            practiceRows.push([subj, grades.map(g => `${g}: ${fmt(row.grades[g])}`).join(' · ') || '—', fmt(row.count)])
        })
        // Games
        const gameTeacherRows = p.game ? Object.values(p.game.by_teacher || {})
            .map(t => ({ t, tot: sumTeacherTotals(t) }))
            .filter(r => r.tot.sessions > 0 || r.tot.playtimeSeconds > 0)
            .sort((a, b) => b.tot.playtimeSeconds - a.tot.playtimeSeconds)
            .map(r => [`${r.t.lastName || ''}, ${r.t.firstName || ''}`, fmtPlaytime(r.tot.playtimeSeconds), r.tot.sessions ? fmt(r.tot.sessions) : '—', <MiniScore avg={gameAccuracy(r.tot)} />]) : []
        const catTotals = p.game ? this.schoolCategoryTotals(p.game) : {}
        const gameSubjectRows = Object.values(catTotals)
            .sort((a, b) => subjectIndex(a.category) - subjectIndex(b.category) || gradeIndex(a.standardName) - gradeIndex(b.standardName))
            .map(c => [c.category, c.standardName || '—', fmtPlaytime(c.playtimeSeconds), <MiniScore avg={gameAccuracy(c)} />])
        // IEP: whole school, one row per plan type
        const iepSchoolRows = IEP_PLAN_TYPES.map(pt => {
            const t = p.iepTotals[pt] || {}
            const n = iepStudentCount(t)
            return [pt, n ? fmt(n) : '—', t.measurements ? fmt(t.measurements) : '—', n ? <MiniScore avg={this.iepRate(t)} /> : dash]
        })
        // IEP: by teacher, students + on track per plan type
        const iepTeachers = (p.iep && p.iep.iep && p.iep.iep.teachers_summary) || {}
        const iepRows = Object.values(iepTeachers)
            .filter(t => IEP_PLAN_TYPES.some(pt => iepStudentCount(t.totals && t.totals[pt]) > 0))
            .sort((a, b) => (a.lastName || '').localeCompare(b.lastName || ''))
            .map(t => {
                const cells = []
                IEP_PLAN_TYPES.forEach(pt => {
                    const tt = (t.totals && t.totals[pt]) || {}
                    const n = iepStudentCount(tt)
                    cells.push(n ? fmt(n) : '—')
                    cells.push(n ? <MiniScore avg={this.iepRate(tt)} /> : dash)
                })
                return [`${t.lastName || ''}, ${t.firstName || ''}`, ...cells]
            })
        const iepTeacherCols = [{ label: 'Teacher' }]
        IEP_PLAN_TYPES.forEach(pt => { iepTeacherCols.push({ label: `${pt} students`, align: 'right' }); iepTeacherCols.push({ label: `${pt} on track`, align: 'right' }) })

        return <div key={p.sid} className={newPage ? 'report-page' : undefined} style={newPage ? undefined : { marginTop: 28 }}>
            <div style={{ borderBottom: '1px solid #cbd5e1', paddingBottom: 8, marginBottom: 4 }}>
                <div style={{ fontSize: 26, fontWeight: 800, color: '#0f172a' }}>{p.name}</div>
                <div style={{ fontSize: 14, color: '#475569' }}>{this.reportDistrictName()} · {schoolYearLabel(this.state.selectedYear)} school year</div>
            </div>

            {sec.quizzes && p.hasQuiz ? <div>
                {this.reportHeading('Quizzes')}
                <div className="report-avoid-break" style={{ maxWidth: 520 }}>
                    {this.reportTable([{ label: 'Subject' }, { label: 'Quizzes given', align: 'right' }, { label: 'Average score', align: 'right' }], quizSubjectRows)}
                </div>
                {hasWeeks ? <div className="report-avoid-break" style={{ display: 'flex', gap: 20, marginBottom: 12 }}>
                    <div style={{ flex: 1 }}>{this.reportSubHeading('Quizzes given each week')}<WeeklyGivenBar byWeek={wk} width={480} /></div>
                    <div style={{ flex: 1 }}>{this.reportSubHeading('Average score each week')}<WeeklyAvgLine byWeek={wk} width={480} /></div>
                </div> : null}
                {this.reportSubHeading('By teacher')}
                {this.reportTable([{ label: 'Teacher' }, { label: 'Grade' }, { label: 'Quizzes given', align: 'right' }, { label: 'Average score', align: 'right' }], teacherRows, 'No teacher has given a quiz yet.')}
                {this.reportSubHeading('By grade level')}
                {this.reportTable([{ label: 'Grade' }, { label: 'Subject' }, { label: 'Quizzes given', align: 'right' }, { label: 'Average score', align: 'right' }], gradeRows)}
            </div> : null}

            {sec.practice && p.hasPractice ? <div>
                {this.reportHeading('Student practice', `${fmt(p.practice.total)} practice activities done`)}
                {this.reportTable([{ label: 'Subject' }, { label: 'By grade' }, { label: 'Practice done', align: 'right' }], practiceRows)}
            </div> : null}

            {sec.games && p.hasGames ? <div>
                {this.reportHeading('Games', `${fmtPlaytime(p.gameTot.playtimeSeconds)} of playtime across ${fmt(p.gameTot.sessions)} sessions. Average score is the percentage of game questions answered correctly.`)}
                {this.reportSubHeading('By teacher')}
                {this.reportTable([{ label: 'Teacher' }, { label: 'Playtime', align: 'right' }, { label: 'Sessions', align: 'right' }, { label: 'Average score', align: 'right' }], gameTeacherRows)}
                {this.reportSubHeading('By subject and grade')}
                {this.reportTable([{ label: 'Subject' }, { label: 'Grade' }, { label: 'Playtime', align: 'right' }, { label: 'Average score', align: 'right' }], gameSubjectRows)}
            </div> : null}

            {sec.iep && p.hasIep ? <div>
                {this.reportHeading('IEP & MTSS', 'Students with plans, progress measurements, and the share of plans on track to meet their goal.')}
                <div className="report-avoid-break" style={{ maxWidth: 640 }}>
                    {this.reportTable([{ label: 'Plan type' }, { label: 'Students with plans', align: 'right' }, { label: 'Measurements', align: 'right' }, { label: 'On track', align: 'right' }], iepSchoolRows)}
                </div>
                {this.reportSubHeading('By teacher')}
                {this.reportTable(iepTeacherCols, iepRows)}
            </div> : null}
        </div>
    }
    renderReport() {
        const { reportScope } = this.state
        const isIF = !!this.props.instructionalFacilitatorBaseProps
        const schools = reportScope === 'district' ? this.allSchoolsSorted() : this.allSchools().filter(s => s.id === reportScope)
        const pages = schools.map(s => this.reportSchoolData(s.id)).filter(p => p.hasAny)
        const scopeLabel = reportScope === 'district' ? (isIF ? 'Your schools' : 'All schools') : this.getSchoolName(reportScope)
        const showOverview = reportScope === 'district' && pages.length > 1
        return <div style={{ width: 1000, background: '#fff', color: '#1e293b' }}>
            {this.renderReportCover(pages, scopeLabel)}
            {pages.length === 0 ? <div style={{ marginTop: 24, fontSize: 15, color: '#334155' }}>No activity recorded for the selected sections.</div> : null}
            {showOverview ? this.renderReportOverview(pages) : null}
            {pages.map((p, i) => this.renderReportSchool(p, showOverview || i > 0))}
        </div>
    }
    renderReportModal() {
        const { reportModal, reportScope, reportSections, schoolsLoaded, iepLoaded } = this.state
        const isIF = !!this.props.instructionalFacilitatorBaseProps
        const ready = schoolsLoaded && iepLoaded
        const toggle = key => e => this.setState({ reportSections: { ...reportSections, [key]: e.target.checked } })
        const box = (key, label) => <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 17, color: '#1e293b', marginBottom: 10, cursor: 'pointer' }}>
            <input type="checkbox" checked={!!reportSections[key]} onChange={toggle(key)} style={{ width: 18, height: 18 }} />{label}
        </label>
        const anySection = Object.values(reportSections).some(Boolean)
        return <Modal title="Print school report" visible={reportModal} width={520} footer={null} onCancel={() => this.setState({ reportModal: false })}>
            <div style={{ fontSize: 16, color: '#334155', lineHeight: 1.5, marginBottom: 20 }}>
                A printable report for the {schoolYearLabel(this.state.selectedYear)} school year. Choose what to include, then print it or save it as a PDF from the print window.
            </div>
            <div style={{ marginBottom: 20 }}>
                <FieldLabel htmlFor="report-scope">Schools</FieldLabel>
                <select id="report-scope" value={reportScope} onChange={e => this.setState({ reportScope: e.target.value })} style={FLAT_SELECT_STYLE}>
                    <option value="district">{isIF ? 'All of my schools' : 'Whole district'}</option>
                    {this.allSchoolsSorted().map(s => <option key={s.id} value={s.id}>{this.getSchoolName(s.id)}</option>)}
                </select>
            </div>
            <FieldLabel>Include</FieldLabel>
            {box('quizzes', 'Quizzes')}
            {box('practice', 'Student practice')}
            {box('games', 'Games')}
            {box('iep', 'IEP & MTSS')}
            <div style={{ marginTop: 20, textAlign: 'right' }}>
                <ReactToPrint
                    trigger={() => {
                        const busy = !ready || this.state.reportPreparing
                        return <Button type="primary" size="large" disabled={busy || !anySection}>
                            <Icon type={busy ? 'loading' : 'printer'} />
                            {this.state.reportPreparing ? 'Preparing report…' : 'Print school report'}
                        </Button>
                    }}
                    content={() => this.reportRef}
                    onBeforeGetContent={this.prepareReport}
                    onBeforePrint={() => this.setState({ reportPreparing: false })}
                    onAfterPrint={() => this.setState({ reportPrinting: false, reportPreparing: false, reportModal: false })}
                    onPrintError={() => this.setState({ reportPrinting: false, reportPreparing: false })}
                    pageStyle={REPORT_PAGE_STYLE}
                    documentTitle={`${this.reportDistrictName()} - ${schoolYearLabel(this.state.selectedYear)} Report`}
                />
            </div>
        </Modal>
    }

    renderResultsModal() {
        const { modalVisible, modalStandard, modalStandardDetail, modalQuizDocs, modalQuizLoading, modalTab } = this.state
        if (!modalVisible || !modalStandardDetail) return <Modal visible={false} onCancel={this.closeResultsModal} footer={null}><div /></Modal>
        const detail = modalStandardDetail, quizzes = detail.quizzes || {}
        const overallQuestions = this.getOverallModalQuestions(detail)
        const quizIndices = Object.keys(quizzes).map(Number).filter(n => !isNaN(n)).sort((a, b) => a - b)
        const tabs = [{ key: 'overall', label: 'Overall' }]; quizIndices.forEach(qi => tabs.push({ key: String(qi), label: `Quiz ${qi}` }))
        const tabStyle = a => ({ padding: '8px 16px', fontSize: 17, fontWeight: a ? 800 : 600, border: 'none', borderBottom: a ? '3px solid #2d6a9f' : '3px solid transparent', cursor: 'pointer', backgroundColor: 'transparent', color: a ? '#2d6a9f' : '#64748b', marginRight: 4 })
        return <Modal title={`Quiz Results — ${modalStandard}`} width={970} visible={modalVisible} onCancel={this.closeResultsModal} footer={null}>
            <Explainer title="What you're looking at">
                This window breaks down every quiz tied to this standard. The <b>Overall</b> tab combines all of the quizzes together; each numbered <b>Quiz</b> tab shows one quiz on its own, including a question-by-question breakdown and the option to view performance by teacher or by student.
            </Explainer>
            {modalQuizLoading && <div style={{ padding: 20, color: '#94a3b8' }}>Loading...</div>}
            {tabs.length > 1 && <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', marginBottom: 20, flexWrap: 'wrap' }}>{tabs.map(t => <button key={t.key} onClick={() => {
                this.setState({ modalTab: t.key }, () => {
                    if (t.key !== 'overall') { this.setState({ loadingTeachers: true, quizModalStatsTab: 'teachers', viewingTeacherId: null }, () => this.loadTeacherData()) }
                    else { this.setState({ quizModalStatsTab: 'teachers', viewingTeacherId: null, teacherAssessments: [], teacherAssessmentDict: {}, teacherAssessmentCountDict: {}, teacherAssessmentRetakes: {}, teachers: [], teachersDict: {}, individualTeacherRetakeDict: {}, loadingTeachers: false }) }
                })
            }} style={tabStyle(modalTab === t.key)}>{t.label}</button>)}</div>}
            {modalTab === 'overall' && <div>
                <Note>This summary combines every quiz given for this standard. <b>Avg</b> is the average score across all completed attempts, <b>Quizzes Given</b> is how many times the quiz was assigned, and <b>Completed</b> is how many graded attempts came back. The colored bar shows how those attempts split across performance bands.</Note>
                <StatsHeader stat={detail} label="Overall" />
                <QuestionBars questions={this.isDemo() ? this.state.demoOverallQuestionStats : overallQuestions} label="Overall Question Results" />
                <Note>Below, each quiz for this standard is broken out on its own so you can compare them side by side.</Note>
                {quizIndices.map(qi => {
                    const qd = quizzes[String(qi)], has = qd && qd.total_graded > 0; return <div key={qi} style={{ marginTop: 20, padding: 16, borderRadius: 12, border: '1px solid #e2e8f0', backgroundColor: '#fafbfc' }}>
                        {has ? <div>
                            <StatsHeader stat={qd} label={`Quiz ${qi}`} />
                            <QuestionBars questions={this.isDemo() ? (this.state.allDemoQuizData && this.state.allDemoQuizData[String(qi)] ? this.state.allDemoQuizData[String(qi)].questionStats : null) : (qd.questions || detail.questions)} label={`Quiz ${qi} — Question Results`} />
                        </div>
                            : <div><div style={{ fontSize: 21, fontWeight: 900, color: '#0f172a', marginBottom: 8 }}>Quiz {qi}</div><div style={{ color: '#94a3b8', fontSize: 17 }}>No data yet — this quiz has not been completed by any students.</div></div>}</div>
                })}</div>}
            {quizIndices.map(qi => {
                if (modalTab !== String(qi)) return null
                const qd = quizzes[String(qi)], has = qd && qd.total_graded > 0, qid = qd ? qd.quizId : null, qdoc = qid ? modalQuizDocs[qid] : null
                return <div key={qi}>
                    <Note>This tab shows only Quiz {qi}. The summary gives its average score and completion counts; the question results show which items students found hardest.</Note>
                    {has ? <div>
                        <StatsHeader stat={qd} label={`Quiz ${qi}`} />
                        <QuestionBars questions={this.isDemo() ? (this.state.allDemoQuizData && this.state.allDemoQuizData[String(qi)] ? this.state.allDemoQuizData[String(qi)].questionStats : null) : (qd.questions || detail.questions)} label={`Quiz ${qi} — Question Results`} />
                    </div> : <div style={{ marginBottom: 16 }}><div style={{ fontSize: 21, fontWeight: 900, color: '#0f172a', marginBottom: 8 }}>Quiz {qi}</div><div style={{ padding: '12px 16px', borderRadius: 8, backgroundColor: '#f8fafc', color: '#94a3b8', fontSize: 17 }}>No data yet.</div></div>}
                    {qdoc && isV3or4(qdoc.version) && qdoc.docs && <div style={{ marginTop: 16, borderTop: '1px solid #e2e8f0', paddingTop: 16 }}>
                        <div style={{ fontSize: 17, fontWeight: 700, color: '#94a3b8', marginBottom: 8 }}>Quiz</div>
                        <Note>Use <b>By Teacher</b> to compare how each teacher's class performed on this quiz, or <b>By Student</b> to inspect individual student answers. In the student view, choose a teacher from the dropdown first.</Note>
                        <div className='flex flex-v-center w-100 mb-4'>
                            <Button size='large' type={this.state.quizModalStatsTab === 'teachers' ? 'primary' : 'default'} onClick={() => this.setState({ quizModalStatsTab: 'teachers' })} style={{ marginRight: 8 }}>By Teacher</Button>
                            <Button size='large' type={this.state.quizModalStatsTab === 'students' ? 'primary' : 'default'} onClick={() => this.setState({ quizModalStatsTab: 'students' })}>By Student</Button>
                        </div>
                        {this.state.quizModalStatsTab === 'teachers' ? <div>
                            {this.state.loadingTeachers ? <div className='font-24 mt-50 mb-50 flex flex-v-center flex-h-center w-100'><Icon type='loading' className='mr-1' /><div>Loading...</div></div> :
                                this.state.individualTeacherRetakeDict && this.state.teachers && this.state.teacherAssessments && this.state.teacherAssessments.length > 0 ?
                                    <DOKTeacherBreakdown parentQuiz={qdoc} quiz={this.state.teacherAssessments[0]} teacherRetakes={this.state.individualTeacherRetakeDict} teachers={this.state.teachers} students={this.state.students} studentDict={this.state.studentsDict} />
                                    : <Empty containerClassName="mt-4 mb-50 text-center" width={55} height={55} image={'/empty/goals.png'} description={<div>There are currently no teacher assessments for this quiz.<br />Once teachers have assigned this quiz and students have completed it, you will be able to view teacher and student performance data here.</div>} />}
                        </div> : ''}
                        {this.state.quizModalStatsTab === 'students' ? <div>
                            <Select value={this.state.viewingTeacherId || undefined} placeholder='Select teacher...' size='large' style={{ width: '250px' }} onChange={e => this.viewTeacherAssessment(e)}>
                                {this.state.teachers && this.state.teachers.map((t, ti) => <Select.Option key={'teacher-' + ti} value={t.id}>{t.firstName} {t.lastName}</Select.Option>)}
                            </Select>
                            {!this.state.viewingTeacherId ? <div className='font-20'><Empty containerClassName="mt-4 mb-50 text-center" width={55} height={55} image={'/empty/goals.png'} description={<div>There is currently no teacher selected.<br />Select a teacher by using the dropdown above.</div>} /></div>
                                : this.state.loadingStudents ? <div className='font-24 mt-50 mb-50 flex flex-v-center flex-h-center w-100'><Icon type='loading' className='mr-1' /><div>Loading...</div></div>
                                    : this.state.teacherAssessmentRetakes && Object.keys(this.state.teacherAssessmentRetakes).length > 0 && Object.keys(this.state.teacherAssessmentRetakes).map((count, countIdx) => {
                                        return this.state.teacherAssessmentRetakes[count].map((tar, retakeIdx) => {
                                            if (tar.teacherId !== this.state.viewingTeacherId) return false
                                            return <div className='mt-4' key={'assessment-retake-' + countIdx + '-' + retakeIdx}>
                                                <div className='sticky-left sticky-left-more display-table'>
                                                    {this.state.teacherAssessmentCountDict[tar.teacherId] > 1 ? <div className='font-20 mb-05 font-bold'>{'Assessment #' + (countIdx + 1)}</div> : <div className='font-20 mb-05 font-bold'>Assessment</div>}
                                                </div>
                                                {tar && this.state.students && this.state.studentsDict ? <DOKStandardBreakdown parentQuiz={qdoc} quiz={tar} students={this.state.students} studentDict={this.state.studentsDict} onlyShowCompleted={true} showProgress={false} /> : ''}
                                            </div>
                                        })
                                    })}
                        </div> : ''}
                    </div>}
                    {qid && !qdoc && !modalQuizLoading && <div style={{ marginTop: 12, padding: 12, borderRadius: 8, backgroundColor: '#f8fafc', color: '#94a3b8', fontSize: 16 }}>Quiz preview unavailable</div>}
                </div>
            })}
        </Modal>
    }
    // ---- Side navigation: Schools -> Teachers -> Standards ----
    renderSideNav() {
        const { view, availableYears, selectedYear } = this.state
        const groups = [
            {
                header: 'Students',
                items: [
                    { key: 'students', label: 'Students', icon: 'user' },
                    { key: 'resources', label: 'Student Practice', icon: 'book' },
                ],
            },
            {
                header: 'Quizzes',
                items: [
                    { key: 'standards', label: 'Quizzes by Standard', icon: 'profile' },
                    { key: 'quiz_results', label: 'Quiz Detailed Results', icon: 'bar-chart' },
                    { key: 'pacing', label: 'Quizzes by Grade Level', icon: 'read' },
                    { key: 'teachers', label: 'Quizzes by Teacher', icon: 'team' },
                    { key: 'schools', label: 'Quizzes by School', icon: 'home' },
                ],
            },
            {
                header: 'Games',
                items: [
                    { key: 'games_school', label: 'Games by School', icon: 'home' },
                    { key: 'games_teacher', label: 'Games by Teacher', icon: 'team' },
                    { key: 'games_standard', label: 'Games by Standard', icon: 'profile' },
                ],
            },
            {
                header: 'IEP & MTSS',
                items: [
                    { key: 'iep_school', label: 'IEP & MTSS by School', icon: 'home' },
                    { key: 'iep_teacher', label: 'IEP & MTSS by Teacher', icon: 'team' },
                ],
            },
        ]
        const groupTitle = (text, first) => <div style={{ fontSize: 14, fontWeight: 700, color: '#64748b', padding: '0 16px', margin: `${first ? 0 : 22}px 0 6px` }}>{text}</div>
        const navItem = (t) => {
            const active = view === t.key
            return <button key={t.key} type="button" aria-current={active ? 'page' : undefined} onClick={() => this.goView(t.key)}
                style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '10px 16px 10px 12px', fontSize: 17, fontWeight: active ? 800 : 500, lineHeight: 1.3, border: 'none', borderLeft: active ? '4px solid #2d6a9f' : '4px solid transparent', borderRadius: 0, cursor: 'pointer', backgroundColor: active ? '#f1f5f9' : 'transparent', color: active ? '#0f172a' : '#334155', textAlign: 'left' }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.backgroundColor = '#f8fafc' }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.backgroundColor = 'transparent' }}>
                <Icon type={t.icon} style={{ fontSize: 18, color: active ? '#2d6a9f' : '#64748b' }} />
                <span>{t.label}</span>
            </button>
        }
        const printButton = (label, onClick) => <button type="button" onClick={onClick}
            style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 14px', marginBottom: 8, fontSize: 16, fontWeight: 600, border: '1px solid #cbd5e1', borderRadius: 8, backgroundColor: '#fff', cursor: 'pointer', color: '#1e293b', textAlign: 'left' }}
            onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#f1f5f9'; e.currentTarget.style.borderColor = '#94a3b8' }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.borderColor = '#cbd5e1' }}>
            <Icon type="printer" style={{ fontSize: 17, color: '#2d6a9f' }} />{label}
        </button>
        return <div style={{ width: 300, minWidth: 300, flexShrink: 0, position: 'fixed', top: 84, left: 0, bottom: 0, backgroundColor: '#fff', borderRight: '1px solid #e2e8f0', padding: '20px 0', zIndex: 150, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
            {availableYears.length > 0 && <div style={{ padding: '0 16px 20px', marginBottom: 16, borderBottom: '1px solid #e2e8f0' }}>
                <label htmlFor="usage-school-year" style={{ display: 'block', fontSize: 14, fontWeight: 700, color: '#64748b', marginBottom: 6 }}>School year</label>
                <select id="usage-school-year" value={selectedYear || ''} onChange={e => this.onYearChange(e.target.value)} style={{ ...SELECT_STYLE, width: '100%', fontSize: 17, borderColor: '#cbd5e1', marginBottom: 16 }}>
                    {availableYears.map(ys => <option key={ys} value={ys}>{schoolYearLabel(ys)}</option>)}
                </select>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#64748b', marginBottom: 6 }}>Print a report</div>
                {printButton('School report', () => this.setState({ reportModal: true }))}
                {printButton('Student report', () => this.setState({ studentReportModal: true, studentReportScope: this.state.selectedSchoolId || 'district' }))}
            </div>}

            {groups.map((g, gi) => <React.Fragment key={g.header}>
                {groupTitle(g.header, gi === 0)}
                {g.items.map(navItem)}
            </React.Fragment>)}
        </div>
    }
    renderHero() {
        const { view } = this.state
        const map = {
            schools: { title: 'Quizzes by School', sub: 'Quiz activity across every school in your district' },
            teachers: { title: 'Quizzes by Teacher', sub: 'How students performed, by grade level and teacher' },
            standards: { title: 'Quizzes by Standard', sub: "Every standard's results — choose a subject and grade level" },
            pacing: { title: 'Quizzes by Grade Level', sub: 'Pacing guide progress for a school — quizzes given and teacher activity by week' },
            resources: { title: 'Student Practice', sub: 'Learning resources students have completed, by school and subject' },
            iep_school: { title: 'IEP & MTSS by School', sub: 'Plans, measurements, and on-track rates for every school' },
            iep_teacher: { title: 'IEP & MTSS by Teacher', sub: 'Plans, measurements, and on-track rates by teacher for a school' },
            games_school: { title: 'Games by School', sub: 'Game activity across every school — playtime, sessions, and average scores' },
            games_teacher: { title: 'Games by Teacher', sub: 'Game playtime and average scores by teacher for a school' },
            games_standard: { title: 'Games by Standard', sub: 'Game playtime and average scores per standard, by subject' },
            students: { title: 'Students', sub: 'Quiz scores, practice, and IEP & MTSS plans for each student' },
            quiz_results: { title: 'Quiz Detailed Results', sub: 'Question-by-question results for each standard, one quiz at a time' },
        }
        const h = map[view] || map.schools
        return <div className='br-20 p-3 mb-4' style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #2d6a9f 100%)' }}>
            <div className='p-2' style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                <div>
                    <div className='font-fff' style={{ fontSize: 34, fontWeight: 800 }}>{h.title}</div>
                    <div className='font-fff' style={{ fontSize: 18, opacity: 0.92 }}>{h.sub}</div>
                </div>
                {this.state.selectedYear ? <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: '#fff', borderRadius: 999, padding: '8px 16px', fontSize: 16, fontWeight: 700, whiteSpace: 'nowrap' }}><Icon type="calendar" />{schoolYearLabel(this.state.selectedYear)} school year</div> : null}
            </div>
        </div>
    }
    renderDropdowns(includeSubject = true) {
        const { selectedSchoolId, selectedSubject } = this.state
        const schools = this.allSchoolsSorted();
        const fixed = ['ELA', 'Math', 'Science', 'Social Studies']
        const ds = this.subjects()
        const allS = [...fixed]; ds.forEach(s => { if (!allS.includes(s)) allS.push(s) }); allS.unshift('All')
        return <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}><span style={{ fontSize: 15, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>School</span>
                <select value={selectedSchoolId || ''}
                    onChange={e => this.selectSchool(e.target.value, { sortCol: 'label', sortDir: 'asc' })}
                    style={SELECT_STYLE}>
                    {schools.length === 0 && <option value="" disabled>No data available</option>}{schools.map(s => <option key={s.id} value={s.id}>{this.getSchoolName(s.id)}</option>)}
                </select></div>
            {includeSubject && <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}><span style={{ fontSize: 15, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Subject</span><select value={selectedSubject} onChange={e => this.setState({ selectedSubject: e.target.value, sortCol: 'label', sortDir: 'asc', expandedRows: {} })} style={SELECT_STYLE}>{allS.map(s => <option key={s} value={s}>{s}</option>)}</select></div>}
        </div>
    }
    thLeft = { textAlign: 'left', padding: '10px 12px', fontSize: 14, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.8, borderBottom: '2px solid #e2e8f0' }
    thRight = { ...this.thLeft, textAlign: 'right' }
    renderTeacherTable(rows, keyPrefix) {
        const sorted = [...rows].sort((a, b) => {
            const av = a.avg == null ? Infinity : a.avg, bv = b.avg == null ? Infinity : b.avg
            if (av !== bv) return av - bv
            return (a.label || '').localeCompare(b.label || '')
        })
        return <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 17 }}>
            <thead><tr>
                <th style={flatTh('left')}>Teacher</th>
                <th style={flatTh('right', 150)}>Quizzes given</th>
                <th style={{ ...flatTh('left', 200), paddingLeft: 28 }}>Average score</th>
                <th style={flatTh('right', 140)} />
            </tr></thead>
            <tbody>
                {sorted.length === 0 && <tr><td colSpan={4} style={{ padding: 28, textAlign: 'center', color: '#334155' }}>No teachers to show.</td></tr>}
                {sorted.map((r, i) => {
                    const rowKey = `${keyPrefix}:${r.id || r.label}:${i}`
                    const open = !!this.state.expandedRows[rowKey]
                    const canExpand = isQuizGiven(r.totals)
                    return <React.Fragment key={rowKey}>
                        <tr style={{ borderBottom: open ? 'none' : '1px solid #e2e8f0', cursor: canExpand ? 'pointer' : 'default' }}
                            onClick={canExpand ? () => this.toggleRow(rowKey) : undefined}
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8fafc'}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                            <td style={{ padding: '12px', fontWeight: 700, color: '#0f172a' }}>{r.label}</td>
                            <td style={{ padding: '12px', textAlign: 'right', color: '#1e293b' }}>{r.given > 0 ? fmt(r.given) : <span style={{ color: '#64748b' }}>None</span>}</td>
                            <td style={{ padding: '12px 12px 12px 28px' }}><QuizScore det={r.totals} /></td>
                            <td style={{ padding: '12px', textAlign: 'right' }}>{canExpand ? <DetailsLink open={open} /> : null}</td>
                        </tr>
                        {open && <tr style={{ borderBottom: '1px solid #e2e8f0' }}><td colSpan={4} style={{ padding: '0 12px 20px' }}>
                            <RowDetail det={r.totals} bySubject={r.id ? this.teacherWeekBySubject(this.state.selectedSchoolId, r.id) : { ELA: r.byWeek }} />
                        </td></tr>}
                    </React.Fragment>
                })}
            </tbody>
        </table>
    }
    // ---- Schools view (default): one row per school ----
    renderSchoolsView() {
        const SUBJECTS = this.state.showSciSS ? ['ELA', 'Math', 'Science', 'Social Studies'] : ['ELA', 'Math']
        const isIF = !!this.props.instructionalFacilitatorBaseProps
        const schools = this.schoolDocs().map(sd => ({ sd, sum: this.schoolSubjectSummary(sd) }))
        let given = 0, gradeSum = 0, graded = 0
        schools.forEach(({ sum }) => { given += sum.All.given; gradeSum += sum.All.gradeSum; graded += sum.All.totalGraded })
        const avg = graded > 0 ? gradeSum / graded : null
        const activeSchools = schools.filter(({ sum }) => sum.All.given > 0).length
        const yearLine = this.state.selectedYear ? `${schoolYearLabel(this.state.selectedYear)} school year` : ''

        const subjectCell = d => {
            if (!d || (d.given === 0 && d.avg == null)) return <span style={{ color: '#64748b' }}>None yet</span>
            return <div>
                {d.avg != null ? <AverageScore avg={d.avg} /> : <span style={{ color: '#475569' }}>No scores yet</span>}
                {d.given > 0 ? <div style={{ fontSize: 14, color: '#64748b', marginTop: 2 }}>{fmt(d.given)} {d.given === 1 ? 'quiz' : 'quizzes'} given</div> : null}
            </div>
        }

        return <div>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 24, cursor: 'pointer', fontSize: 16, fontWeight: 600, color: '#334155', userSelect: 'none' }}>
                <input type="checkbox" checked={this.state.showSciSS} onChange={e => this.setState({ showSciSS: e.target.checked })} style={{ width: 18, height: 18, cursor: 'pointer' }} />
                Show Science and Social Studies
            </label>

            {!this.state.schoolsLoaded ? <LoadingLine text="Loading schools…" /> : <div>
                <ViewHeading title={isIF ? 'Your schools' : this.reportDistrictName()} sub={yearLine} />
                <StatRow>
                    <FlatStat label="Schools" value={fmt(schools.length)} />
                    <FlatStat label="Schools giving quizzes" value={fmt(activeSchools)} />
                    <FlatStat label="Quizzes given" value={fmt(given)} />
                    <FlatStat label="Average score"><AverageScore avg={avg} big /></FlatStat>
                </StatRow>

                <SectionTitle note="Each subject shows the average score and how many quizzes were given." right={<AverageColorKey />}>Quizzes by school</SectionTitle>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 17 }}>
                    <thead><tr>
                        <th style={flatTh('left')}>School</th>
                        {SUBJECTS.map(s => <th key={s} style={flatTh('left', 150)}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><SubjectIcon subject={s} size={22} />{s}</span>
                        </th>)}
                        <th style={flatTh('right', 140)} />
                    </tr></thead>
                    <tbody>
                        {schools.length === 0 && <tr><td colSpan={SUBJECTS.length + 2} style={{ padding: 28, textAlign: 'center', color: '#334155' }}>No quiz activity yet this school year.</td></tr>}
                        {schools.map(({ sd, sum }, idx) => {
                            const rowKey = `school:${sd.schoolId}:${idx}`
                            const open = !!this.state.expandedRows[rowKey]
                            const wkBySubject = this.schoolWeekBySubject(sd)
                            const canExpand = Object.keys(wkBySubject).length > 0
                            return <React.Fragment key={rowKey}>
                                <tr style={{ borderBottom: open ? 'none' : '1px solid #e2e8f0', cursor: canExpand ? 'pointer' : 'default' }}
                                    onClick={canExpand ? () => this.toggleRow(rowKey) : undefined}
                                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8fafc'}
                                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                                    <td style={{ padding: '12px', fontWeight: 700, color: '#0f172a' }}>{(sd.quizzes && sd.quizzes.name) || this.getSchoolName(sd.schoolId)}</td>
                                    {SUBJECTS.map(s => <td key={s} style={{ padding: '12px' }}>{subjectCell(sum[s])}</td>)}
                                    <td style={{ padding: '12px', textAlign: 'right' }}>{canExpand ? <DetailsLink open={open} /> : null}</td>
                                </tr>
                                {open && <tr style={{ borderBottom: '1px solid #e2e8f0' }}><td colSpan={SUBJECTS.length + 2} style={{ padding: '0 12px 20px' }}>
                                    <div style={{ marginLeft: 14, paddingLeft: 18, borderLeft: '3px solid #e2e8f0' }}>
                                        <WeekTrends bySubject={wkBySubject} />
                                    </div>
                                </td></tr>}
                            </React.Fragment>
                        })}
                    </tbody>
                </table>
            </div>}
        </div>
    }
    // ---- Teachers view: per-teacher results + grade-level table with weekly charts ----
    renderTeachersView() {
        const { selectedSchoolId, selectedSubject } = this.state
        const schoolOptions = this.allSchoolsSorted()
        const schoolName = selectedSchoolId ? this.getSchoolName(selectedSchoolId) : ''
        const subjectOptions = ['All', 'ELA', 'Math', 'Science', 'Social Studies']
        this.subjects().forEach(s => { if (!subjectOptions.includes(s)) subjectOptions.push(s) })
        const subjectWords = selectedSubject === 'All' ? '' : ` in ${selectedSubject}`

        const tRows = this.teacherRows(selectedSchoolId)
        const stdRows = this.standardRows(selectedSchoolId)

        // School totals come from the grade-level rollup (each quiz counted once).
        let given = 0, gradeSum = 0, graded = 0
        stdRows.forEach(r => { given += r.given || 0; gradeSum += (r.totals && r.totals.grade_sum) || 0; graded += (r.totals && r.totals.total_graded) || 0 })
        const avg = graded > 0 ? gradeSum / graded : null

        const gg = {}
        tRows.forEach(r => { const gl = r.gradeLevel || 'Other'; if (!gg[gl]) gg[gl] = []; gg[gl].push(r) })
        const sg = Object.keys(gg).sort((x, y) => (gradeIndex(x) - gradeIndex(y)) || x.localeCompare(y))
        const gradeRows = [...stdRows].sort((x, y) => (subjectIndex(x.standardCategory) - subjectIndex(y.standardCategory)) || (gradeIndex(x.standardName) - gradeIndex(y.standardName)) || (x.label || '').localeCompare(y.label || ''))

        const sub = [
            selectedSubject === 'All' ? 'All subjects' : selectedSubject,
            this.state.selectedYear ? `${schoolYearLabel(this.state.selectedYear)} school year` : null,
        ].filter(Boolean).join(' · ')

        const onSchool = sid => this.selectSchool(sid, { sortCol: 'label', sortDir: 'asc' })

        return <div>
            <FilterGrid max={560}>
                <FlatSelectField id="teachers-school" label="School" title={schoolName} value={selectedSchoolId || ''} onChange={onSchool}>
                    {schoolOptions.length === 0 && <option value="" disabled>No schools available</option>}
                    {schoolOptions.map(s => <option key={s.id} value={s.id}>{this.getSchoolName(s.id)}</option>)}
                </FlatSelectField>
                <FlatSelectField id="teachers-subject" label="Subject" value={selectedSubject} onChange={v => this.setState({ selectedSubject: v, sortCol: 'label', sortDir: 'asc', expandedRows: {} })}>
                    {subjectOptions.map(s => <option key={s} value={s}>{s === 'All' ? 'All subjects' : s}</option>)}
                </FlatSelectField>
            </FilterGrid>

            {!this.state.schoolsLoaded ? <LoadingLine text="Loading teachers…" /> : <div>
                <ViewHeading title={schoolName} sub={sub} />
                <StatRow>
                    <FlatStat label="Teachers giving quizzes" value={fmt(tRows.length)} />
                    <FlatStat label={'Quizzes given' + subjectWords} value={fmt(given)} />
                    <FlatStat label={'Average score' + subjectWords}><AverageScore avg={avg} big /></FlatStat>
                </StatRow>

                <SectionTitle note="Lowest average first. Only teachers who have given a quiz are listed." right={<AverageColorKey />}>Teachers</SectionTitle>
                {tRows.length === 0
                    ? <EmptyLine>No teacher at {schoolName || 'this school'} has given a quiz{subjectWords} yet.</EmptyLine>
                    : sg.length > 1
                        ? sg.map(gl => <div key={gl} style={{ marginBottom: 24 }}>
                            <div style={{ fontSize: 17, fontWeight: 800, color: '#334155', margin: '12px 0 2px' }}>
                                {gl} <span style={{ fontWeight: 500, color: '#64748b' }}>({fmt(gg[gl].length)})</span>
                            </div>
                            {this.renderTeacherTable(gg[gl], `teacher-${gl}`)}
                        </div>)
                        : this.renderTeacherTable(tRows, 'teacher')}

                <div style={{ height: 28 }} />
                <SectionTitle note="Each row is one grade level and subject.">Grade levels</SectionTitle>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 17 }}>
                    <thead><tr>
                        <th style={flatTh('left')}>Grade level</th>
                        <th style={flatTh('right', 150)}>Quizzes given</th>
                        <th style={{ ...flatTh('left', 200), paddingLeft: 28 }}>Average score</th>
                        <th style={flatTh('right', 140)} />
                    </tr></thead>
                    <tbody>
                        {gradeRows.length === 0 && <tr><td colSpan={4} style={{ padding: 28, textAlign: 'center', color: '#334155' }}>No quiz activity yet.</td></tr>}
                        {gradeRows.map((r, i) => {
                            const rowKey = `gradelevel:${r.standardCategory || ''}:${r.label}:${i}`
                            const open = !!this.state.expandedRows[rowKey]
                            const canExpand = isQuizGiven(r.totals)
                            return <React.Fragment key={rowKey}>
                                <tr style={{ borderBottom: open ? 'none' : '1px solid #e2e8f0', cursor: canExpand ? 'pointer' : 'default' }}
                                    onClick={canExpand ? () => this.toggleRow(rowKey) : undefined}
                                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8fafc'}
                                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                                    <td style={{ padding: '12px' }}>
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                                            <SubjectIcon subject={r.standardCategory} size={28} />
                                            <span style={{ fontWeight: 700, color: '#0f172a' }}>{r.standardName}</span>
                                            <span style={{ color: '#64748b' }}>{r.standardCategory}</span>
                                        </span>
                                    </td>
                                    <td style={{ padding: '12px', textAlign: 'right', color: '#1e293b' }}>{r.given > 0 ? fmt(r.given) : <span style={{ color: '#64748b' }}>None</span>}</td>
                                    <td style={{ padding: '12px 12px 12px 28px' }}><QuizScore det={r.totals} /></td>
                                    <td style={{ padding: '12px', textAlign: 'right' }}>{canExpand ? <DetailsLink open={open} /> : null}</td>
                                </tr>
                                {open && <tr style={{ borderBottom: '1px solid #e2e8f0' }}><td colSpan={4} style={{ padding: '0 12px 20px' }}>
                                    <RowDetail det={r.totals} bySubject={r.standardName ? this.gradeWeekBySubject(selectedSchoolId, r.standardName) : { [r.standardCategory || 'ELA']: r.byWeek }} />
                                </td></tr>}
                            </React.Fragment>
                        })}
                    </tbody>
                </table>
            </div>}
        </div>
    }
    // ---- Standards view: subject + grade selectors, then the per-standard table ----
    renderStandardsView() {
        const { selectedSchoolId, standardsCategory, standardsName } = this.state
        const schoolOptions = this.allSchoolsSorted()
        const schoolName = selectedSchoolId ? this.getSchoolName(selectedSchoolId) : ''
        const cats = selectedSchoolId ? this.getCategories(selectedSchoolId) : []
        const names = (selectedSchoolId && standardsCategory) ? this.getStandardNames(selectedSchoolId, standardsCategory) : []
        const sub = [
            standardsCategory, standardsName,
            this.state.selectedYear ? `${schoolYearLabel(this.state.selectedYear)} school year` : null,
        ].filter(Boolean).join(' · ')
        return <div>
            <FilterGrid max={780}>
                <FlatSelectField id="standards-school" label="School" title={schoolName} value={selectedSchoolId || ''} onChange={this.onStandardsSchoolChange}>
                    {schoolOptions.length === 0 && <option value="" disabled>No schools available</option>}
                    {schoolOptions.map(s => <option key={s.id} value={s.id}>{this.getSchoolName(s.id)}</option>)}
                </FlatSelectField>
                <FlatSelectField id="standards-subject" label="Subject" value={standardsCategory || ''} onChange={this.onStandardsCategoryChange}>
                    {cats.length === 0 && <option value="" disabled>None available</option>}
                    {cats.map(c => <option key={c} value={c}>{c}</option>)}
                </FlatSelectField>
                <FlatSelectField id="standards-grade" label="Grade" value={standardsName || ''} onChange={this.onStandardsNameChange}>
                    {names.length === 0 && <option value="" disabled>None available</option>}
                    {names.map(n => <option key={n} value={n}>{n}</option>)}
                </FlatSelectField>
            </FilterGrid>
            <ViewHeading title={schoolName} sub={sub} />
            {this.renderStandardsTable()}
        </div>
    }
    // ---- Quiz Detailed Results view ----
    renderQuizResultsView() {
        const { selectedSchoolId, standardsCategory, standardsName, standardDetailLoading, standardsLoading, qrPending, qrStandardKey, modalStandardDetail } = this.state
        const schoolOptions = this.allSchoolsSorted()
        const schoolName = selectedSchoolId ? this.getSchoolName(selectedSchoolId) : ''
        const cats = selectedSchoolId ? this.getCategories(selectedSchoolId) : []
        const names = (selectedSchoolId && standardsCategory) ? this.getStandardNames(selectedSchoolId, standardsCategory) : []

        const filters = <FilterGrid max={780}>
            <FlatSelectField id="qr-school" label="School" title={schoolName} value={selectedSchoolId || ''} onChange={this.onStandardsSchoolChange}>
                {schoolOptions.length === 0 && <option value="" disabled>No schools available</option>}
                {schoolOptions.map(s => <option key={s.id} value={s.id}>{this.getSchoolName(s.id)}</option>)}
            </FlatSelectField>
            <FlatSelectField id="qr-subject" label="Subject" value={standardsCategory || ''} onChange={this.onStandardsCategoryChange}>
                {cats.length === 0 && <option value="" disabled>None available</option>}
                {cats.map(c => <option key={c} value={c}>{c}</option>)}
            </FlatSelectField>
            <FlatSelectField id="qr-grade" label="Grade" value={standardsName || ''} onChange={this.onStandardsNameChange}>
                {names.length === 0 && <option value="" disabled>None available</option>}
                {names.map(n => <option key={n} value={n}>{n}</option>)}
            </FlatSelectField>
        </FilterGrid>

        let body
        if (!this.state.schoolsLoaded || standardDetailLoading || standardsLoading || qrPending) {
            body = <LoadingLine text="Loading results…" />
        } else if (!standardsCategory || !standardsName) {
            body = <EmptyLine>There are no quiz results for this school yet.</EmptyLine>
        } else {
            const rows = this.quizResultsRows()
            const withData = rows.filter(r => r.hasData)
            const cur = rows.find(r => r.standard === qrStandardKey) || null
            if (rows.length === 0) {
                body = <EmptyLine>No standards found for {standardsName} {standardsCategory}.</EmptyLine>
            } else {
                body = <div>
                    {this.renderQuizResultsNav(rows, withData, cur)}
                    {!cur
                        ? <EmptyLine>Choose a standard above.</EmptyLine>
                        : (!cur.hasData || !modalStandardDetail)
                            ? <div>
                                <ViewHeading title={cur.standard} sub={cur.description} />
                                <EmptyLine>
                                    No students have completed a quiz for {cur.standard} yet.<br />
                                    {withData.length ? 'Use Next to jump to a standard with results.' : `None of the ${standardsName} ${standardsCategory} standards have results yet.`}
                                </EmptyLine>
                            </div>
                            : this.renderQuizResultsStandard(cur)}
                </div>
            }
        }
        return <div>{filters}{body}</div>
    }
    renderQuizResultsNav(rows, withData, cur) {
        const prev = this.adjacentWithData(rows, -1)
        const next = this.adjacentWithData(rows, 1)
        const pos = cur ? withData.findIndex(r => r.standard === cur.standard) : -1
        const navBtn = (label, icon, target, iconAfter) => <button type="button" disabled={!target}
            onClick={() => { if (target) this.selectQuizResultsStandard(target.standard) }}
            title={target ? `Go to ${target.standard}` : 'No more standards with results'}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 16px', fontSize: 17, fontWeight: 700, borderRadius: 8, border: '1px solid #cbd5e1', backgroundColor: '#fff', color: target ? '#1d4ed8' : '#cbd5e1', cursor: target ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap' }}>
            {iconAfter ? null : <Icon type={icon} style={{ fontSize: 13 }} />}{label}{iconAfter ? <Icon type={icon} style={{ fontSize: 13 }} /> : null}
        </button>
        return <div style={{ marginBottom: 24, paddingBottom: 20, borderBottom: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 300px', maxWidth: 460, minWidth: 0 }}>
                    <FlatSelectField id="qr-standard" label="Standard" value={cur ? cur.standard : ''} onChange={this.selectQuizResultsStandard}>
                        {!cur && <option value="" disabled>Choose a standard</option>}
                        {rows.map(r => <option key={r.standard} value={r.standard}>{r.standard}{r.hasData ? '' : ' (no results yet)'}</option>)}
                    </FlatSelectField>
                </div>
                {navBtn('Previous', 'left', prev, false)}
                {navBtn('Next', 'right', next, true)}
            </div>
            <div style={{ fontSize: 15, color: '#475569', marginTop: 8 }}>
                {withData.length === 0
                    ? 'No standards in this grade have quiz results yet.'
                    : pos >= 0
                        ? `Standard ${pos + 1} of ${withData.length} with results. Previous and Next skip standards without results.`
                        : `${withData.length} ${withData.length === 1 ? 'standard has' : 'standards have'} results. Previous and Next skip standards without results.`}
            </div>
        </div>
    }
    renderQuizResultsStandard(cur) {
        const { modalStandardDetail: detail, modalTab: tab, modalQuizDocs, modalQuizLoading } = this.state
        const quizzes = detail.quizzes || {}
        const idx = Object.keys(quizzes).map(Number).filter(n => !isNaN(n)).sort((a, b) => a - b)
        const withResults = idx.filter(i => hasResults(quizzes[String(i)])).length
        const qd = tab != null ? quizzes[tab] : null
        const qid = qd ? qd.quizId : null
        const qdoc = qid ? modalQuizDocs[qid] : null
        const questions = this.isDemo()
            ? (this.state.allDemoQuizData && this.state.allDemoQuizData[tab] ? this.state.allDemoQuizData[tab].questionStats : null)
            : (qd ? (qd.questions || detail.questions) : null)
        const mini = (label, value) => <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>{value}</div>
            <div style={{ fontSize: 14, color: '#475569' }}>{label}</div>
        </div>
        const quizTab = i => {
            const k = String(i), active = tab === k, has = hasResults(quizzes[k])
            return <button key={k} type="button" aria-pressed={active} onClick={() => this.onQuizResultsTab(k)}
                style={{ padding: '10px 2px', marginRight: 28, marginBottom: -1, fontSize: 17, fontWeight: active ? 800 : 500, border: 'none', borderBottom: active ? '3px solid #2d6a9f' : '3px solid transparent', background: 'none', cursor: 'pointer', color: active ? '#0f172a' : '#475569', whiteSpace: 'nowrap' }}>
                Quiz {i}{has ? null : <span style={{ color: '#94a3b8', fontWeight: 500 }}> (no results)</span>}
            </button>
        }

        return <div>
            <ViewHeading title={cur.standard} sub={cur.description} />
            <StatRow>
                <FlatStat label="Average score"><AverageScore avg={getAvg(detail)} big /></FlatStat>
                <FlatStat label="Quizzes given" value={fmt(givenCount(detail))} />
                <FlatStat label="Completed by students" value={fmt(detail.completed || detail.total_graded || 0)} />
                {idx.length ? <FlatStat label="Quizzes with results" value={`${fmt(withResults)} of ${fmt(idx.length)}`} /> : null}
            </StatRow>

            {idx.length === 0
                ? <EmptyLine>Results for individual quizzes aren't available for this standard.</EmptyLine>
                : <div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', borderBottom: '1px solid #e2e8f0', marginBottom: 20 }}>{idx.map(quizTab)}</div>
                    {!hasResults(qd)
                        ? <EmptyLine>No students have completed Quiz {tab} yet.</EmptyLine>
                        : <div>
                            <div style={{ display: 'flex', gap: 40, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 24 }}>
                                {mini('Average score', <AverageScore avg={getAvg(qd)} />)}
                                {mini('Quizzes given', fmt(givenCount(qd)))}
                                {mini('Completed', fmt(qd.completed || qd.total_graded || 0))}
                                {mini('How students performed', <EmadBar stat={{ exceed: qd.exceed || 0, meet: qd.meet || 0, approach: qd.approach || 0, does_not_meet: qd.does_not_meet || 0 }} w={180} h={12} />)}
                            </div>
                            <SectionTitle right={<AverageColorKey />}>Question-by-question results</SectionTitle>
                            {questions && Object.keys(questions).length
                                ? <QuestionBars questions={questions} />
                                : this.isDemo() ? <LoadingLine text="Loading question results…" /> : <EmptyLine>Question results aren't available for this quiz.</EmptyLine>}
                            {qdoc && isV3or4(qdoc.version) && qdoc.docs
                                ? this.renderQuizResultsBreakdown(qdoc)
                                : modalQuizLoading
                                    ? <LoadingLine text="Loading quiz…" />
                                    : qid ? <div style={{ marginTop: 24, fontSize: 16, color: '#475569' }}>Teacher and student results aren't available for this quiz.</div> : null}
                        </div>}
                </div>}
        </div>
    }
    renderQuizResultsBreakdown(qdoc) {
        const { quizModalStatsTab, loadingTeachers, loadingStudents, viewingTeacherId, teachers, teacherAssessments, individualTeacherRetakeDict, teacherAssessmentRetakes, teacherAssessmentCountDict, students, studentsDict } = this.state
        const seg = (key, label) => {
            const active = quizModalStatsTab === key
            return <button type="button" aria-pressed={active} onClick={() => this.setState({ quizModalStatsTab: key })}
                style={{ padding: '8px 18px', fontSize: 16, fontWeight: 700, borderRadius: 8, cursor: 'pointer', border: active ? '1px solid #2d6a9f' : '1px solid #cbd5e1', backgroundColor: active ? '#2d6a9f' : '#fff', color: active ? '#fff' : '#334155' }}>{label}</button>
        }
        return <div style={{ marginTop: 36, paddingTop: 24, borderTop: '1px solid #e2e8f0' }}>
            <SectionTitle note="Compare how each teacher's class did, or choose a teacher to see each student's answers.">Results by teacher and student</SectionTitle>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>{seg('teachers', 'By teacher')}{seg('students', 'By student')}</div>

            {quizModalStatsTab === 'teachers'
                ? (loadingTeachers
                    ? <LoadingLine text="Loading teachers…" />
                    : (individualTeacherRetakeDict && teachers && teacherAssessments && teacherAssessments.length > 0)
                        ? <div style={{ overflowX: 'auto' }}><DOKTeacherBreakdown parentQuiz={qdoc} quiz={teacherAssessments[0]} teacherRetakes={individualTeacherRetakeDict} teachers={teachers} students={students} studentDict={studentsDict} /></div>
                        : <EmptyLine>No teacher has results for this quiz yet.</EmptyLine>)
                : null}

            {quizModalStatsTab === 'students' ? <div>
                <div style={{ maxWidth: 320, marginBottom: 16 }}>
                    <FieldLabel>Teacher</FieldLabel>
                    <Select value={viewingTeacherId || undefined} placeholder="Choose a teacher" size="large" style={{ width: '100%' }} onChange={tid => this.viewTeacherAssessment(tid)}>
                        {(teachers || []).map(t => <Select.Option key={t.id} value={t.id}>{t.firstName} {t.lastName}</Select.Option>)}
                    </Select>
                </div>
                {!viewingTeacherId
                    ? <EmptyLine>Choose a teacher to see each student's answers.</EmptyLine>
                    : loadingStudents
                        ? <LoadingLine text="Loading students…" />
                        : Object.keys(teacherAssessmentRetakes || {}).map((count, countIdx) => (teacherAssessmentRetakes[count] || []).map((tar, ri) => {
                            if (tar.teacherId !== viewingTeacherId) return null
                            return <div key={`qr-retake-${countIdx}-${ri}`} style={{ marginTop: 16, overflowX: 'auto' }}>
                                <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a', marginBottom: 6 }}>
                                    {teacherAssessmentCountDict[tar.teacherId] > 1 ? `Assessment #${countIdx + 1}` : 'Assessment'}
                                </div>
                                {students && studentsDict ? <DOKStandardBreakdown parentQuiz={qdoc} quiz={tar} students={students} studentDict={studentsDict} onlyShowCompleted={true} showProgress={false} /> : null}
                            </div>
                        }))}
            </div> : null}
        </div>
    }
    renderStandardsTable() {
        const { standardDetailData, standardDetailLoading, standardsList, standardsLoading, standardsCategory, standardsName } = this.state
        if (!standardsCategory || !standardsName) return <EmptyLine>There are no quiz results for this school yet.</EmptyLine>
        if (standardDetailLoading || standardsLoading) return <LoadingLine text="Loading standards…" />
        const stds = standardsList || [], data = standardDetailData || {}
        const rows = stds.map(std => {
            const key = std.standard || std.id, det = data[key]
            return { standard: key, description: std.title || std.description || std.standardDescription || '', quizGiven: isQuizGiven(det), label: key, detail: det }
        })
        rows.sort((a, b) => { if (a.quizGiven && !b.quizGiven) return -1; if (!a.quizGiven && b.quizGiven) return 1; return a.label.localeCompare(b.label) })

        const givenRows = rows.filter(r => r.quizGiven)
        let gv = 0, gs = 0, tg = 0
        givenRows.forEach(r => { gv += givenCount(r.detail); gs += (r.detail && r.detail.grade_sum) || 0; tg += (r.detail && r.detail.total_graded) || 0 })
        const avg = tg > 0 ? gs / tg : null

        return <div>
            <StatRow>
                <FlatStat label="Standards" value={fmt(rows.length)} />
                <FlatStat label="Standards with a quiz given" value={`${fmt(givenRows.length)} of ${fmt(rows.length)}`} />
                <FlatStat label="Quizzes given" value={fmt(gv)} />
                <FlatStat label="Average score"><AverageScore avg={avg} big /></FlatStat>
            </StatRow>

            <SectionTitle note="Standards with a quiz given are listed first." right={<AverageColorKey />}>Standards</SectionTitle>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 17 }}>
                <thead><tr>
                    <th style={flatTh('left')}>Standard</th>
                    <th style={flatTh('right', 150)}>Quizzes given</th>
                    <th style={{ ...flatTh('left', 190), paddingLeft: 28 }}>Average score</th>
                    <th style={flatTh('right', 140)} />
                </tr></thead>
                <tbody>
                    {rows.length === 0 && <tr><td colSpan={4} style={{ padding: 28, textAlign: 'center', color: '#334155' }}>No standards found for this subject and grade.</td></tr>}
                    {rows.map((r, i) => {
                        const rowKey = `standards:${r.standard}:${i}`
                        const open = !!this.state.expandedRows[rowKey]
                        const canExpand = r.quizGiven
                        const gvRow = givenCount(r.detail)
                        const desc = r.description ? (r.description.length > 140 ? r.description.substring(0, 140) + '…' : r.description) : ''
                        return <React.Fragment key={rowKey}>
                            <tr style={{ borderBottom: open ? 'none' : '1px solid #e2e8f0', cursor: canExpand ? 'pointer' : 'default' }}
                                onClick={canExpand ? () => this.toggleRow(rowKey) : undefined}
                                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8fafc'}
                                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                                <td style={{ padding: '12px' }}>
                                    <div style={{ fontWeight: 700, color: '#0f172a' }}>{r.standard}</div>
                                    {desc ? <div title={r.description} style={{ fontSize: 15, color: '#475569', lineHeight: 1.4, marginTop: 2, maxWidth: 520 }}>{desc}</div> : null}
                                </td>
                                <td style={{ padding: '12px', textAlign: 'right', color: '#1e293b' }}>{gvRow > 0 ? fmt(gvRow) : <span style={{ color: '#64748b' }}>None</span>}</td>
                                <td style={{ padding: '12px 12px 12px 28px' }}><QuizScore det={r.detail} /></td>
                                <td style={{ padding: '12px', textAlign: 'right' }}>{canExpand ? <DetailsLink open={open} showLabel="View results" hideLabel="Hide results" /> : null}</td>
                            </tr>
                            {open && <tr style={{ borderBottom: '1px solid #e2e8f0' }}><td colSpan={4} style={{ padding: '0 12px 20px' }}>
                                <RowDetail det={r.detail} hideCharts onResults={hasResults(r.detail) ? () => this.openResultsModal(r.standard, r.detail, standardsCategory, standardsName) : null} />
                            </td></tr>}
                        </React.Fragment>
                    })}
                </tbody>
            </table>
        </div>
    }
    // ---- Student Resources view: completed resources per school, all subjects ----
    renderResourcesView() {
        const { selectedSchoolId } = this.state
        const sd = this.schoolDocs().find(d => d.schoolId === selectedSchoolId)
        const schoolOptions = this.allSchoolsSorted()
        const schoolName = selectedSchoolId ? this.getSchoolName(selectedSchoolId) : ''
        const summary = sd ? this.schoolResourceSummary(sd) : { total: 0, bySubject: {} }

        const subjects = Object.keys(summary.bySubject)
            .filter(s => summary.bySubject[s].count > 0)
            .sort((a, b) => (subjectIndex(a) - subjectIndex(b)) || a.localeCompare(b))

        // Distinct grade levels with any practice, across all subjects.
        const gradeSet = new Set()
        subjects.forEach(subj => {
            const g = summary.bySubject[subj].grades || {}
            Object.keys(g).forEach(name => { if (g[name] > 0) gradeSet.add(name) })
        })
        const topSubject = subjects.length
            ? subjects.reduce((best, s) => summary.bySubject[s].count > summary.bySubject[best].count ? s : best, subjects[0])
            : null

        const fieldLabel = (text, htmlFor) => <label htmlFor={htmlFor} style={{ display: 'block', fontSize: 14, fontWeight: 700, color: '#64748b', marginBottom: 6 }}>{text}</label>
        const FLAT_SELECT = { ...SELECT_STYLE, fontSize: 17, borderColor: '#cbd5e1', width: '100%', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
        const stat = (label, value) => <div>
            <div style={{ fontSize: 26, fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>{value}</div>
            <div style={{ fontSize: 15, color: '#475569' }}>{label}</div>
        </div>
        const th = (align = 'left', width) => ({ textAlign: align, padding: '10px 12px', fontSize: 14, fontWeight: 700, color: '#64748b', borderBottom: '1px solid #cbd5e1', width })
        const subTh = (align) => ({ textAlign: align, padding: align === 'right' ? '6px 0' : '6px 32px 6px 0', fontSize: 14, fontWeight: 700, color: '#64748b', borderBottom: '1px solid #cbd5e1' })

        return <div>
            {/* Choices */}
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 360px)', gap: 16, marginBottom: 24 }}>
                <div style={{ minWidth: 0 }}>
                    {fieldLabel('School', 'practice-school')}
                    <select id="practice-school" title={schoolName} value={selectedSchoolId || ''} onChange={e => this.selectSchool(e.target.value)} style={FLAT_SELECT}>
                        {schoolOptions.length === 0 && <option value="" disabled>No schools available</option>}
                        {schoolOptions.map(s => <option key={s.id} value={s.id}>{this.getSchoolName(s.id)}</option>)}
                    </select>
                </div>
            </div>

            {/* Results */}
            {!this.state.schoolsLoaded
                ? <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '60px 0', justifyContent: 'center', fontSize: 18, color: '#334155' }}><Icon type="loading" style={{ fontSize: 24, color: '#2d6a9f' }} />Loading practice…</div>
                : <div>
                    {/* Which school this is */}
                    <div style={{ marginBottom: 16, paddingTop: 4, borderTop: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: 26, fontWeight: 800, color: '#0f172a', lineHeight: 1.25, marginTop: 16 }}>{schoolName}</div>
                        {this.state.selectedYear ? <div style={{ fontSize: 16, color: '#475569', marginTop: 2 }}>{schoolYearLabel(this.state.selectedYear)} school year</div> : null}
                    </div>

                    {(!sd || summary.total === 0)
                        ? <div style={{ padding: '40px 0', textAlign: 'center', fontSize: 17, color: '#334155', lineHeight: 1.6 }}>
                            Students at {schoolName || 'this school'} haven't done any practice yet this school year.<br />
                            Practice shows up here once students complete practice activities.
                        </div>
                        : <div>
                            <div style={{ display: 'flex', gap: 48, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 28, paddingBottom: 24, borderBottom: '1px solid #e2e8f0' }}>
                                {stat('Practice done', fmt(summary.total))}
                                {stat(subjects.length === 1 ? 'Subject' : 'Subjects', fmt(subjects.length))}
                                {stat(gradeSet.size === 1 ? 'Grade level' : 'Grade levels', fmt(gradeSet.size))}
                                {topSubject ? <div>
                                    <div style={{ lineHeight: 1.2, paddingBottom: 2 }}><SubjectName subject={topSubject} size={20} /></div>
                                    <div style={{ fontSize: 15, color: '#475569' }}>Most practiced subject</div>
                                </div> : null}
                            </div>

                            <div style={{ fontSize: 16, fontWeight: 700, color: '#1e293b', marginBottom: 10 }}>
                                Practice by subject
                            </div>

                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 17 }}>
                                <thead><tr>
                                    <th style={th('left')}>Subject</th>
                                    <th style={th('right', 180)}>Practice done</th>
                                    <th style={th('right', 170)} />
                                </tr></thead>
                                <tbody>
                                    {subjects.map(subj => {
                                        const row = summary.bySubject[subj]
                                        const grades = Object.keys(row.grades || {})
                                            .filter(g => row.grades[g] > 0)
                                            .sort((x, y) => (gradeIndex(x) - gradeIndex(y)) || x.localeCompare(y))
                                        const rowKey = `resource:${subj}`
                                        const open = !!this.state.expandedRows[rowKey]
                                        const canExpand = grades.length > 0
                                        return <React.Fragment key={rowKey}>
                                            <tr style={{ borderBottom: open ? 'none' : '1px solid #e2e8f0', cursor: canExpand ? 'pointer' : 'default' }}
                                                onClick={canExpand ? () => this.toggleRow(rowKey) : undefined}
                                                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8fafc'}
                                                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                                                <td style={{ padding: '12px' }}>
                                                    <SubjectName subject={subj} />
                                                    {grades.length ? <div style={{ fontSize: 15, color: '#64748b', marginTop: 4, marginLeft: 40 }}>
                                                        {grades.length === 1 ? '1 grade level' : `${grades.length} grade levels`}
                                                    </div> : null}
                                                </td>
                                                <td style={{ padding: '12px', textAlign: 'right', fontWeight: 800, color: '#0f172a' }}>{fmt(row.count)}</td>
                                                <td style={{ padding: '12px', textAlign: 'right' }}>{canExpand ? <DetailsLink open={open} showLabel="View grades" hideLabel="Hide grades" /> : null}</td>
                                            </tr>
                                            {open && <tr style={{ borderBottom: '1px solid #e2e8f0' }}><td colSpan={3} style={{ padding: '0 12px 20px' }}>
                                                <div style={{ marginLeft: 14, paddingLeft: 18, borderLeft: '3px solid #e2e8f0' }}>
                                                    <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>{subj} practice by grade</div>
                                                    <table style={{ borderCollapse: 'collapse', fontSize: 17 }}>
                                                        <thead><tr>
                                                            <th style={subTh('left')}>Grade</th>
                                                            <th style={subTh('right')}>Practice done</th>
                                                        </tr></thead>
                                                        <tbody>
                                                            {grades.map(g => <tr key={g} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                                <td style={{ padding: '8px 32px 8px 0', color: '#1e293b', fontWeight: 600 }}>{g}</td>
                                                                <td style={{ padding: '8px 0', textAlign: 'right', fontWeight: 800, color: '#0f172a' }}>{fmt(row.grades[g])}</td>
                                                            </tr>)}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </td></tr>}
                                        </React.Fragment>
                                    })}
                                </tbody>
                            </table>
                        </div>}
                </div>}
        </div>
    }
    renderStudentDetail(s) {
        const q = s.quizzes || {}, p = s.practice || {}
        const bySubj = (a, b) => (subjectIndex(a) - subjectIndex(b)) || a.localeCompare(b)
        const subjects = Object.keys(q.by_subject || {}).filter(k => q.by_subject[k].taken > 0).sort(bySubj)
        const practiceSubjects = Object.keys(p.by_subject || {}).filter(k => p.by_subject[k] > 0).sort(bySubj)
        const hasWeeks = Object.keys(q.by_week || {}).length > 0
        const heading = text => <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>{text}</div>
        const subTh = (align) => ({ textAlign: align, padding: align === 'right' ? '6px 0' : '6px 32px 6px 0', fontSize: 14, fontWeight: 700, color: '#64748b', borderBottom: '1px solid #cbd5e1' })
        return <div style={{ marginLeft: 50, paddingLeft: 18, borderLeft: '3px solid #e2e8f0' }}>
            <div style={{ marginBottom: 20 }}>
                {heading('Progress by subject')}
                {subjects.length === 0
                    ? <div style={{ color: '#334155' }}>No quizzes taken this school year.</div>
                    : <table style={{ borderCollapse: 'collapse', fontSize: 17 }}>
                        <thead><tr>
                            <th style={subTh('left')}>Subject</th>
                            <th style={subTh('left')}>Average score</th>
                            <th style={subTh('right')}>Quizzes</th>
                        </tr></thead>
                        <tbody>
                            {subjects.map(subj => {
                                const d = q.by_subject[subj]
                                return <tr key={subj} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '8px 32px 8px 0' }}><SubjectName subject={subj} /></td>
                                    <td style={{ padding: '8px 32px 8px 0' }}><AverageScore avg={d.grade_sum / d.taken} /></td>
                                    <td style={{ padding: '8px 0', textAlign: 'right', color: '#1e293b' }}>{fmt(d.taken)}</td>
                                </tr>
                            })}
                        </tbody>
                    </table>}
            </div>
            {practiceSubjects.length ? <div style={{ marginBottom: hasWeeks ? 20 : 0 }}>
                {heading('Practice done')}
                <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap' }}>
                    {practiceSubjects.map(k => <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                        <SubjectName subject={k} />
                        <span style={{ fontWeight: 800, color: '#0f172a' }}>{fmt(p.by_subject[k])}</span>
                    </span>)}
                </div>
            </div> : null}
            {hasWeeks ? <StudentQuizHistory key={s.id} byWeek={q.by_week} /> : null}
        </div>
    }

    // ---- Students view: one row per student, with plan badges ----
    renderStudentsView() {
        const { selectedSchoolId, studentFilter, studentSearch, studentSubject, studentSort, studentLimit, studentGrade } = this.state
        const schoolOptions = this.allSchoolsSorted()
        const all = selectedSchoolId ? this.state.studentDocsBySchool[selectedSchoolId] : []
        const loading = this.state.studentPicking
            || !this.state.yearsLoaded
            || this.studentAutoPick !== false
            || (!!selectedSchoolId && all === undefined)
        const list = all || []

        // Grade options from the students at this school (K → 12, then "No grade listed").
        const gradeKey = s => studentGradeLabel(s.grade)   // 'K', '1'..'12', or '—'
        const gradeOptions = [...new Set(list.map(gradeKey))]
            .sort((a, b) => studentGradeRank(a === '—' ? '' : a) - studentGradeRank(b === '—' ? '' : b))
        const activeGrade = studentGrade && studentGrade !== 'all' && gradeOptions.includes(studentGrade) ? studentGrade : 'all'
        const gradeOptionLabel = k => k === '—' ? 'No grade listed' : gradeWords(k)
        const gradeList = activeGrade === 'all' ? list : list.filter(s => gradeKey(s) === activeGrade)

        const rows = sortStudentRows(filterStudentRows(gradeList, studentFilter, studentSearch), studentSort, studentSubject)
        const shown = rows.slice(0, studentLimit)

        let taken = 0, gsum = 0, practice = 0, withPlans = 0
        rows.forEach(s => {
            const st = studentStats(s, studentSubject)
            taken += st.taken
            if (st.avg != null) gsum += st.avg * st.taken
            practice += st.practice
            if ((s.plans || []).length) withPlans++
        })
        const avg = taken > 0 ? gsum / taken : null

        const counts = { all: gradeList.length, plans: gradeList.filter(s => (s.plans || []).length).length }
        IEP_PLAN_TYPES.forEach(pt => { counts[pt] = gradeList.filter(s => (s.plans || []).includes(pt)).length })

        const SUBJECTS = ['All', 'ELA', 'Math', 'Science', 'Social Studies']
        const subjectWords = studentSubject === 'All' ? '' : ` in ${studentSubject}`
        const isFiltered = activeGrade !== 'all' || studentFilter !== 'all' || (studentSearch || '').trim() !== ''
        const TEACHER_TIP = 'These are the teacher(s) who have given quizzes to this student.'
        const schoolName = selectedSchoolId ? this.getSchoolName(selectedSchoolId) : ''
        // "2026–2027 school year · Grade 3 · IEP"
        const contextLine = [
            this.state.selectedYear ? `${schoolYearLabel(this.state.selectedYear)} school year` : null,
            activeGrade !== 'all' ? gradeOptionLabel(activeGrade) : null,
            studentFilter !== 'all' ? STUDENT_FILTER_LABEL(studentFilter) : null,
        ].filter(Boolean).join(' · ')

        const fieldLabel = (text, htmlFor) => <label htmlFor={htmlFor} style={{ display: 'block', fontSize: 14, fontWeight: 700, color: '#64748b', marginBottom: 6 }}>{text}</label>
        // Fills its grid column; long text is clipped instead of widening the select.
        const FLAT_SELECT = { ...SELECT_STYLE, fontSize: 17, borderColor: '#cbd5e1', width: '100%', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
        const tab = f => {
            const active = studentFilter === f.key
            return <button key={f.key} type="button" aria-pressed={active} onClick={() => this.setState({ studentFilter: f.key, studentLimit: 100, expandedRows: {} })}
                style={{ padding: '10px 2px', marginRight: 24, marginBottom: -1, fontSize: 17, fontWeight: active ? 800 : 500, border: 'none', borderBottom: active ? '3px solid #2d6a9f' : '3px solid transparent', background: 'none', cursor: 'pointer', color: active ? '#0f172a' : '#475569', whiteSpace: 'nowrap' }}>
                {f.label}{all ? <span style={{ color: '#64748b', fontWeight: 500 }}> ({fmt(counts[f.key] || 0)})</span> : null}
            </button>
        }
        const stat = (label, value) => <div>
            <div style={{ fontSize: 26, fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>{value}</div>
            <div style={{ fontSize: 15, color: '#475569' }}>{label}</div>
        </div>
        const th = (align = 'left', width) => ({ textAlign: align, padding: '10px 12px', fontSize: 14, fontWeight: 700, color: '#64748b', borderBottom: '1px solid #cbd5e1', width })

        return <div>
            {/* Choices */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 16, marginBottom: 18 }}>
                <div style={{ minWidth: 0 }}>
                    {fieldLabel('School', 'students-school')}
                    <select id="students-school" title={schoolName} value={selectedSchoolId || ''} onChange={e => this.onStudentsSchoolChange(e.target.value)} style={FLAT_SELECT}>
                        {schoolOptions.length === 0 && <option value="" disabled>No schools available</option>}
                        {schoolOptions.map(s => <option key={s.id} value={s.id}>{this.getSchoolName(s.id)}</option>)}
                    </select>
                </div>
                <div style={{ minWidth: 0 }}>
                    {fieldLabel('Grade', 'students-grade')}
                    <select id="students-grade" value={activeGrade} onChange={e => this.setState({ studentGrade: e.target.value, studentLimit: 100, expandedRows: {} })} style={FLAT_SELECT}>
                        <option value="all">All grades</option>
                        {gradeOptions.map(k => <option key={k} value={k}>{gradeOptionLabel(k)}</option>)}
                    </select>
                </div>
                <div style={{ minWidth: 0 }}>
                    {fieldLabel('Subject', 'students-subject')}
                    <select id="students-subject" value={studentSubject} onChange={e => this.setState({ studentSubject: e.target.value })} style={FLAT_SELECT}>
                        {SUBJECTS.map(s => <option key={s} value={s}>{s === 'All' ? 'All subjects' : s}</option>)}
                    </select>
                </div>
                <div style={{ minWidth: 0 }}>
                    {fieldLabel('Order', 'students-sort')}
                    <select id="students-sort" value={studentSort} onChange={e => this.setState({ studentSort: e.target.value })} style={FLAT_SELECT}>
                        <option value="name">Last name</option>
                        <option value="lowest">Lowest average</option>
                        <option value="quizzes">Most quizzes</option>
                        <option value="grade">Grade</option>
                    </select>
                </div>
            </div>
            <div style={{ marginBottom: 20 }}>
                {fieldLabel('Find a student', 'students-search')}
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%', maxWidth: 480 }}>
                    <Icon type="search" style={{ position: 'absolute', left: 12, fontSize: 18, color: '#64748b', pointerEvents: 'none' }} />
                    <input id="students-search" type="text" value={studentSearch} placeholder="Type a name"
                        onChange={e => this.setState({ studentSearch: e.target.value, studentLimit: 100 })}
                        style={{ width: '100%', padding: '10px 12px 10px 40px', fontSize: 17, borderRadius: 8, border: '1px solid #cbd5e1', color: '#1e293b' }} />
                </div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', borderBottom: '1px solid #e2e8f0', marginBottom: 24 }}>
                {STUDENT_FILTERS.map(tab)}
            </div>

            {/* Results */}
            {loading
                ? <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '60px 0', justifyContent: 'center', fontSize: 18, color: '#334155' }}><Icon type="loading" style={{ fontSize: 24, color: '#2d6a9f' }} />Loading students…</div>
                : list.length === 0
                    ? <div style={{ padding: '48px 0', textAlign: 'center', fontSize: 17, color: '#334155', lineHeight: 1.6 }}>No students at {schoolName || 'this school'} yet this school year.<br />Students appear here once they take a quiz, do practice, or have a plan.</div>
                    : <div>
                        {/* Which school this is */}
                        <div style={{ marginBottom: 16 }}>
                            <div style={{ fontSize: 26, fontWeight: 800, color: '#0f172a', lineHeight: 1.25 }}>{schoolName}</div>
                            {contextLine ? <div style={{ fontSize: 16, color: '#475569', marginTop: 2 }}>{contextLine}</div> : null}
                        </div>

                        <div style={{ display: 'flex', gap: 48, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 28, paddingBottom: 24, borderBottom: '1px solid #e2e8f0' }}>
                            {stat('Students', fmt(rows.length))}
                            {stat('Have a plan', fmt(withPlans))}
                            {stat('Quizzes taken' + subjectWords, fmt(taken))}
                            <div>
                                <div style={{ lineHeight: 1.2 }}><AverageScore avg={avg} big /></div>
                                <div style={{ fontSize: 15, color: '#475569' }}>Average score{subjectWords}</div>
                            </div>
                            {stat('Practice done' + subjectWords, fmt(practice))}
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
                            <div style={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
                                {isFiltered ? `Showing ${fmt(rows.length)} of ${fmt(list.length)} students` : `${fmt(rows.length)} students`}
                            </div>
                            <AverageColorKey />
                        </div>

                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 17 }}>
                            <thead><tr>
                                <th style={th('left')}>Student</th>
                                <th style={th('left', 170)}>Plans</th>
                                <th style={th('left', 180)}>Average score{subjectWords}</th>
                                <th style={th('right', 150)} />
                            </tr></thead>
                            <tbody>
                                {rows.length === 0 && <tr><td colSpan={4} style={{ padding: 28, textAlign: 'center', color: '#334155' }}>No students match. Try "All grades", "All Students", or clear the search box.</td></tr>}
                                {shown.map(s => {
                                    const st = studentStats(s, studentSubject)
                                    const rowKey = `student:${s.id}`
                                    const open = !!this.state.expandedRows[rowKey]
                                    const canExpand = ((s.quizzes && s.quizzes.taken) || 0) > 0 || ((s.practice && s.practice.count) || 0) > 0
                                    const gw = gradeWords(s.grade)
                                    const teachers = (s.teachers || []).join(', ')
                                    const teacherCount = (s.teachers || []).length
                                    const teacherTip = teacherCount === 1
                                        ? 'This is the teacher who gave quizzes to this student.'
                                        : 'These are the teachers who gave quizzes to this student.'
                                    return <React.Fragment key={rowKey}>
                                        <tr style={{ borderBottom: open ? 'none' : '1px solid #e2e8f0', cursor: canExpand ? 'pointer' : 'default' }}
                                            onClick={canExpand ? () => this.toggleRow(rowKey) : undefined}
                                            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8fafc'}
                                            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                                            <td style={{ padding: '12px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                    <StudentInitials s={s} />
                                                    <div>
                                                        <div style={{ fontWeight: 700, color: '#0f172a' }}>{studentName(s)}</div>
                                                        {(gw !== '—' || teachers) ? <div style={{ fontSize: 15, color: '#64748b', marginTop: 1 }}>
                                                            {gw !== '—' ? gw : null}
                                                            {gw !== '—' && teachers ? ' · ' : null}
                                                            {teachers ? <span title={teacherTip} style={{ cursor: 'help', borderBottom: '1px dotted #94a3b8' }}>{teachers}</span> : null}
                                                        </div> : null}
                                                    </div>
                                                </div>
                                            </td>
                                            <td style={{ padding: '12px' }}><FlatPlans plans={s.plans} /></td>
                                            <td style={{ padding: '12px' }}>
                                                <AverageScore avg={st.avg} />
                                                {st.taken ? <div style={{ fontSize: 14, color: '#64748b', marginTop: 2 }}>from {fmt(st.taken)} {st.taken === 1 ? 'quiz' : 'quizzes'}</div> : null}
                                            </td>
                                            <td style={{ padding: '12px', textAlign: 'right' }}>{canExpand ? <DetailsLink open={open} /> : null}</td>
                                        </tr>
                                        {open && <tr style={{ borderBottom: '1px solid #e2e8f0' }}><td colSpan={4} style={{ padding: '0 12px 20px' }}>
                                            {this.renderStudentDetail(s)}
                                        </td></tr>}
                                    </React.Fragment>
                                })}
                            </tbody>
                        </table>
                        {rows.length > shown.length ? <div style={{ textAlign: 'center', margin: '20px 0 8px' }}>
                            <button type="button" onClick={() => this.setState({ studentLimit: studentLimit + 100 })}
                                style={{ padding: '8px 4px', fontSize: 17, fontWeight: 700, border: 'none', background: 'none', cursor: 'pointer', color: '#1d4ed8' }}>
                                Show more students ({fmt(rows.length - shown.length)} more)
                            </button>
                        </div> : null}
                    </div>}
        </div>
    }

    // ---- Added to IEP & MTSS by School: quizzes + practice for students with plans ----
    renderIEPQuizSection(iepType) {
        if (!this.state.iepQuizLoaded) return null
        const isIF = !!this.props.instructionalFacilitatorBaseProps
        const groupOf = (groups) => (groups && groups[iepType]) || null
        const rows = this.state.iepQuizSchools.map(r => {
            const g = groupOf(r.groups)
            const q = (g && g.quizzes) || {}, res = (g && g.resources) || {}
            const totals = q.totals || {}
            return {
                sid: r.schoolId, label: this.getSchoolName(r.schoolId), totals,
                given: givenCount(totals), avg: getAvg(totals),
                practice: (res.totals && res.totals.count) || 0,
                bySubject: q.by_subject || {}, resBySubject: res.by_subject || {},
            }
        }).filter(r => r.given > 0 || r.practice > 0)
            .sort((a, b) => { const ra = schoolTypeRank(a.label), rb = schoolTypeRank(b.label); return ra !== rb ? ra - rb : a.label.localeCompare(b.label) })

        // Headline: district doc for admins, otherwise the sum of the rows shown.
        const dg = !isIF ? groupOf(this.state.iepQuizDistrict) : null
        const headTotals = dg ? ((dg.quizzes && dg.quizzes.totals) || {}) : rows.reduce((acc, r) => mergeWeekBucket(acc, r.totals), {})
        const headPractice = dg ? ((dg.resources && dg.resources.totals && dg.resources.totals.count) || 0) : rows.reduce((n, r) => n + r.practice, 0)
        const headAvg = getAvg(headTotals)
        const subTh = (align) => ({ textAlign: align, padding: align === 'right' ? '6px 0 6px 32px' : '6px 32px 6px 0', fontSize: 14, fontWeight: 700, color: '#64748b', borderBottom: '1px solid #cbd5e1' })

        return <div style={{ marginTop: 48, paddingTop: 28, borderTop: '1px solid #e2e8f0' }}>
            <ViewHeading title={`Quizzes and practice for students with ${iepType} plans`} sub="How these students are doing on quizzes and practice at each school." />
            <StatRow>
                <FlatStat label="Quizzes given" value={fmt(givenCount(headTotals))} />
                <FlatStat label="Average score">{headAvg == null ? <span style={{ fontSize: 17, fontWeight: 400, color: '#475569' }}>No quizzes yet</span> : <AverageScore avg={headAvg} big />}</FlatStat>
                <FlatStat label="Practice done" value={fmt(headPractice)} />
            </StatRow>

            <SectionTitle note={`Quizzes given counts quizzes that at least one student with a ${iepType} plan took.`} right={<AverageColorKey />}>Schools</SectionTitle>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 17 }}>
                <thead><tr>
                    <th style={flatTh('left')}>School</th>
                    <th style={flatTh('right', 150)}>Quizzes given</th>
                    <th style={{ ...flatTh('left', 170), paddingLeft: 28 }}>Average score</th>
                    <th style={flatTh('right', 150)}>Practice done</th>
                    <th style={flatTh('right', 140)} />
                </tr></thead>
                <tbody>
                    {rows.length === 0 && <tr><td colSpan={5} style={{ padding: 28, textAlign: 'center', color: '#334155' }}>No quiz or practice activity yet for students with {iepType} plans.</td></tr>}
                    {rows.map(r => {
                        const rowKey = `iepquiz:${iepType}:${r.sid}`
                        const open = !!this.state.expandedRows[rowKey]
                        const subjects = [...new Set([...Object.keys(r.bySubject), ...Object.keys(r.resBySubject)])]
                            .sort((a, b) => (subjectIndex(a) - subjectIndex(b)) || a.localeCompare(b))
                        const wk = {}
                        Object.entries(r.bySubject).forEach(([c, sD]) => { if (sD && sD.by_week && Object.keys(sD.by_week).length) wk[c] = sD.by_week })
                        const canExpand = subjects.length > 0
                        return <React.Fragment key={rowKey}>
                            <tr style={{ borderBottom: open ? 'none' : '1px solid #e2e8f0', cursor: canExpand ? 'pointer' : 'default' }}
                                onClick={canExpand ? () => this.toggleRow(rowKey) : undefined}
                                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8fafc'}
                                onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                                <td style={{ padding: '12px', fontWeight: 700, color: '#0f172a' }}>{r.label}</td>
                                <td style={{ padding: '12px', textAlign: 'right', color: '#1e293b' }}>{r.given > 0 ? fmt(r.given) : <span style={{ color: '#64748b' }}>None</span>}</td>
                                <td style={{ padding: '12px 12px 12px 28px' }}><AverageScore avg={r.avg} /></td>
                                <td style={{ padding: '12px', textAlign: 'right', color: '#1e293b', fontWeight: 600 }}>{r.practice > 0 ? fmt(r.practice) : <span style={{ color: '#64748b', fontWeight: 400 }}>None</span>}</td>
                                <td style={{ padding: '12px', textAlign: 'right' }}>{canExpand ? <DetailsLink open={open} /> : null}</td>
                            </tr>
                            {open && <tr style={{ borderBottom: '1px solid #e2e8f0' }}><td colSpan={5} style={{ padding: '0 12px 20px' }}>
                                <div style={{ marginLeft: 14, paddingLeft: 18, borderLeft: '3px solid #e2e8f0' }}>
                                    <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>Progress by subject</div>
                                    <table style={{ borderCollapse: 'collapse', fontSize: 17, marginBottom: Object.keys(wk).length ? 20 : 0 }}>
                                        <thead><tr>
                                            <th style={subTh('left')}>Subject</th>
                                            <th style={subTh('right')}>Quizzes</th>
                                            <th style={subTh('right')}>Average score</th>
                                            <th style={subTh('right')}>Practice done</th>
                                        </tr></thead>
                                        <tbody>
                                            {subjects.map(subj => {
                                                const sq = (r.bySubject[subj] && r.bySubject[subj].totals) || {}
                                                const sGiven = givenCount(sq)
                                                const sPractice = (r.resBySubject[subj] && r.resBySubject[subj].totals && r.resBySubject[subj].totals.count) || 0
                                                return <tr key={subj} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                    <td style={{ padding: '8px 32px 8px 0' }}><SubjectName subject={subj} /></td>
                                                    <td style={{ padding: '8px 0 8px 32px', textAlign: 'right', color: '#1e293b' }}>{sGiven > 0 ? fmt(sGiven) : <span style={{ color: '#64748b' }}>None</span>}</td>
                                                    <td style={{ padding: '8px 0 8px 32px', textAlign: 'right' }}><AverageScore avg={getAvg(sq)} /></td>
                                                    <td style={{ padding: '8px 0 8px 32px', textAlign: 'right', color: '#1e293b', fontWeight: 600 }}>{sPractice > 0 ? fmt(sPractice) : <span style={{ color: '#64748b', fontWeight: 400 }}>None</span>}</td>
                                                </tr>
                                            })}
                                        </tbody>
                                    </table>
                                    {Object.keys(wk).length ? <WeekTrends bySubject={wk} /> : null}
                                </div>
                            </td></tr>}
                        </React.Fragment>
                    })}
                </tbody>
            </table>
        </div>
    }

    // ---- Student report: print modal ----
    renderStudentReportModal() {
        const { studentReportModal, studentReportScope, studentReportFilter, studentReportHistory, studentReportPreparing } = this.state
        const isIF = !!this.props.instructionalFacilitatorBaseProps
        return <Modal title="Print student report" visible={studentReportModal} width={520} footer={null} onCancel={() => this.setState({ studentReportModal: false })}>
            <div style={{ fontSize: 16, color: '#334155', lineHeight: 1.5, marginBottom: 20 }}>
                A printable list of students for the {schoolYearLabel(this.state.selectedYear)} school year with their plans, average score, and practice done. Print it or save it as a PDF from the print window.
            </div>
            <div style={{ marginBottom: 18 }}>
                <FieldLabel htmlFor="student-report-scope">Schools</FieldLabel>
                <select id="student-report-scope" value={studentReportScope || 'district'} onChange={e => this.setState({ studentReportScope: e.target.value })} style={FLAT_SELECT_STYLE}>
                    <option value="district">{isIF ? 'All of my schools' : 'Whole district'}</option>
                    {this.allSchoolsSorted().map(s => <option key={s.id} value={s.id}>{this.getSchoolName(s.id)}</option>)}
                </select>
            </div>
            <div style={{ marginBottom: 18 }}>
                <FieldLabel htmlFor="student-report-filter">Students</FieldLabel>
                <select id="student-report-filter" value={studentReportFilter} onChange={e => this.setState({ studentReportFilter: e.target.value })} style={FLAT_SELECT_STYLE}>
                    {STUDENT_FILTERS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
                </select>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 17, color: '#1e293b', cursor: 'pointer' }}>
                <input type="checkbox" checked={!!studentReportHistory} onChange={e => this.setState({ studentReportHistory: e.target.checked })} style={{ width: 18, height: 18 }} />
                Include each student's scores by subject
            </label>
            <div style={{ marginTop: 20, textAlign: 'right' }}>
                <ReactToPrint
                    trigger={() => <Button type="primary" size="large" disabled={studentReportPreparing}>
                        <Icon type={studentReportPreparing ? 'loading' : 'printer'} />
                        {studentReportPreparing ? 'Preparing report…' : 'Print student report'}
                    </Button>}
                    content={() => this.studentReportRef}
                    onBeforeGetContent={this.prepareStudentReport}
                    onBeforePrint={() => this.setState({ studentReportPreparing: false })}
                    onAfterPrint={() => this.setState({ studentReportPrinting: false, studentReportPreparing: false, studentReportModal: false })}
                    onPrintError={() => this.setState({ studentReportPrinting: false, studentReportPreparing: false })}
                    pageStyle={REPORT_PAGE_STYLE}
                    documentTitle={`${this.reportDistrictName()} - ${schoolYearLabel(this.state.selectedYear)} Student Report`}
                />
            </div>
        </Modal>
    }

    // ---- Student report: printed content (rendered off-screen while printing) ----
    renderStudentReportHistory(rows) {
        const withQuizzes = rows.filter(s => s.quizzes && s.quizzes.taken > 0)
        if (!withQuizzes.length) return null
        const bySubj = (a, b) => (subjectIndex(a) - subjectIndex(b)) || a.localeCompare(b)
        return <div>
            {this.reportHeading('Scores by subject', "Each student's average score in each subject this school year.")}
            {withQuizzes.map(s => {
                const subj = s.quizzes.by_subject || {}
                return <div key={s.id} className="report-avoid-break" style={{ padding: '8px 0', borderBottom: '1px solid #e2e8f0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>{studentName(s)}</span>
                        <span style={{ fontSize: 13, color: '#475569' }}>{gradeWords(s.grade) !== '—' ? gradeWords(s.grade) : ''}</span>
                        <PrintPlans plans={s.plans} />
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 22px' }}>
                        {Object.keys(subj).filter(k => subj[k].taken > 0).sort(bySubj).map(k => <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 14, color: '#1e293b' }}>
                            <b>{k}</b> {fmt(subj[k].taken)} {subj[k].taken === 1 ? 'quiz' : 'quizzes'}<MiniScore avg={subj[k].grade_sum / subj[k].taken} />
                        </span>)}
                    </div>
                </div>
            })}
        </div>
    }
    renderStudentReport() {
        const pages = this.state.studentReportPages || []
        const filterLabel = STUDENT_FILTER_LABEL(this.state.studentReportFilter)
        const scope = this.state.studentReportScope || 'district'
        const isIF = !!this.props.instructionalFacilitatorBaseProps
        const scopeLabel = scope === 'district' ? (isIF ? 'Your schools' : 'All schools') : this.getSchoolName(scope)
        const total = pages.reduce((n, p) => n + p.rows.length, 0)
        const cols = [
            { label: 'Student' }, { label: 'Grade' }, { label: 'Plans' }, { label: 'Teachers' },
            { label: 'Quizzes', align: 'right' }, { label: 'Average score', align: 'right' }, { label: 'Practice done', align: 'right' },
        ]
        return <div style={{ width: 1000, background: '#fff', color: '#1e293b' }}>
            <div style={{ borderBottom: '3px solid #2d6a9f', paddingBottom: 14, marginBottom: 18 }}>
                <img src={`${window.location.origin}/dotit-logo-color-new.png`} alt="logo" height="50" style={{ display: 'block', marginBottom: 22 }} />
                <div style={{ fontSize: 30, fontWeight: 800, color: '#0f172a' }}>{this.reportDistrictName()}</div>
                <div style={{ fontSize: 18, color: '#334155' }}>Student report · {schoolYearLabel(this.state.selectedYear)} school year</div>
                <div style={{ fontSize: 14, color: '#475569', marginTop: 4 }}>{scopeLabel} · {filterLabel} · {fmt(total)} students · Printed {new Date().toLocaleDateString()}</div>
            </div>
            <PrintColorKey />
            {pages.length === 0 ? <div style={{ marginTop: 24, fontSize: 15, color: '#334155' }}>No students match the selected filter.</div> : null}
            {pages.map((p, i) => <div key={p.sid} className={i > 0 ? 'report-page' : undefined} style={i === 0 ? { marginTop: 24 } : undefined}>
                <div style={{ borderBottom: '1px solid #cbd5e1', paddingBottom: 8, marginBottom: 4 }}>
                    <div style={{ fontSize: 26, fontWeight: 800, color: '#0f172a' }}>{p.name}</div>
                    <div style={{ fontSize: 14, color: '#475569' }}>{fmt(p.rows.length)} students · {filterLabel} · {schoolYearLabel(this.state.selectedYear)} school year</div>
                </div>
                {this.reportHeading('Students')}
                {this.reportTable(cols, p.rows.map(s => {
                    const st = studentStats(s, 'All')
                    return [
                        studentName(s),
                        gradeWords(s.grade),
                        <PrintPlans plans={s.plans} />,
                        (s.teachers || []).join(', ') || '—',
                        st.taken ? fmt(st.taken) : '—',
                        <MiniScore avg={st.avg} />,
                        st.practice ? fmt(st.practice) : '—',
                    ]
                }))}
                {this.state.studentReportHistory ? this.renderStudentReportHistory(p.rows) : null}
            </div>)}
        </div>
    }
    // ---- Pacing Guide view: same weekly card layout, with quiz-given + teacher activity per week ----
    renderPacingCard(item, weekNumber) {
        // Prefer the per-standard pacingGuideStats data (given / avg / teachers for THIS standard);
        // fall back to the grade-level weekly rollup if those stats haven't been generated yet.
        const { info, usingPerStandard } = this.pacingItemInfo(item, weekNumber)
        const given = info.given || 0
        const badge = given > 0
            ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 999, backgroundColor: '#eff6ff', color: '#2563eb', fontWeight: 700, fontSize: 15, border: '1px solid #bfdbfe', whiteSpace: 'nowrap' }}><Icon type="check-circle" /> {fmt(given)} {given === 1 ? 'quiz' : 'quizzes'} given</span>
            : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 999, backgroundColor: '#fffbeb', color: '#d97706', fontWeight: 700, fontSize: 15, border: '1px solid #fde68a', whiteSpace: 'nowrap' }}><Icon type="edit" /> No quiz given yet</span>
        return <div key={item.standard + '-' + weekNumber} style={{ background: '#fff', border: '1px solid #e8eef5', borderRadius: 16, padding: '18px 20px', marginBottom: 14, boxShadow: 'rgba(100, 100, 111, 0.12) 0px 4px 14px 0px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#1e293b' }}>{item.standard}</div>
                {item.isPriority ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#722ed1', color: '#fff', borderRadius: 999, padding: '4px 12px', fontSize: 14, fontWeight: 700 }}><Icon type='pushpin' /> Priority</span> : ''}
                <div style={{ marginLeft: 'auto' }}>{badge}</div>
            </div>
            <div style={{ fontSize: 17, color: '#334155', lineHeight: 1.5 }}>{item.title}</div>
            <div style={{ marginTop: 12, padding: '12px 14px', background: '#f0f7ff', border: '1px solid #cfe3ff', borderRadius: 12, fontSize: 16, color: '#334155' }}>
                <div style={{ fontWeight: 800, color: '#1e293b', marginBottom: info.teachers.length ? 8 : 0 }}>
                    {fmt(given)} {given === 1 ? 'quiz' : 'quizzes'} given {usingPerStandard ? 'for this standard' : 'this week'}{info.avg != null ? <span style={{ fontWeight: 700, color: '#475569' }}> · average score {pct(info.avg)}</span> : ''}
                </div>
                {info.teachers.length
                    ? <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {info.teachers.map((t, ti) => <div key={ti} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Icon type="user" style={{ color: '#2d6a9f' }} /><span style={{ fontWeight: 600 }}>{t.name}</span>{t.grade ? <span style={{ fontSize: 13, color: '#94a3b8' }}>({t.grade})</span> : null}</span>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, whiteSpace: 'nowrap' }}>
                                {t.avg != null ? <ScoreChip avg={t.avg} /> : null}
                                <span style={{ fontWeight: 800, color: '#1e293b' }}>{fmt(t.given)} {t.given === 1 ? 'quiz' : 'quizzes'}</span>
                            </span>
                        </div>)}
                    </div>
                    : <div style={{ color: '#94a3b8' }}>{given > 0 ? 'Teacher-level detail isn\u2019t available for this standard.' : 'No teacher has given this quiz yet.'}</div>}
            </div>
        </div>
    }
    renderPacingQuarterHero(quarter) {
        const color = quarter === 1 ? '#3fa672' : quarter === 2 ? '#faa916' : quarter === 3 ? '#722ed1' : '#1098f7'
        const start = (quarter - 1) * 9 + 1, end = quarter * 9
        return <div id={'PG-Quarter-' + quarter} style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginTop: quarter === 1 ? 8 : 40, paddingBottom: 8, borderBottom: `3px solid ${color}` }}>
            <span style={{ fontSize: 24, fontWeight: 800, color: '#0f172a' }}>Quarter {quarter}</span>
            <span style={{ fontSize: 16, color: '#475569' }}>School-year weeks {start}–{end}</span>
        </div>
    }
    renderPacingView() {
        const { selectedSchoolId, pacingCategory, pacingName, pgLoading, pgStatsLoading, pgGuide, pgErr, pgYear } = this.state
        const schoolOptions = this.allSchoolsSorted()
        const schoolName = selectedSchoolId ? this.getSchoolName(selectedSchoolId) : ''
        const cats = selectedSchoolId ? this.getCategories(selectedSchoolId) : []
        const names = (selectedSchoolId && pacingCategory) ? this.getStandardNames(selectedSchoolId, pacingCategory) : []
        const sub = [
            pacingName && pacingCategory ? `${pacingName} ${pacingCategory} pacing guide` : null,
            this.state.selectedYear ? `${schoolYearLabel(this.state.selectedYear)} school year` : null,
        ].filter(Boolean).join(' · ')

        const filters = <FilterGrid max={780}>
            <FlatSelectField id="pacing-school" label="School" title={schoolName} value={selectedSchoolId || ''} onChange={this.onPacingSchoolChange}>
                {schoolOptions.length === 0 && <option value="" disabled>No schools available</option>}
                {schoolOptions.map(s => <option key={s.id} value={s.id}>{this.getSchoolName(s.id)}</option>)}
            </FlatSelectField>
            <FlatSelectField id="pacing-subject" label="Subject" value={pacingCategory || ''} onChange={this.onPacingCategoryChange}>
                {cats.length === 0 && <option value="" disabled>None available</option>}
                {cats.map(c => <option key={c} value={c}>{c}</option>)}
            </FlatSelectField>
            <FlatSelectField id="pacing-grade" label="Grade" value={pacingName || ''} onChange={this.onPacingNameChange}>
                {names.length === 0 && <option value="" disabled>None available</option>}
                {names.map(n => <option key={n} value={n}>{n}</option>)}
            </FlatSelectField>
        </FilterGrid>

        let body
        if (pgLoading || pgStatsLoading || !this.state.schoolsLoaded) {
            body = <LoadingLine text="Loading pacing guide…" />
        } else if (!selectedSchoolId || !pacingCategory || !pacingName) {
            body = <EmptyLine>There's no quiz data for this school yet, so there's no pacing guide to show.</EmptyLine>
        } else if (pgErr) {
            body = <EmptyLine><span style={{ color: '#b91c1c' }}>{pgErr}</span></EmptyLine>
        } else if (!pgGuide || !pgGuide.weeks) {
            body = <EmptyLine>No pacing guide was found for {pacingName} {pacingCategory}{pgYear != null ? ` for ${pgYear}` : ''}.</EmptyLine>
        } else {
            const weeks = pgGuide.weeks
            const weekNums = Object.keys(weeks).map(Number).filter(n => !isNaN(n) && n > 0).sort((a, b) => a - b)
            if (weekNums.length === 0) {
                body = <EmptyLine>This pacing guide doesn't have any weeks yet.</EmptyLine>
            } else {
                let total = 0, givenStd = 0
                weekNums.forEach(w => (weeks[String(w)] || []).forEach(item => {
                    total++
                    if ((this.pacingItemInfo(item, w).info.given || 0) > 0) givenStd++
                }))
                let prevQuarter = null
                body = <div>
                    <StatRow>
                        <FlatStat label="Weeks in the guide" value={fmt(weekNums.length)} />
                        <FlatStat label="Standards in the guide" value={fmt(total)} />
                        <FlatStat label="Standards with a quiz given" value={`${fmt(givenStd)} of ${fmt(total)}`} />
                    </StatRow>
                    {weekNums.map(weekNumber => {
                        const items = weeks[String(weekNumber)] || []
                        const quarter = Math.min(4, Math.ceil(weekNumber / 9))
                        const weekInQuarter = ((weekNumber - 1) % 9) + 1
                        const showQuarter = quarter !== prevQuarter
                        prevQuarter = quarter
                        return <div key={'pg-week-' + weekNumber}>
                            {showQuarter ? this.renderPacingQuarterHero(quarter) : null}
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginTop: 22, marginBottom: 2 }}>
                                <span style={{ fontSize: 20, fontWeight: 800, color: '#0f172a' }}>Week {weekInQuarter}</span>
                                <span style={{ fontSize: 15, color: '#64748b' }}>School-year week {weekNumber}</span>
                            </div>
                            {items.length === 0
                                ? <div style={{ color: '#475569', fontSize: 16, padding: '10px 0', borderBottom: '1px solid #e2e8f0' }}>No standards listed for this week.</div>
                                : items.map(item => this.renderPacingCard(item, weekNumber))}
                        </div>
                    })}
                </div>
            }
        }
        return <div>
            {filters}
            <ViewHeading title={schoolName} sub={sub} />
            {body}
        </div>
    }
    renderIEPTable(rows, firstLabel, firstIcon, expandable = false) {
        const sorted = [...rows].sort((a, b) => {
            const ar = this.iepRate(a.t), br = this.iepRate(b.t)
            const av = ar == null ? -Infinity : ar, bv = br == null ? -Infinity : br
            if (av !== bv) return bv - av
            return (a.label || '').localeCompare(b.label || '')
        })
        const colCount = expandable ? 5 : 4
        const onTrackCell = rate => rate == null ? <span style={{ color: '#64748b' }}>None</span> : <AverageScore avg={rate} />
        return <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 17 }}>
            <thead><tr>
                <th style={flatTh('left')}>{firstLabel}</th>
                <th style={flatTh('right', 180)}>Students with plans</th>
                <th style={flatTh('right', 150)}>Measurements</th>
                <th style={{ ...flatTh('left', 130), paddingLeft: 28 }}>On track</th>
                {expandable ? <th style={flatTh('right', 140)} /> : null}
            </tr></thead>
            <tbody>
                {sorted.length === 0 && <tr><td colSpan={colCount} style={{ padding: 28, textAlign: 'center', color: '#334155' }}>No plans of this type yet.</td></tr>}
                {sorted.map((r, i) => {
                    const rate = this.iepRate(r.t)
                    const students = iepStudentCount(r.t)
                    const cells = <>
                        <td style={{ padding: '12px', fontWeight: 700, color: '#0f172a' }}>{r.label}</td>
                        <td style={{ padding: '12px', textAlign: 'right', color: '#1e293b', fontWeight: 600 }}>{students > 0 ? fmt(students) : <span style={{ color: '#64748b' }}>None</span>}</td>
                        <td style={{ padding: '12px', textAlign: 'right', color: '#1e293b' }}>{r.t.measurements > 0 ? fmt(r.t.measurements) : <span style={{ color: '#64748b' }}>None</span>}</td>
                        <td style={{ padding: '12px 12px 12px 28px' }}>{onTrackCell(rate)}</td>
                    </>
                    if (!expandable) {
                        return <tr key={r.key + ':' + i} style={{ borderBottom: '1px solid #e2e8f0' }}
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8fafc'}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                            {cells}
                        </tr>
                    }
                    const rowKey = `iepteacher:${r.key}`
                    const open = !!this.state.expandedRows[rowKey]
                    const canExpand = students > 0
                    return <React.Fragment key={rowKey + ':' + i}>
                        <tr style={{ borderBottom: open ? 'none' : '1px solid #e2e8f0', cursor: canExpand ? 'pointer' : 'default' }}
                            onClick={canExpand ? () => this.toggleIEPTeacherRow(r.key) : undefined}
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8fafc'}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                            {cells}
                            <td style={{ padding: '12px', textAlign: 'right' }}>{canExpand ? <DetailsLink open={open} showLabel="View plans" hideLabel="Hide plans" /> : null}</td>
                        </tr>
                        {open && <tr style={{ borderBottom: '1px solid #e2e8f0' }}><td colSpan={colCount} style={{ padding: '0 12px 20px' }}>
                            {this.renderIEPTeacherExpansion(r)}
                        </td></tr>}
                    </React.Fragment>
                })}
            </tbody>
        </table>
    }
    renderIEPView(mode) {
        const { selectedSchoolId, iepType, iepSchools, iepLoaded } = this.state
        const PLAN_TYPES = ['IEP', 'Tier 2', 'Tier 3']
        const isIF = !!this.props.instructionalFacilitatorBaseProps
        const schools = this.schoolDocs()
        const schoolOptions = this.allSchoolsSorted()
        const empty = { plans: 0, students: 0, measurements: 0, on_track: 0, total: 0 }
        const yearText = this.state.selectedYear ? `${schoolYearLabel(this.state.selectedYear)} school year` : null
        const sub = [`${iepType} plans`, yearText].filter(Boolean).join(' · ')
        const onTrackNote = 'On track is the share of plans on track to meet their goal, based on the trend of their measurements. Plans with no measurements yet count as on track.'

        const planTab = t => {
            const active = iepType === t
            return <button key={t} type="button" aria-pressed={active} onClick={() => this.setState({ iepType: t, expandedRows: {} })}
                style={{ padding: '10px 2px', marginRight: 28, marginBottom: -1, fontSize: 17, fontWeight: active ? 800 : 500, border: 'none', borderBottom: active ? '3px solid #2d6a9f' : '3px solid transparent', background: 'none', cursor: 'pointer', color: active ? '#0f172a' : '#475569', whiteSpace: 'nowrap' }}>
                {t}
            </button>
        }
        const planTabs = <div style={{ marginBottom: 24 }}>
            <FieldLabel>Plan type</FieldLabel>
            <div style={{ display: 'flex', flexWrap: 'wrap', borderBottom: '1px solid #e2e8f0' }}>{PLAN_TYPES.map(planTab)}</div>
        </div>
        const statsFor = t => {
            const rate = this.iepRate(t || empty)
            return <StatRow>
                <FlatStat label="Students with plans" value={fmt(iepStudentCount(t || empty))} />
                <FlatStat label="Measurements" value={fmt((t && t.measurements) || 0)} />
                <FlatStat label="On track">{rate == null ? <span style={{ fontSize: 17, fontWeight: 400, color: '#475569' }}>None</span> : <AverageScore avg={rate} big />}</FlatStat>
            </StatRow>
        }

        // ---- BY SCHOOL ----
        if (mode === 'school') {
            const schoolRows = iepSchools.map(s => {
                const sd = schools.find(d => d.schoolId === s.schoolId)
                const name = (s.iep && s.iep.name) || (sd && sd.quizzes ? sd.quizzes.name : this.getSchoolName(s.schoolId))
                const t = (s.iep && s.iep.totals && s.iep.totals[iepType]) || empty
                return { key: s.schoolId, label: name, t }
            }).filter(r => iepStudentCount(r.t) > 0)

            // Sum of the rows shown. IFs only ever see their own schools, so this is their headline.
            const overall = { plans: 0, students: 0, measurements: 0, on_track: 0, total: 0 }
            schoolRows.forEach(r => {
                overall.plans += r.t.plans || 0
                overall.students += iepStudentCount(r.t)
                overall.measurements += r.t.measurements || 0
                overall.on_track += r.t.on_track || 0
                overall.total += r.t.total || 0
            })
            // Admins: use the district doc (students de-duplicated across schools) when it's loaded.
            const districtT = (!isIF && this.state.iepDistrict && this.state.iepDistrict.iep && this.state.iepDistrict.iep.totals)
                ? this.state.iepDistrict.iep.totals[iepType]
                : null

            return <div>
                {planTabs}
                {!iepLoaded ? <LoadingLine text="Loading plans…" /> : <div>
                    <ViewHeading title={isIF ? 'Your schools' : this.reportDistrictName()} sub={sub} />
                    {statsFor(districtT || overall)}
                    <SectionTitle note={`Highest on-track rate first. ${onTrackNote}`} right={<AverageColorKey />}>Schools</SectionTitle>
                    {this.renderIEPTable(schoolRows, 'School')}
                    {this.renderIEPQuizSection(iepType)}
                </div>}
            </div>
        }

        // ---- BY TEACHER ----
        const schoolName = selectedSchoolId ? this.getSchoolName(selectedSchoolId) : ''
        const sIep = iepSchools.find(s => s.schoolId === selectedSchoolId) || null
        const schoolTotals = (sIep && sIep.iep && sIep.iep.totals && sIep.iep.totals[iepType]) || empty
        const teacherRows = (sIep && sIep.iep && sIep.iep.teachers_summary)
            ? Object.keys(sIep.iep.teachers_summary).map(tid => {
                const ts = sIep.iep.teachers_summary[tid]
                return { key: tid, label: `${ts.lastName}, ${ts.firstName}`, firstName: ts.firstName, lastName: ts.lastName, t: (ts.totals && ts.totals[iepType]) || empty }
            }).filter(r => iepStudentCount(r.t) > 0)
            : []

        return <div>
            <FilterGrid max={380}>
                <FlatSelectField id="iep-teacher-school" label="School" title={schoolName} value={selectedSchoolId || ''} onChange={this.onIEPSchoolChange}>
                    {schoolOptions.length === 0 && <option value="" disabled>No schools available</option>}
                    {schoolOptions.map(s => <option key={s.id} value={s.id}>{this.getSchoolName(s.id)}</option>)}
                </FlatSelectField>
            </FilterGrid>
            {planTabs}
            {!iepLoaded ? <LoadingLine text="Loading plans…" /> : <div>
                <ViewHeading title={schoolName} sub={sub} />
                {!sIep
                    ? <EmptyLine>No IEP or MTSS plans recorded at {schoolName || 'this school'} this school year.</EmptyLine>
                    : <div>
                        {statsFor(schoolTotals)}
                        <SectionTitle note={`Highest on-track rate first. Click a teacher to see each student's goals. ${onTrackNote}`} right={<AverageColorKey />}>Teachers</SectionTitle>
                        {this.renderIEPTable(teacherRows, 'Teacher', null, true)}
                    </div>}
            </div>}
        </div>
    }
    renderGamesLoading() { return <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 24px' }}><Icon type="loading" style={{ fontSize: 32, marginBottom: 16, color: '#3b82f6' }} /><div style={{ fontSize: 20, fontWeight: 700, color: '#64748b' }}>Loading...</div></div> }
    gameSchoolSelector(onChange) {
        const schools = this.sortedGameSchools()
        return <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}><span style={{ fontSize: 15, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>School</span>
            <select value={this.state.selectedSchoolId || ''} onChange={e => onChange(e.target.value)} style={SELECT_STYLE}>
                {schools.length === 0 && <option value="" disabled>No data available</option>}
                {schools.map(s => <option key={s.id} value={s.id}>{this.getSchoolName(s.id)}</option>)}
            </select></div>
    }
    // School-wide per-category totals, summed from every teacher's categories in the base doc.
    schoolCategoryTotals(base) {
        const out = {}
        const bt = (base && base.by_teacher) || {}
        Object.values(bt).forEach(t => {
            const cats = (t && t.categories) || {}
            Object.entries(cats).forEach(([rawCat, c]) => {
                const cat = (rawCat || '').trim()
                if (!cat) return
                const grade = (c.standardName || '').trim()
                const key = cat + '||' + grade   // split by subject AND grade
                if (!out[key]) out[key] = { category: cat, standardName: grade, playtimeSeconds: 0, correct: 0, incorrect: 0, sessions: 0 }
                out[key].playtimeSeconds += c.playtimeSeconds || 0
                out[key].correct += c.correct || 0
                out[key].incorrect += c.incorrect || 0
                out[key].sessions += c.sessions || 0
            })
        })
        return out
    }
    // ---- Games by School: one row per school, expand for per-subject split ----
    renderGamesSchoolView() {
        const isIF = !!this.props.instructionalFacilitatorBaseProps
        const yearLine = this.state.selectedYear ? `${schoolYearLabel(this.state.selectedYear)} school year` : ''
        const sortCats = (a, b) => (subjectIndex(a.category) - subjectIndex(b.category))
            || (gradeIndex(a.standardName) - gradeIndex(b.standardName))
            || (a.standardName || '').localeCompare(b.standardName || '')

        // Each school's headline is summed from the same by_teacher data as its breakdown,
        // so the row always equals the sum of its subject rows.
        const rows = this.state.gameBaseDocs.map(d => ({
            d,
            name: this.getSchoolName(d.schoolId),
            tot: sumGameStats(Object.values(d.by_teacher || {}).map(sumTeacherTotals)),
        })).sort((a, b) => {
            const ra = schoolTypeRank(a.name), rb = schoolTypeRank(b.name)
            return ra !== rb ? ra - rb : a.name.localeCompare(b.name)
        })
        const all = sumGameStats(rows.map(r => r.tot))
        const playing = rows.filter(r => r.tot.playtimeSeconds > 0 || r.tot.sessions > 0).length

        return <div>
            {!this.state.gamesLoaded ? <LoadingLine text="Loading games…" /> : <div>
                <ViewHeading title={isIF ? 'Your schools' : this.reportDistrictName()} sub={yearLine} />
                {rows.length === 0
                    ? <EmptyLine>No game activity has been recorded yet this school year.</EmptyLine>
                    : <div>
                        <StatRow>
                            <FlatStat label="Schools playing games" value={fmt(playing)} />
                            <FlatStat label="Total playtime" value={fmtPlaytime(all.playtimeSeconds)} />
                            <FlatStat label="Game sessions" value={fmt(all.sessions)} />
                            <FlatStat label="Average score"><AverageScore avg={gameAccuracy(all)} big /></FlatStat>
                        </StatRow>

                        <SectionTitle note="Average score is the percentage of game questions answered correctly." right={<AverageColorKey />}>Games by school</SectionTitle>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 17 }}>
                            <thead><tr>
                                <th style={flatTh('left')}>School</th>
                                <th style={flatTh('right', 160)}>Playtime</th>
                                <th style={flatTh('right', 120)}>Sessions</th>
                                <th style={{ ...flatTh('left', 170), paddingLeft: 28 }}>Average score</th>
                                <th style={flatTh('right', 140)} />
                            </tr></thead>
                            <tbody>
                                {rows.map(({ d, name, tot }, i) => {
                                    const rowKey = `game-school:${d.schoolId}:${i}`
                                    const open = !!this.state.expandedRows[rowKey]
                                    const cats = Object.values(this.schoolCategoryTotals(d)).sort(sortCats)
                                    const canExpand = cats.length > 0
                                    return <React.Fragment key={rowKey}>
                                        <tr style={{ borderBottom: open ? 'none' : '1px solid #e2e8f0', cursor: canExpand ? 'pointer' : 'default' }}
                                            onClick={canExpand ? () => this.toggleRow(rowKey) : undefined}
                                            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8fafc'}
                                            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                                            <td style={{ padding: '12px', fontWeight: 700, color: '#0f172a' }}>{name}</td>
                                            <td style={{ padding: '12px', textAlign: 'right', color: '#1e293b', fontWeight: 600 }}>{fmtPlaytime(tot.playtimeSeconds)}</td>
                                            <td style={{ padding: '12px', textAlign: 'right', color: '#1e293b' }}>{tot.sessions ? fmt(tot.sessions) : <span style={{ color: '#64748b' }}>None</span>}</td>
                                            <td style={{ padding: '12px 12px 12px 28px' }}><AverageScore avg={gameAccuracy(tot)} /></td>
                                            <td style={{ padding: '12px', textAlign: 'right' }}>{canExpand ? <DetailsLink open={open} /> : null}</td>
                                        </tr>
                                        {open && <tr style={{ borderBottom: '1px solid #e2e8f0' }}><td colSpan={5} style={{ padding: '0 12px 20px' }}>
                                            <GameBreakdown rows={cats} />
                                        </td></tr>}
                                    </React.Fragment>
                                })}
                            </tbody>
                        </table>
                    </div>}
            </div>}
        </div>
    }
    // ---- Games by Teacher: school selector, one row per teacher, expand for subjects ----
    // ---- Games by Teacher: school selector, one row per teacher, expand for subjects ----
    renderGamesTeacherView() {
        const sid = this.state.selectedSchoolId
        const schools = this.sortedGameSchools()
        const schoolName = sid ? this.getSchoolName(sid) : ''
        const yearLine = this.state.selectedYear ? `${schoolYearLabel(this.state.selectedYear)} school year` : ''
        const base = this.state.gameBaseDocs.find(d => d.schoolId === sid)
        const byTeacher = (base && base.by_teacher) || {}
        const rows = Object.entries(byTeacher)
            .map(([tid, t]) => ({ id: tid, label: `${t.lastName || ''}, ${t.firstName || ''}`.replace(/^, |, $/g, '').trim() || 'Unknown teacher', t, tot: sumTeacherTotals(t) }))
            .sort((a, b) => (b.tot.playtimeSeconds - a.tot.playtimeSeconds) || a.label.localeCompare(b.label))
        const all = sumGameStats(rows.map(r => r.tot))

        return <div>
            <FilterGrid max={380}>
                <FlatSelectField id="games-teacher-school" label="School" title={schoolName} value={sid || ''} onChange={this.onGamesSchoolChange}>
                    {schools.length === 0 && <option value="" disabled>No schools available</option>}
                    {schools.map(s => <option key={s.id} value={s.id}>{this.getSchoolName(s.id)}</option>)}
                </FlatSelectField>
            </FilterGrid>

            {!this.state.gamesLoaded ? <LoadingLine text="Loading games…" /> : <div>
                <ViewHeading title={schoolName} sub={yearLine} />
                {rows.length === 0
                    ? <EmptyLine>No game activity at {schoolName || 'this school'} yet this school year.</EmptyLine>
                    : <div>
                        <StatRow>
                            <FlatStat label="Teachers with game play" value={fmt(rows.length)} />
                            <FlatStat label="Total playtime" value={fmtPlaytime(all.playtimeSeconds)} />
                            <FlatStat label="Game sessions" value={fmt(all.sessions)} />
                            <FlatStat label="Average score"><AverageScore avg={gameAccuracy(all)} big /></FlatStat>
                        </StatRow>

                        <SectionTitle note="Most playtime first. Average score is the percentage of game questions answered correctly." right={<AverageColorKey />}>Teachers</SectionTitle>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 17 }}>
                            <thead><tr>
                                <th style={flatTh('left')}>Teacher</th>
                                <th style={flatTh('right', 160)}>Playtime</th>
                                <th style={flatTh('right', 120)}>Sessions</th>
                                <th style={{ ...flatTh('left', 170), paddingLeft: 28 }}>Average score</th>
                                <th style={flatTh('right', 140)} />
                            </tr></thead>
                            <tbody>
                                {rows.map((r, i) => {
                                    const rowKey = `game-teacher:${r.id}:${i}`
                                    const open = !!this.state.expandedRows[rowKey]
                                    const cats = Object.entries(r.t.categories || {})
                                        .map(([cat, c]) => ({ category: cat, ...c }))
                                        .sort((a, b) => (subjectIndex(a.category) - subjectIndex(b.category)) || (gradeIndex(a.standardName) - gradeIndex(b.standardName)))
                                    const canExpand = cats.length > 0
                                    return <React.Fragment key={rowKey}>
                                        <tr style={{ borderBottom: open ? 'none' : '1px solid #e2e8f0', cursor: canExpand ? 'pointer' : 'default' }}
                                            onClick={canExpand ? () => this.toggleRow(rowKey) : undefined}
                                            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8fafc'}
                                            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                                            <td style={{ padding: '12px', fontWeight: 700, color: '#0f172a' }}>{r.label}</td>
                                            <td style={{ padding: '12px', textAlign: 'right', color: '#1e293b', fontWeight: 600 }}>{fmtPlaytime(r.tot.playtimeSeconds)}</td>
                                            <td style={{ padding: '12px', textAlign: 'right', color: '#1e293b' }}>{r.tot.sessions ? fmt(r.tot.sessions) : <span style={{ color: '#64748b' }}>None</span>}</td>
                                            <td style={{ padding: '12px 12px 12px 28px' }}><AverageScore avg={gameAccuracy(r.tot)} /></td>
                                            <td style={{ padding: '12px', textAlign: 'right' }}>{canExpand ? <DetailsLink open={open} /> : null}</td>
                                        </tr>
                                        {open && <tr style={{ borderBottom: '1px solid #e2e8f0' }}><td colSpan={5} style={{ padding: '0 12px 20px' }}>
                                            <GameBreakdown rows={cats} />
                                        </td></tr>}
                                    </React.Fragment>
                                })}
                            </tbody>
                        </table>
                    </div>}
            </div>}
        </div>
    }
    // ---- Games by Standard: school + subject selectors, one row per standard ----
    // ---- Games by Standard: school + subject selectors, one row per standard ----
    renderGamesStandardView() {
        const sid = this.state.selectedSchoolId
        const schools = this.sortedGameSchools()
        const schoolName = sid ? this.getSchoolName(sid) : ''
        const subjects = this.isDemo()
            ? GAME_DEMO_SUBJECTS.filter(s => this.state.gameStandardsDocs.some(d => d.standardCategory === s))
            : this.gameSubjects()
        const sub = this.state.gamesSubject
        const doc = this.state.gameStandardsDocs.find(d => d.schoolId === sid && d.standardCategory === sub)
        const stds = (doc && doc.standards) || {}
        const rows = Object.entries(stds)
            .map(([std, s]) => ({ standard: std, ...s }))
            .sort((a, b) => ((b.playtimeSeconds || 0) - (a.playtimeSeconds || 0)) || a.standard.localeCompare(b.standard))
        const all = sumGameStats(rows)
        const headingSub = [
            sub,
            this.state.selectedYear ? `${schoolYearLabel(this.state.selectedYear)} school year` : null,
        ].filter(Boolean).join(' · ')

        return <div>
            <FilterGrid max={560}>
                <FlatSelectField id="games-standard-school" label="School" title={schoolName} value={sid || ''} onChange={this.onGamesSchoolChange}>
                    {schools.length === 0 && <option value="" disabled>No schools available</option>}
                    {schools.map(s => <option key={s.id} value={s.id}>{this.getSchoolName(s.id)}</option>)}
                </FlatSelectField>
                <FlatSelectField id="games-standard-subject" label="Subject" value={sub || ''} onChange={v => this.setState({ gamesSubject: v, expandedRows: {} })}>
                    {subjects.length === 0 && <option value="" disabled>None available</option>}
                    {subjects.map(s => <option key={s} value={s}>{s}</option>)}
                </FlatSelectField>
            </FilterGrid>

            {!this.state.gamesLoaded ? <LoadingLine text="Loading games…" /> : <div>
                <ViewHeading title={schoolName} sub={headingSub} />
                {rows.length === 0
                    ? <EmptyLine>No {sub ? sub + ' ' : ''}game activity at {schoolName || 'this school'} yet this school year.</EmptyLine>
                    : <div>
                        <StatRow>
                            <FlatStat label="Standards played" value={fmt(rows.length)} />
                            <FlatStat label="Total playtime" value={fmtPlaytime(all.playtimeSeconds)} />
                            <FlatStat label="Game sessions" value={fmt(all.sessions)} />
                            <FlatStat label="Average score"><AverageScore avg={gameAccuracy(all)} big /></FlatStat>
                        </StatRow>

                        <SectionTitle note="Most playtime first. Average score is the percentage of game questions answered correctly." right={<AverageColorKey />}>Standards</SectionTitle>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 17 }}>
                            <thead><tr>
                                <th style={flatTh('left')}>Standard</th>
                                <th style={flatTh('right', 160)}>Playtime</th>
                                <th style={flatTh('right', 120)}>Sessions</th>
                                <th style={{ ...flatTh('left', 170), paddingLeft: 28 }}>Average score</th>
                            </tr></thead>
                            <tbody>
                                {rows.map((r, i) => <tr key={`game-std:${r.standard}:${i}`} style={{ borderBottom: '1px solid #e2e8f0' }}
                                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8fafc'}
                                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                                    <td style={{ padding: '12px' }}>
                                        <div style={{ fontWeight: 700, color: '#0f172a' }}>{r.standard}</div>
                                        {r.standardName ? <div style={{ fontSize: 15, color: '#64748b', marginTop: 1 }}>{r.standardName}</div> : null}
                                    </td>
                                    <td style={{ padding: '12px', textAlign: 'right', color: '#1e293b', fontWeight: 600 }}>{fmtPlaytime(r.playtimeSeconds)}</td>
                                    <td style={{ padding: '12px', textAlign: 'right', color: '#1e293b' }}>{r.sessions ? fmt(r.sessions) : <span style={{ color: '#64748b' }}>None</span>}</td>
                                    <td style={{ padding: '12px 12px 12px 28px' }}><AverageScore avg={gameAccuracy(r)} /></td>
                                </tr>)}
                            </tbody>
                        </table>
                    </div>}
            </div>}
        </div>
    }
    render() {
        const { view } = this.state
        return <div className="mt-4" style={{ fontSize: 18 }}>
            {this.renderSideNav()}
            <div style={{ marginLeft: 300, display: 'flex', justifyContent: 'center' }}>
                <div style={{ flex: 1, padding: '0 24px', maxWidth: 1100, width: '100%' }}>
                    {this.renderHero()}
                    {view === 'schools' && this.renderSchoolsView()}
                    {view === 'teachers' && this.renderTeachersView()}
                    {view === 'standards' && this.renderStandardsView()}
                    {view === 'quiz_results' && this.renderQuizResultsView()}
                    {view === 'resources' && this.renderResourcesView()}
                    {view === 'pacing' && this.renderPacingView()}
                    {view === 'iep_school' && this.renderIEPView('school')}
                    {view === 'iep_teacher' && this.renderIEPView('teacher')}
                    {view === 'games_school' && this.renderGamesSchoolView()}
                    {view === 'games_teacher' && this.renderGamesTeacherView()}
                    {view === 'games_standard' && this.renderGamesStandardView()}
                    {view === 'students' && this.renderStudentsView()}
                </div>
            </div>
            {this.renderResultsModal()}
            {this.renderReportModal()}
            {this.renderStudentReportModal()}
            {this.state.reportPrinting
                ? <div style={{ position: 'fixed', left: -20000, top: 0 }}>
                    <div ref={el => { this.reportRef = el }}>{this.renderReport()}</div>
                </div>
                : null}
            {this.state.studentReportPrinting
                ? <div style={{ position: 'fixed', left: -20000, top: 0 }}>
                    <div ref={el => { this.studentReportRef = el }}>{this.renderStudentReport()}</div>
                </div>
                : null}
        </div>
    }
}
export default QuizStats